import * as React from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  BadgeCheck,
  BookmarkPlus,
  CheckCircle2,
  ChevronsUpDown,
  Inbox,
  MoreHorizontal,
  Pencil,
  Play,
  RefreshCw,
  ScrollText,
  Search,
  Square,
  Trash2,
  Undo2,
  X,
} from 'lucide-react'
import {
  LIVE_STATE_META,
  RUN_ACTIVE_PHASES,
  STEP_INDEX,
  STEP_LABELS,
  liveStateOf,
  runOwnsLiveState,
  stepProgress,
  type CustomerRow,
  type CustomerSegment,
  type CustomerStatus,
  type Gender,
  type LiveState,
  type PipelineStep,
} from '@acs/shared'
import type { RunPhase, RunStateDto } from '@acs/shared'
import { isActiveHot, markActiveHit, registerActiveHitView } from '@/lib/active-hits'
import { ApiError, apiErrorCode, apiPatch, apiPost } from '@/lib/api'
import {
  useDefaultFlow,
  useDefaultGqlWorkers,
  useDefaultScheduleWorkers,
  useDefaultWorkers,
  useRunStates,
} from '@/hooks/use-automation'
import { RunDrawer } from '@/components/session/run-drawer'
import { RunPhasePill } from '@/components/session/run-console'
import { cn } from '@/lib/cn'
import { applyCustomerPatches, customersKey, useCustomers } from '@/hooks/use-customers'
import { useSaveRoster } from '@/hooks/use-resume'
import { useRelativeTime } from '@/hooks/use-relative-time'
import { useUiStore } from '@/stores/ui-store'
import { Badge, type Tone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip } from '@/components/ui/tooltip'
import { useToast } from '@/components/ui/toast'
import { CustomerDetailPanel } from '@/components/customers/customer-detail-panel'
import { ResumeBar } from '@/components/customers/resume-bar'
import { ShiftPriorityIcons } from '@/components/customers/shift-priority'
import { SignInDialog } from '@/components/customers/signin-dialog'
import {
  GenderGateDialog,
  type GenderGateAsk,
} from '@/components/customers/gender-gate-dialog'
import { Dialog } from '@/components/ui/dialog'
import {
  friendlyError,
  useApproveCustomer,
  useDeleteCustomer,
  useUnapproveCustomer,
} from '@/hooks/use-create-customer'
import {
  CustomerPagination,
  CustomerToolbar,
  CLEARED_FILTERS,
  COLUMN_FILTERS,
  COLUMN_FILTER_LABELS,
  hasActiveFilters,
  pageCountOf,
  listNounFor,
  toCustomerQuery,
  useCustomerSearch,
  useSetCustomerSearch,
  type ColumnFilter,
  type CustomerSearch,
  type CustomerSort,
} from '@/components/customers/customer-toolbar'

/**
 * The Approve list. This is where operators live, so the whole file is written
 * against one budget: **a scroll frame must cost almost nothing**, and a burst
 * of realtime patches must repaint the rows that changed and nothing else.
 *
 * Five mechanisms carry that:
 *
 *  1. **Virtualisation.** Only the ~20 rows in view exist in the DOM. Row
 *     height is a fixed token, so the virtualizer never has to measure and
 *     scrolling never reflows.
 *  2. **No component library inside a row.** A virtualised list mounts and
 *     unmounts its rows continuously while scrolling, so anything a row
 *     mounts is paid for *per scroll*, not per page. Radix roots (each one a
 *     context subscription plus mount effects) used to cost ~8 per row —
 *     ~200 live instances at 25 visible rows, and the dominant cost of a
 *     scroll frame. A row now mounts **zero**: hover hints are the native
 *     `title` attribute, selection is a native `<input type=checkbox>`, and
 *     the overflow menu is a plain `<button>` that upgrades itself into a real
 *     Radix menu only once it has actually been opened (see `RowMenu`). Radix
 *     survives only where the instance count is small and stable — the header,
 *     the toolbar and the bulk bar.
 *  3. **A memoised row with a hand-written comparator.** `rowDataEqual`
 *     compares exactly the fields the row paints. An `updated_at` bump on the
 *     whole page produces zero renders; a step change on one row produces one.
 *  4. **Containment.** Each row declares `contain: layout paint style`, so a
 *     label growing inside one row cannot invalidate layout or paint outside
 *     its own 56px box. (Deliberately no `will-change`: on 25+ simultaneous
 *     elements it costs more GPU memory than the compositing it saves.)
 *  5. **Flash outside React.** The "what just changed" highlight is applied by
 *     touching `classList` on the handful of mounted row elements, driven by a
 *     ref-held `id -> timestamp` map. Nothing about it goes through state, so
 *     a burst of 300 patches cannot cause 300 table renders.
 *
 * Below `md` the table is not a table at all — it is a virtualised card list.
 * A grid that scrolls sideways on a phone is a failure, not a compromise.
 */

/* ================================================================== *
 * Constants
 * ================================================================== */

/** Must match the `h-row` token (56px). The virtualizer trusts it absolutely. */
/**
 * Row heights, keyed by the density setting in the rail.
 *
 * `comfortable` matches the `--spacing-row` token (60px) exactly. The
 * virtualizer trusts whichever value it is given absolutely — it positions every
 * row by multiplying this number, never by measuring — so a value that disagrees
 * with what the row actually paints makes rows drift further out of place the
 * further you scroll.
 *
 * Until now the density toggle set a store value that nothing read: it reported
 * state through `aria-pressed` while changing nothing on screen, which is worse
 * than not offering the control at all.
 */
const ROW_HEIGHTS = { comfortable: 60, compact: 44 } as const
type Density = keyof typeof ROW_HEIGHTS

/**
 * Phone card height. Two stacked blocks inside 10px of vertical padding:
 * identity (44px checkbox beside name 20 + id 15 + email 16 = 51) and, after
 * an 8px gap, the status + controls line (32). 20 + 51 + 8 + 32 = 111 -> 112.
 */
const CARD_HEIGHT = 112

const OVERSCAN = 8

/**
 * Whether the PRIMARY pointer is a finger, sampled ONCE at module load — like
 * `ROW_HEIGHTS`, the constants derived from it are hard-synced with virtualizer
 * math, so they must never change within a session. That is safe: a device's
 * primary pointer class does not change at runtime (a tablet stays a tablet),
 * and sampling at load keeps every derived layout/style object a frozen
 * module-scope singleton — nothing re-renders, nothing is read per frame.
 * Touch-laptops whose primary pointer is a trackpad report `fine` and keep the
 * desktop constants, which is the behaviour they expect.
 */
const IS_COARSE =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches

/**
 * The two sticky header rows: the column labels, then the filter inputs.
 *
 * `HEADER_HEIGHT` is what keyboard scroll-into-view subtracts, so it has to be
 * the height of everything sticky above the body — get it wrong and the row the
 * arrow keys just scrolled to ends up underneath the header. The card list has
 * no sticky header at all, so its offset is zero.
 *
 * The filter row is deliberately shorter than the label row: it is a persistent
 * band across the top of the table, and 32px of 12px type is the smallest a row
 * of real text inputs can be without becoming fiddly to hit — with a MOUSE. On
 * a coarse pointer the band grows to 44px (the touch-target floor the rest of
 * the app holds to, see TOUCH_TARGET in ui/button.tsx). Grown HERE, in the JS
 * constant, not in CSS: `filterStyle`, the header block and the keyboard
 * scroll offset all derive from these numbers, so a CSS-only enlargement would
 * desync every one of them.
 */
const LABEL_ROW_HEIGHT = 44
const FILTER_ROW_HEIGHT = IS_COARSE ? 44 : 32
const HEADER_HEIGHT = LABEL_ROW_HEIGHT + FILTER_ROW_HEIGHT

/** Input height inside the filter band: band minus the same 5px top/bottom inset. */
const FILTER_INPUT_CLASS = IS_COARSE ? 'h-[34px]' : 'h-[22px]'

/** Rows of `<thead>`, for the `aria-rowindex` of everything below it. */
const HEADER_ROWS = 2

/** Matches the `.flash-update` AND `.flash-beat` durations in theme.css. */
const FLASH_MS = 1100

const CELL = 'flex h-full min-w-0 items-center gap-1.5 px-2.5'
/**
 * The padding a `compact` column wears instead — see `ColumnMeta.compact`.
 * Merged over `CELL` by `cn`, so the header, the body and the skeleton all
 * narrow together or not at all.
 */
const COMPACT_CELL = 'px-1'

/* ================================================================== *
 * Layout — one source of truth for header, body and card
 * ================================================================== */

type ColumnId =
  | 'select'
  | 'customer'
  | 'agent'
  | 'email'
  | 'job'
  /** Approved only — the pair split into two columns. */
  | 'jobId'
  | 'scheduleId'
  /** Pending only. */
  | 'cities'
  /** Pending only. */
  | 'workHours'
  /**
   * Approved only — the three fleet overrides (`hunt_workers`,
   * `hunt_gql_workers`, `schedule_workers`) as ONE column of three boxes,
   * edited in place. See `FLEET_COLUMNS`.
   */
  | 'fleets'
  /** Approved only — `hunt_flow`, edited in place. */
  | 'flow'
  /** Approved only — `computer_number`, edited in place. */
  | 'number'
  /*
   * Amazon's rehire verdict has NO column of its own, and that is a width
   * decision rather than a judgement about how much it matters.
   *
   * Every tier here is budgeted to the pixel against its breakpoint's usable
   * width — `wide` has 14px of slack at 1292, `xl` has 40 at 1036 — so any new
   * track, even a narrow one, puts the grid into a sideways scroll at the
   * breakpoint it is added to. Re-balancing the existing tracks to make room
   * would mean re-deciding somebody else's carefully measured layout for a
   * field most rows will never carry.
   *
   * It rides in the `status` cell instead: 184px, present at every tier, and
   * already the column an operator reads to find out what is going on with a
   * customer. See `SessionCell`.
   */
  | 'status'
  | 'actions'

interface ColumnMeta {
  label: string
  /** Server sort key. Absent => not sortable. */
  sortKey?: CustomerSort
  align?: 'right' | 'center'
  /** Extra explanation on the header button. */
  hint?: string
  /** Header text exists for screen readers but is not painted. */
  srOnlyLabel?: boolean
  /**
   * Search param this column's header input writes to. Absent => no input.
   *
   * Every one of these narrows the *server's* result set, not the loaded page.
   * Filtering fifty rows in the browser while the footer still says "of 330"
   * would be a search box that lies about what it searched.
   */
  filter?: ColumnFilter
  /** Placeholder for the filter input. Says what the column matches on. */
  filterHint?: string
  /**
   * A column of one small control, sat as close to its neighbours as the
   * controls allow.
   *
   * The five override columns and the number tag are each a two-digit box or
   * a short select, and at `CELL`'s 10px a side they spread across a third of
   * the screen saying almost nothing. `COMPACT_CELL` closes that to 4px, the
   * tracks are sized to the control plus that, and the header carries the
   * fleet's one-letter tag from the run drawer — the same `w`, `s`, `j`, `ga`
   * an operator already reads on every worker chip — with the full name in
   * the tooltip.
   */
  compact?: boolean
}

const COLUMN_META: Readonly<Record<ColumnId, ColumnMeta>> = Object.freeze({
  select: { label: 'Select', srOnlyLabel: true },
  customer: {
    label: 'Customer',
    sortKey: 'name',
    hint: 'Name, customer ID and mode. Sorts by name.',
    filter: 'name',
    filterHint: 'Name or ID',
  },
  agent: { label: 'Agent', filter: 'agentName', filterHint: 'Agent' },
  email: { label: 'Email', filter: 'email', filterHint: 'Email' },
  // The stacked cell keeps the job-id filter; its schedule line is covered by
  // the toolbar search, which prefix-matches both ids.
  job: { label: 'Job / Schedule', filter: 'job', filterHint: 'Job id' },
  cities: {
    label: 'Cities',
    hint: 'Hover a row to see every city and its warehouse count.',
    filter: 'city',
    filterHint: 'City',
  },
  workHours: { label: 'Work hours', filter: 'workHours', filterHint: 'Hours' },
  /*
   * The three fleets as ONE column.
   *
   * As separate columns they each took a share of the spare width — an equal
   * gap between every pair, the same as between Email and Job — and drifted
   * apart across the screen. As one column the group takes one share, and
   * inside it the three boxes sit a fixed 8px apart, which is what they are:
   * the worker counts of one hunt, side by side. The header carries three
   * letters, one over each box, from `FLEET_COLUMNS`; this label exists for the
   * column's own name in assistive tech and react-table.
   */
  fleets: {
    label: 'Fleet workers',
    compact: true,
    // No filter. There is no server-side one to write to — adding it would mean
    // a new entry in `CUSTOMER_COLUMN_FILTERS` and a clause in the repo — and a
    // column of two-digit numbers is not one anybody narrows a 600-row list by.
  },
  flow: {
    label: 'Flow',
    hint: 'How this customer claims once a shift is seen open. Current fires one confirm immediately. Brazil waits out the early-warning window, then works the v1/workflow endpoint in a loop — and files no application at Start. Blank uses the hunt setting. Takes effect on the next Start.',
    compact: true,
    // No filter, for the same reason as the three worker columns: it is an
    // override on a global, so most rows are blank and filtering on it would
    // mostly answer "the ones somebody has touched".
  },
  // The split pair (Approved only). A filter each, and each matches ONLY its
  // own column — an input that also narrowed on the neighbouring id would
  // return rows the column above it visibly does not contain.
  jobId: { label: 'Job', filter: 'job', filterHint: 'Job id' },
  scheduleId: { label: 'Schedule', filter: 'schedule', filterHint: 'Schedule id' },
  number: {
    label: 'Number',
    hint: 'The operator’s own tag. Edited in place; saves as you type.',
    compact: true,
    // The one operator-owned value on the row that is a stable string worth
    // matching on. The filter moved here with the field itself.
    filter: 'computerNumber',
    filterHint: 'e.g. N S2',
  },
  status: {
    label: 'Status',
    sortKey: 'updated_at',
    hint: 'Live state, current step and heartbeat — and Amazon’s rehire verdict when it refuses this customer. Sorts by last update.',
  },
  actions: { label: 'Actions', align: 'right' },
})

/**
 * The three boxes inside the `fleets` column, in the order the fleets run.
 *
 * Each is headed by the letter it wears inside a run — `w3`, `s6`, `ga14` — so
 * the box and the worker chip name the same thing the same way, and the
 * tooltip spells the fleet out for anyone who has not met the tags yet. The
 * order is the order of `FleetsCell`'s children; the two are kept beside each
 * other in the row so they cannot drift apart.
 */
const FLEET_COLUMNS: readonly { key: string; label: string; hint: string }[] = [
  {
    key: 'watch',
    label: 'W',
    hint: 'W — workers on the schedule watch (the “w…” tags in a run): the only fleet that watches for the opening and the only one that claims. Blank uses the hunt setting; 0 leaves nothing hunting and the hunt refuses to arm. Takes effect on the next Start.',
  },
  {
    key: 'shift-list',
    label: 'S',
    hint: 'S — workers on the shift list, over GraphQL (the “s…” tags in a run): on the Current flow they run only while the shift has no UUID, to read it off the job’s listing, then stand down. They never claim. Blank uses the hunt setting; 0 is raised to 1. Takes effect on the next Start.',
  },
  {
    key: 'ga',
    label: 'GA',
    hint: "GA — workers reading the job's schedule list (the “ga…” tags in a run): on the Current flow they arm only while the assigned shift has no UUID, to read it off the listing, then stand down. They never claim. Blank uses the hunt setting, and the floor is 1 so the UUID reader always exists. Each holds an ISP address and borrowed accounts while it runs. Takes effect on the next Start.",
  },
]

/**
 * A CSS grid track: a fixed pixel width, or `minmax(floor, ceiling)` in pixels.
 *
 * ── Why a ceiling, and not `fr` ─────────────────────────────────────
 * The readable columns used to be `minmax(floor, Nfr)`, and `fr` absorbs every
 * spare pixel: on a wide monitor Customer and Email swelled to four hundred
 * pixels of mostly nothing while the fixed columns sat crammed against the
 * right edge. With a ceiling, each readable column grows to a width that suits
 * its content and then stops — and whatever is left over is handed to the grid
 * as `justify-content: space-between`, which spreads it into EQUAL gaps between
 * every pair of columns. A narrow window sees no difference: below the sum of
 * floors the grid overflows as before, and there is nothing left to spread.
 */
type Track = { fixed: number; min?: undefined } | { fixed?: undefined; min: number; max: number }

interface TableLayout {
  id: 'md' | 'lg' | 'xl' | 'wide'
  columns: readonly ColumnId[]
  has: Readonly<Record<ColumnId, boolean>>
  /** Shared by the header row and every body row, so the two always align. */
  template: string
  /** Sum of fixed widths + every `minmax` floor. Below this the grid scrolls. */
  minWidth: number
  rowStyle: React.CSSProperties
  headerStyle: React.CSSProperties
  /** The filter row. Same tracks, its own height. */
  filterStyle: React.CSSProperties
  /** Start/Stop carry their text label only where the column is wide enough. */
  actionLabels: boolean
}

function buildLayout(
  id: TableLayout['id'],
  tracks: readonly (readonly [ColumnId, Track])[],
  actionLabels: boolean,
  rowHeight: number
): TableLayout {
  const columns = tracks.map(([col]) => col)
  const has: Record<ColumnId, boolean> = {
    select: false,
    customer: false,
    agent: false,
    email: false,
    job: false,
    jobId: false,
    scheduleId: false,
    cities: false,
    workHours: false,
    fleets: false,
    flow: false,
    number: false,
    status: false,
    actions: false,
  }
  for (const col of columns) has[col] = true

  let minWidth = 0
  const template = tracks
    .map(([, track]) => {
      if (track.fixed !== undefined) {
        minWidth += track.fixed
        return `${track.fixed}px`
      }
      minWidth += track.min
      return `minmax(${track.min}px, ${track.max}px)`
    })
    .join(' ')

  /*
   * Spare width becomes equal gaps between the columns — see `Track`. Set on
   * all three grids, or the header labels and the filter inputs would drift
   * off the cells they name the moment there was anything to spread.
   */
  const justifyContent = 'space-between' as const

  return {
    id,
    columns,
    has,
    template,
    minWidth,
    rowStyle: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: rowHeight,
      display: 'grid',
      gridTemplateColumns: template,
      justifyContent,
      alignItems: 'center',
      // Isolates the row completely: a step label growing in one row cannot
      // dirty the layout *or* the paint of the other 19 on screen.
      contain: 'layout paint style',
    },
    headerStyle: {
      display: 'grid',
      gridTemplateColumns: template,
      justifyContent,
      alignItems: 'stretch',
    },
    // Built here, once per breakpoint at module scope, for the same reason
    // `rowStyle` is: a style object rebuilt per render is a new prop every
    // render, and the row it is spread on can then never bail out.
    filterStyle: {
      display: 'grid',
      gridTemplateColumns: template,
      justifyContent,
      alignItems: 'center',
      height: FILTER_ROW_HEIGHT,
    },
    actionLabels,
  }
}

/**
 * Three tiers, because the usable width is not the viewport width — the rail
 * takes 60px from `md` and 244px from `lg`.
 *
 *   md  768 - 1023 : 768  - 60  = 708 usable
 *   lg 1024 - 1279 : 1024 - 244 = 780 usable
 *   xl 1280+       : 1280 - 244 = 1036 usable
 *
 * Every tier's floor (fixed widths + `minmax` floors) sits under its usable
 * width, which is what keeps the table from scrolling sideways at any of the
 * target sizes. Columns arrive in order of how often an operator needs them:
 * who it is and what the session is doing are never dropped; the agent, then
 * the email, then the job identifiers appear as the screen affords them.
 *
 * The actions column is sized from its contents, not by eye: three 32px
 * buttons with 6px gaps inside 18px of cell padding is 126px, and 172px once
 * the primary button carries its "Start"/"Stop" label (min 74px) at `xl`.
 *
 *   md  fixed 36+184+128           = 348 · floors 144+92        = 584
 *   lg  fixed 36+184+128           = 348 · floors 144+92+148    = 732
 *   xl  fixed 36+132+184+172       = 524 · floors 144+92+148    = 908
 *
 * ── Floors, ceilings, and where the spare width goes ─────────────────
 * The four readable columns — Customer, Agent, Email, Cities — each have a
 * floor and a ceiling. The floor is what the column needs to be scannable:
 * Agent was once a hard 92px, which truncates a real name to about eleven
 * characters, so every row on a wide screen read "Manthan …" or "Yunita Ch…"
 * and the tooltip is no substitute for a column you are scanning down. The
 * ceiling is what its content can actually use: 260px seats the longest name
 * over its id, 280px the longest address on file, 160px any agent, 180px
 * "Tsawwassen First Nation, BC" with its site count.
 *
 * Above the ceilings nothing grows any further. The spare width is spread as
 * EQUAL gaps between every pair of columns instead (`justify-content:
 * space-between`, set in `buildLayout`), so a 2300px monitor shows the same
 * columns as a 1500px one with a little more air between each — rather than
 * the two `fr` columns swelling to hold nothing while the fixed ones sat
 * crammed against the right edge. The floors are chosen so every tier's
 * minimum still fits under its usable width (708 / 780 / 1036), and `job`
 * gains 12px because "Job / Schedule" was truncating in its own header.
 */
/**
 * Cities has a ceiling above its floor because the names are long
 * ("Tsawwassen First Nation, BC"); work hours is fixed, because its three
 * possible values are known and none is wider than "Part-time". Shared by
 * both variants so the two lists cannot drift apart on the columns they have
 * in common.
 */
const CITIES_TRACK: Track = { min: 110, max: 180 }
const WORK_HOURS_TRACK = 92
/**
 * The split Job / Schedule columns (Approved only). Sized like the combined
 * 132px column was — from the content, a ten-digit mono id at 11.5px (~70px)
 * plus cell padding, with headroom for the AUTO chip a running pick prepends.
 */
const SPLIT_ID_TRACK = 108
/**
 * `computer_number` in its own column: the 56px input plus `COMPACT_CELL`'s
 * 8px. Six characters is the longest tag on file ("1N A S"), and six of
 * 12px mono fit in 56 with room to see the caret.
 */
const NUMBER_TRACK = 64
/**
 * A fleet box: two digits of 12px mono with the caret beside them. Two digits
 * is the entire vocabulary — the ceiling is 50 — so it never needs to grow.
 * Mirrors the `w-[36px]` on `WorkerCountCell`.
 */
const FLEET_BOX = 36
/**
 * Between two boxes. Fixed and small — the whole reason the three share a
 * track — and mirrors the `gap-2` on the `fleets` cell.
 */
const FLEET_GAP = 8
/**
 * The three fleets in ONE track: three boxes, two gaps, `COMPACT_CELL`'s 8px.
 * Sized to the controls and nothing else; the header is three short labels,
 * one over each box, so it can never want more than the boxes do.
 */
const FLEETS_TRACK = FLEET_BOX * 3 + FLEET_GAP * 2 + 8
/**
 * `hunt_flow`, sized from the two values it can hold: "Current" at 11.5px is
 * ~42px, the native select's arrow ~18, plus `COMPACT_CELL`. Wider than the
 * fleet columns because this one holds a word, and a truncated flow name is
 * the one thing in this row nobody can infer from context.
 */
const FLOW_TRACK = 72

const LAYOUT_MD_BUILD = (rowHeight: number) => buildLayout(
  'md',
  [
    ['select', { fixed: 36 }],
    ['customer', { min: 144, max: 260 }],
    ['agent', { min: 92, max: 160 }],
    ['status', { fixed: 184 }],
    ['actions', { fixed: 128 }],
  ],
  false,
  rowHeight
)

const LAYOUT_LG_BUILD = (rowHeight: number) => buildLayout(
  'lg',
  [
    ['select', { fixed: 36 }],
    ['customer', { min: 144, max: 260 }],
    ['agent', { min: 92, max: 160 }],
    ['email', { min: 148, max: 280 }],
    ['status', { fixed: 184 }],
    ['actions', { fixed: 128 }],
  ],
  false,
  rowHeight
)

const LAYOUT_XL_BUILD = (rowHeight: number) => buildLayout(
  'xl',
  [
    ['select', { fixed: 36 }],
    ['customer', { min: 144, max: 260 }],
    ['agent', { min: 92, max: 160 }],
    ['email', { min: 148, max: 280 }],
    ['job', { fixed: 132 }],
    ['cities', CITIES_TRACK],
    ['status', { fixed: 184 }],
    ['actions', { fixed: 172 }],
  ],
  true,
  rowHeight
)

/**
 * A fourth tier, for the width where Cities AND Work hours both fit.
 *
 *   xl   (1280) 36+144+92+148+132+110    +184+172 = 1018 / 1036
 *   wide (1536) 36+144+92+148+132+110+92+184+172 = 1110 / 1292
 *
 * Work hours needs 92px that `xl` does not have — this list keeps Status, which
 * the Pending list dropped — so it appears one breakpoint later rather than
 * pushing the table into a sideways scroll at 1280.
 *
 * The tiers stay MONOTONIC, which is the property worth protecting: every step
 * up in width adds a column and never removes one. Cities would technically fit
 * at `md` (694/708, because `md` has no Email column) and then vanish at `lg`
 * where Email takes its space — a column that disappears as the window grows is
 * worse than one that arrives late.
 */
const LAYOUT_WIDE_BUILD = (rowHeight: number) => buildLayout(
  'wide',
  [
    ['select', { fixed: 36 }],
    ['customer', { min: 144, max: 260 }],
    ['agent', { min: 92, max: 160 }],
    ['email', { min: 148, max: 280 }],
    ['job', { fixed: 132 }],
    ['cities', CITIES_TRACK],
    ['workHours', { fixed: WORK_HOURS_TRACK }],
    ['status', { fixed: 184 }],
    ['actions', { fixed: 172 }],
  ],
  true,
  rowHeight
)

/* ------------------------------------------------------------------ *
 * The Approved list's own tracks
 *
 * Three asked-for differences from the shared set: no Agent column, the job
 * and schedule ids in two side-by-side columns instead of one stacked cell,
 * and `computer_number` as its own column between Work hours and Status —
 * moved out of the Status cell, which keeps the pill, the step line and the
 * progress bar.
 *
 * The tiers stay monotonic (every step up adds columns, never removes one).
 * The split pair costs 216px against the combined column's 132, which is what
 * pushes Cities and Work hours out to `wide` on this list — the ids and the
 * number tag are what the operator asked to see first.
 *
 * The fleet group and Flow join at `wide` for the same arithmetic reason:
 * `xl` has 72px of slack and the two of them need 248. They are overrides
 * that most rows will never carry, so arriving with Cities and Work hours
 * rather than displacing an id is the right trade — and every tier still only
 * ever gains columns as it widens.
 *
 *   md   36+144            +64+184+128 =  556 / 708
 *   lg   36+144+148        +64+184+128 =  704 / 780
 *   xl   36+144+148+108+108+64+184+172 =  964 / 1036
 *   wide 36+144+148+108+108+110+92+176+72+64+184+172 = 1414 / 1292
 *
 * `wide` is over by 122px at 1536 and fits from ~1660, so at exactly 1536 the
 * grid scrolls sideways by that much. It was over by 342 before the fleet
 * columns were narrowed to their boxes; the remaining excess is the split id
 * pair plus Cities and Work hours, and none of those has a box to shrink to.
 * ------------------------------------------------------------------ */

const APPROVED_MD_BUILD = (rowHeight: number) => buildLayout(
  'md',
  [
    ['select', { fixed: 36 }],
    ['customer', { min: 144, max: 260 }],
    ['number', { fixed: NUMBER_TRACK }],
    ['status', { fixed: 184 }],
    ['actions', { fixed: 128 }],
  ],
  false,
  rowHeight
)

const APPROVED_LG_BUILD = (rowHeight: number) => buildLayout(
  'lg',
  [
    ['select', { fixed: 36 }],
    ['customer', { min: 144, max: 260 }],
    ['email', { min: 148, max: 280 }],
    ['number', { fixed: NUMBER_TRACK }],
    ['status', { fixed: 184 }],
    ['actions', { fixed: 128 }],
  ],
  false,
  rowHeight
)

const APPROVED_XL_BUILD = (rowHeight: number) => buildLayout(
  'xl',
  [
    ['select', { fixed: 36 }],
    ['customer', { min: 144, max: 260 }],
    ['email', { min: 148, max: 280 }],
    ['jobId', { fixed: SPLIT_ID_TRACK }],
    ['scheduleId', { fixed: SPLIT_ID_TRACK }],
    ['number', { fixed: NUMBER_TRACK }],
    ['status', { fixed: 184 }],
    ['actions', { fixed: 172 }],
  ],
  true,
  rowHeight
)

const APPROVED_WIDE_BUILD = (rowHeight: number) => buildLayout(
  'wide',
  [
    ['select', { fixed: 36 }],
    ['customer', { min: 144, max: 260 }],
    ['email', { min: 148, max: 280 }],
    ['jobId', { fixed: SPLIT_ID_TRACK }],
    ['scheduleId', { fixed: SPLIT_ID_TRACK }],
    ['cities', CITIES_TRACK],
    ['workHours', { fixed: WORK_HOURS_TRACK }],
    ['fleets', { fixed: FLEETS_TRACK }],
    ['flow', { fixed: FLOW_TRACK }],
    ['number', { fixed: NUMBER_TRACK }],
    ['status', { fixed: 184 }],
    ['actions', { fixed: 172 }],
  ],
  true,
  rowHeight
)

/* ------------------------------------------------------------------ *
 * The Pending list's own tracks
 *
 * Pending rows carry THREE buttons where every other list carries one, and the
 * 128/172px track sized for `Start + log + ⋯` is roughly half of what Edit +
 * Approve + Delete + ⋯ needs. That is the overflow: the buttons were painting
 * straight over the Status column.
 *
 * Measured from the real button CSS rather than by eye — `px-2.5`, a 15px icon,
 * a 6px gap and 13.5px labels — the same way the existing tracks were derived,
 * and the model reproduces the current 172px track to within a pixel.
 *
 *   icons only                      164px   fits every tier
 *   Edit / Approve / Delete labelled 313px   fits only at xl, and only just
 *
 *   md  36+144+92    +184+164 = 620 / 708
 *   lg  36+144+92+148+184+164 = 768 / 780
 *   xl  36+144+92+148+132+150+313 = 1015 / 1036
 *
 * ── Where the 13px at `xl` comes from ───────────────────────────────
 * With labels, `xl` overflowed by 13px. Rather than shave the buttons or force
 * a scrollbar, Status gives it up — from 184 to 150 — because on a pending row
 * Status has the least to say of any column on the table: the customer has
 * never been started, so it is a NOT STARTED pill over "No step yet" and a
 * progress bar at zero. Every other list keeps 184px, where the column carries
 * a live step and a moving bar.
 * ------------------------------------------------------------------ */

/*
 * Two buttons now, not three — Delete moved into the overflow menu, where an
 * irreversible action belongs anyway.
 *
 *   Edit 68 + Approve 89 + ⋯ 32 + two 6px gaps + 18px cell padding = 219
 *   icons  32 × 3 + two 6px gaps + 18px cell padding               = 126
 */
const PENDING_ACTIONS_ICONS = 126
const PENDING_ACTIONS_LABELLED = 219

/*
 * Status is gone from this list entirely, and its 184px pays for the two new
 * columns. It had the least to say here of any column on the table: a pending
 * customer has never been started, so it was a NOT STARTED pill over "No step
 * yet" over a progress bar at zero — three lines of nothing, on every row.
 *
 *   md  36+144    +148+110    +126 =  564 / 708
 *   lg  36+144+92 +148+110+92 +126 =  748 / 780
 *   xl  36+144+92 +148+132+110+92+219 = 973 / 1036
 *
 * Both new tracks are the shared `CITIES_TRACK` / `WORK_HOURS_TRACK`.
 */

const PENDING_MD_BUILD = (rowHeight: number) => buildLayout(
  'md',
  [
    ['select', { fixed: 36 }],
    ['customer', { min: 144, max: 260 }],
    ['email', { min: 148, max: 280 }],
    ['cities', CITIES_TRACK],
    ['actions', { fixed: PENDING_ACTIONS_ICONS }],
  ],
  false,
  rowHeight
)

const PENDING_LG_BUILD = (rowHeight: number) => buildLayout(
  'lg',
  [
    ['select', { fixed: 36 }],
    ['customer', { min: 144, max: 260 }],
    ['agent', { min: 92, max: 160 }],
    ['email', { min: 148, max: 280 }],
    ['cities', CITIES_TRACK],
    ['workHours', { fixed: WORK_HOURS_TRACK }],
    ['actions', { fixed: PENDING_ACTIONS_ICONS }],
  ],
  false,
  rowHeight
)

const PENDING_XL_BUILD = (rowHeight: number) => buildLayout(
  'xl',
  [
    ['select', { fixed: 36 }],
    ['customer', { min: 144, max: 260 }],
    ['agent', { min: 92, max: 160 }],
    ['email', { min: 148, max: 280 }],
    ['job', { fixed: 132 }],
    ['cities', CITIES_TRACK],
    ['workHours', { fixed: WORK_HOURS_TRACK }],
    ['actions', { fixed: PENDING_ACTIONS_LABELLED }],
  ],
  true,
  rowHeight
)

/**
 * Both densities are built once, at module scope. The row `style` object is
 * spread on every visible row, so its identity must stay stable across renders
 * or every row re-renders on each scroll frame.
 */
type LayoutSet = { md: TableLayout; lg: TableLayout; xl: TableLayout; wide: TableLayout }

/**
 * `pending` and `approved` are each their own table; `confirmed` (and any
 * future status) takes the default tracks.
 */
type LayoutVariant = 'default' | 'pending' | 'approved'

const LAYOUTS: Record<LayoutVariant, Record<Density, LayoutSet>> = {
  default: {
    comfortable: {
      md: LAYOUT_MD_BUILD(ROW_HEIGHTS.comfortable),
      lg: LAYOUT_LG_BUILD(ROW_HEIGHTS.comfortable),
      xl: LAYOUT_XL_BUILD(ROW_HEIGHTS.comfortable),
      wide: LAYOUT_WIDE_BUILD(ROW_HEIGHTS.comfortable),
    },
    compact: {
      md: LAYOUT_MD_BUILD(ROW_HEIGHTS.compact),
      lg: LAYOUT_LG_BUILD(ROW_HEIGHTS.compact),
      xl: LAYOUT_XL_BUILD(ROW_HEIGHTS.compact),
      wide: LAYOUT_WIDE_BUILD(ROW_HEIGHTS.compact),
    },
  },
  pending: {
    comfortable: {
      md: PENDING_MD_BUILD(ROW_HEIGHTS.comfortable),
      lg: PENDING_LG_BUILD(ROW_HEIGHTS.comfortable),
      xl: PENDING_XL_BUILD(ROW_HEIGHTS.comfortable),
      // Nothing left to add: the Pending list already shows every column it
      // has, so the extra width goes to the flexible tracks instead.
      wide: PENDING_XL_BUILD(ROW_HEIGHTS.comfortable),
    },
    compact: {
      md: PENDING_MD_BUILD(ROW_HEIGHTS.compact),
      lg: PENDING_LG_BUILD(ROW_HEIGHTS.compact),
      xl: PENDING_XL_BUILD(ROW_HEIGHTS.compact),
      wide: PENDING_XL_BUILD(ROW_HEIGHTS.compact),
    },
  },
  approved: {
    comfortable: {
      md: APPROVED_MD_BUILD(ROW_HEIGHTS.comfortable),
      lg: APPROVED_LG_BUILD(ROW_HEIGHTS.comfortable),
      xl: APPROVED_XL_BUILD(ROW_HEIGHTS.comfortable),
      wide: APPROVED_WIDE_BUILD(ROW_HEIGHTS.comfortable),
    },
    compact: {
      md: APPROVED_MD_BUILD(ROW_HEIGHTS.compact),
      lg: APPROVED_LG_BUILD(ROW_HEIGHTS.compact),
      xl: APPROVED_XL_BUILD(ROW_HEIGHTS.compact),
      wide: APPROVED_WIDE_BUILD(ROW_HEIGHTS.compact),
    },
  },
}


/**
 * The widest tier's floor: 896px. Exported because it is the number that says
 * whether this grid can ever scroll sideways — it only can on a window
 * narrowed by hand to under 1140px (896 + the 244px rail) at `xl` type sizes.
 */
// Column tracks are identical across densities — only row height differs —
// so either density reports the same floor. The Pending variant is the wider
// of the two, so it is the one that decides whether anything can scroll.
// The widest floor across every variant and tier decides whether anything can
// ever scroll sideways.
export const MIN_TABLE_WIDTH = Math.max(
  LAYOUTS.default.comfortable.xl.minWidth,
  LAYOUTS.pending.comfortable.xl.minWidth,
  LAYOUTS.approved.comfortable.xl.minWidth
)

/**
 * The actions column is pinned to the right edge, so Start and Stop — the
 * whole point of the screen — can never be the thing that scrolls out of
 * reach on a hand-narrowed window.
 *
 * A sticky cell has to paint an opaque background or the row shows through,
 * and that background has to track the row's own state: `group-hover` and
 * `aria-selected` are read off the `<tr>`, which carries both.
 *
 * PERFORMANCE: the 1px `before:` hairline is the *only* decoration here. There
 * used to be an `after:` gradient falloff as well, and a gradient on a
 * `position: sticky` element is re-rasterised on every single scroll frame —
 * 25 of them per frame, for a shadow nobody was looking at.
 */
const STICKY_ACTIONS = cn(
  'sticky right-0 z-[1] bg-s1 group-hover:bg-s2 group-aria-selected:bg-accent-soft',
  'before:pointer-events-none before:absolute before:inset-y-0 before:-left-px before:w-px before:bg-line'
)

/**
 * The same pin for the filter band's Actions cell.
 *
 * Written out rather than composed from `STICKY_ACTIONS` because that constant
 * carries `group-hover` / `group-aria-selected` backgrounds that describe a body
 * row's state, and this cell belongs to a header that has no such state. It also
 * has to paint the band's own tint, not the header's — a sticky cell that
 * disagrees with the row behind it shows as a stripe the moment anything scrolls
 * sideways.
 */
const STICKY_FILTER_ACTIONS = cn(
  'sticky right-0 z-[2] bg-s2',
  'before:pointer-events-none before:absolute before:inset-y-0 before:-left-px before:w-px before:bg-line'
)

/** Native checkbox, styled by `accent-color`. A Radix checkbox root per row is
 *  ~25 extra component instances mounting and unmounting on every scroll. */
const CHECKBOX = 'flex-none cursor-pointer accent-[var(--accent)]'

/* ================================================================== *
 * Viewport
 *
 * `matchMedia` through `useSyncExternalStore`: one subscription per query,
 * and it only fires when a breakpoint is actually crossed — no resize handler,
 * no state churn while the window is being dragged.
 * ================================================================== */

const MD_UP = '(min-width: 768px)'
/**
 * On a coarse pointer the lg/xl tiers arrive earlier. The desktop breakpoints
 * assume a 244px rail from `lg` up, but on touch devices the rail stays 60px
 * through `xl` (see TABLET_ONLY in rail.tsx), which frees 184px — enough that
 * the richer tiers fit sooner. Budgets against every variant's minWidth floor:
 * 900−60 = 840 ≥ lg floors (default 732, approved 736, pending 748);
 * 1100−60 = 1040 ≥ xl floors (default 1018, approved 996, pending 973).
 * Concretely: an iPad landscape (1024) gets a comfortable lg instead of an
 * at-the-floor one, and an iPad Pro landscape (1180) reaches xl — where the
 * Approved list's Job and Schedule columns live — instead of never seeing it.
 * Constant at module load, like IS_COARSE itself: the strings feed
 * useMediaQuery, so layout still flips on window resize exactly as before.
 */
const LG_UP = IS_COARSE ? '(min-width: 900px)' : '(min-width: 1024px)'
const XL_UP = IS_COARSE ? '(min-width: 1100px)' : '(min-width: 1280px)'
/** Where Cities AND Work hours both fit beside Status. See LAYOUT_WIDE_BUILD. */
const WIDE_UP = '(min-width: 1536px)'

function useMediaQuery(query: string): boolean {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {}
      }
      const mql = window.matchMedia(query)
      mql.addEventListener('change', onChange)
      return () => {
        mql.removeEventListener('change', onChange)
      }
    },
    [query]
  )

  const getSnapshot = React.useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(query).matches
  }, [query])

  return React.useSyncExternalStore(subscribe, getSnapshot, () => false)
}

/* ================================================================== *
 * Tone maps — Tailwind cannot see interpolated class names, so every
 * tone the UI can render is spelled out here.
 * ================================================================== */

const TONE_BAR: Readonly<Record<Tone, string>> = Object.freeze({
  live: 'bg-live',
  ok: 'bg-ok',
  info: 'bg-info',
  warn: 'bg-warn',
  danger: 'bg-danger',
  neutral: 'bg-neutral',
  accent: 'bg-accent',
})

/** Which live states allow which control. Colour is never the only signal. */
const CAN_START: ReadonlySet<LiveState> = new Set<LiveState>([
  'never_started',
  'stopped',
  'failed',
  'stale',
])
const CAN_STOP: ReadonlySet<LiveState> = new Set<LiveState>(['live', 'starting', 'stale'])

/** Run phases where work is in progress, so Stop is the useful control. */
const RUN_ACTIVE: ReadonlySet<RunPhase> = new Set<RunPhase>(RUN_ACTIVE_PHASES)

/**
 * Whether this system's run should own the row's status, rather than the legacy
 * state.
 *
 * The rule itself lives in `@acs/shared` because three readers have to agree on
 * it: this pill, the API's `liveState` stamp, and the query planner that decides
 * which rows a `?live=` filter reaches for. When it lived only here, the column
 * said "Holding shift" on a row that the Sessions filter did not return.
 */
function runOwnsRow(run: RunStateDto | null): run is RunStateDto {
  return runOwnsLiveState(run)
}

/* ================================================================== *
 * Formatting helpers (module scope, memoised where they are hot)
 * ================================================================== */

function labelForStep(step: string | null): string {
  if (!step) return 'No step yet'
  const known = STEP_LABELS[step as PipelineStep]
  return known ?? step
}

function stepIndexLabel(step: string | null): string {
  if (!step) return ''
  const index = STEP_INDEX[step]
  if (index === undefined) return ''
  return `${index + 1}/${Object.keys(STEP_INDEX).length}`
}

/** `"4m ago"` -> `"4m"`. The surrounding copy already supplies the tense. */
function stripAgo(label: string): string {
  return label.endsWith(' ago') ? label.slice(0, -4) : label
}

/** Restart the CSS animation on an element that may still be mid-flash.
 *  Both flash classes are cleared first so an update flash and a beat glide
 *  never stack — whichever fired last owns the row for its 1.1s. */
function playFlash(el: HTMLElement, cls: 'flash-update' | 'flash-beat' = 'flash-update'): void {
  el.classList.remove('flash-update', 'flash-beat')
  // Reading a layout property forces the style recalc that makes re-adding
  // the class start a *new* animation instead of continuing the old one.
  void el.offsetWidth
  el.classList.add(cls)
}

function handleFlashEnd(event: React.AnimationEvent<HTMLElement>): void {
  // Filtered by name: animationend BUBBLES, and a child's ending animation
  // (a menu, a spinner) must not strip a flash still mid-play on the row.
  if (event.animationName === 'acs-flash' || event.animationName === 'acs-beat') {
    event.currentTarget.classList.remove('flash-update', 'flash-beat')
  }
}

function rowLiveState(run: RunStateDto | null): LiveState {
  /*
   * From this system's run and nothing else.
   *
   * It used to blend in `deriveLiveState` over `row.sessionStatus` /
   * `currentStep` / `lastHeartbeatAt` — fields the SIBLING fleet writes — with
   * the run layered on top for ten minutes. So a row could read "Holding
   * shift", with a Stop button, for a session this console never started and
   * cannot stop.
   *
   * Still re-derived client-side rather than trusting `row.liveState`, because
   * the browser has a live clock: a run that stops being owned crosses the
   * retention window here without waiting for a server push.
   */
  return liveStateOf(run)
}

/**
 * The line under a blocked customer's badge: when, or why, in a few words.
 *
 * The DATE is the whole point when there is one. "Ineligible for Rehire - Within
 * Waiting Period" is a status an operator can do nothing about; "eligible from
 * 16 Sep 2026" is a date they can put in a calendar, and it is the difference
 * between a row that looks broken and a row that looks scheduled.
 *
 * Formatted in the browser's own locale rather than as the raw ISO instant: this
 * is a calendar day, not a timestamp, and `2026-09-16T00:00:00Z` invites the
 * reader to wonder about the time — which Amazon did not mean.
 */
function rehireSubtitle(rehire: NonNullable<CustomerRow['rehireEligibility']>): string {
  if (rehire.futureEligibleAt !== null) {
    const d = new Date(rehire.futureEligibleAt)
    if (!Number.isNaN(d.getTime())) {
      return `eligible from ${d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        // Read as a calendar day in UTC, which is how Amazon sends it. Without
        // this a midnight-UTC date renders as the previous day for anybody west
        // of Greenwich — every Canadian operator this console has.
        timeZone: 'UTC',
      })}`
    }
    // Unparseable: show what was sent rather than nothing. A date this cannot
    // read is a shape change worth seeing, not one worth hiding.
    return `eligible from ${rehire.futureEligibleAt}`
  }
  // The one refused status that is not about the past. Worth spelling out,
  // because "Active" on a red badge reads like a system state rather than like
  // "this person already works at Amazon".
  if (rehire.status.trim().toLowerCase() === 'active') return 'already employed at Amazon'
  return 'Amazon will not hire this customer'
}

/* ================================================================== *
 * Optimistic cache writes
 * ================================================================== */

/*
 * The merge itself is `applyCustomerPatches` in `hooks/use-customers` — the
 * same function the realtime stream and the drawer write through, so an
 * optimistic write here and a server push are indistinguishable to the rows,
 * and an edit made in this table reaches an open drawer in the same frame.
 * It used to be a copy of the stream's merge that skipped the detail cache.
 */

/*
 * One field each now, where there were six.
 *
 * The other five described the sibling fleet's pipeline step and heartbeat, and
 * writing them optimistically meant this console faking progress in another
 * system's record. `liveState` is ours, and it is the only thing the row reads.
 */
function startPatch(): Partial<CustomerRow> {
  return { liveState: 'starting' }
}

function stopPatch(): Partial<CustomerRow> {
  return { liveState: 'stopped' }
}

/** Exactly the fields an optimistic control write touches, for rollback. */
function controlSnapshot(row: CustomerRow): Partial<CustomerRow> {
  return { liveState: row.liveState }
}

/* ================================================================== *
 * Row actions — one stable object shared by every row
 * ================================================================== */

interface RowActions {
  openDetail: (id: string) => void
  toggleSelect: (id: string) => void
  start: (row: CustomerRow) => void
  stop: (row: CustomerRow) => void
  viewLogs: (row: CustomerRow) => void
  copy: (value: string) => void
  /**
   * Write this customer's `computer_number` — the tag in the Status column.
   *
   * Takes the row, not just the id, because a failed save has to put back the
   * value that was there when the edit started, and only the row carries it.
   */
  setNumber: (row: CustomerRow, value: string) => void
  /**
   * Write this customer's `hunt_workers`. `null` clears the override, which is
   * a real edit — it puts the customer back on the fleet-wide setting.
   */
  setWorkers: (row: CustomerRow, value: number | null) => void
  /**
   * Write this customer's `hunt_gql_workers` — the shift-list fleet's count.
   *
   * Same contract as `setWorkers`: `null` clears the override and is a real
   * edit. `0` is NOT the same as null here — it is stored as typed and raised to
   * 1 when the hunt arms — which is a different sentence from "use the fleet
   * default".
   */
  setGqlWorkers: (row: CustomerRow, value: number | null) => void
  setScheduleWorkers: (row: CustomerRow, value: number | null) => void
  setFlow: (row: CustomerRow, value: 'current' | 'brazil' | null) => void
  /** Open the job / shift reassignment dialog for this row. */
  editTarget: (row: CustomerRow) => void
  /** Pending only — see `ActionsCell`. */
  edit: (row: CustomerRow) => void
  approve: (row: CustomerRow) => void
  /** Approved -> Pending. See the note in `RowMenu`. */
  unapprove: (row: CustomerRow) => void
  askDelete: (row: CustomerRow) => void
  /** Move the table's single cities panel onto this cell. See `CitiesCell`. */
  showCities: (anchor: HTMLElement, cities: readonly string[], warehouses: number) => void
  hideCities: () => void
  /** Touch path: tap toggles the panel the pointer cannot hover. */
  toggleCities: (anchor: HTMLElement, cities: readonly string[], warehouses: number) => void
  /** Promote this row's overflow button into a real (open) Radix menu. */
  openMenu: (id: string) => void
  /** Radix's own open/close, once the menu exists. */
  setMenuOpen: (id: string, open: boolean) => void
  /** Flash timestamp for a row that changed while it was scrolled out of view. */
  flashAt: (id: string) => number | undefined
}

/**
 * `none`  — never opened: one plain `<button>`, zero library code.
 * `idle`  — opened at least once and still the tracked row; the Radix root
 *           stays mounted so that closing can hand focus back to its trigger.
 * `open`  — the menu is showing.
 */
type RowMenuState = 'none' | 'idle' | 'open'

/* ================================================================== *
 * Cells — every one defined at module scope so its identity is stable
 * across table renders and React can bail out on the memo boundary.
 * ================================================================== */

/**
 * The part of a cell the search matched, marked.
 *
 * ── Why this is worth rendering ─────────────────────────────────────
 * The box says "Search name, email, ID, job…" and then the table gives no clue
 * which of the four a row came back for. Typing `0591` returns four customers
 * whose names, ids and emails share nothing with it, and the reason — a job id
 * in a column that is 132px wide and frequently off-screen — is invisible. The
 * operator's next move is to widen the window or open a row to find out why it
 * is even in the list.
 *
 * ── Why it costs nothing ────────────────────────────────────────────
 * The table is virtualised, so this runs for the ~20 rows actually on screen and
 * not the 334 in the result: four `indexOf` calls a row, on strings of a few
 * dozen characters, inside a render that was already happening because the query
 * changed. The needle is lower-cased ONCE by the parent rather than per cell —
 * doing it here would be the only part of this that showed up in a profile.
 *
 * First occurrence only. These cells truncate, so a second match is usually not
 * on screen anyway, and splitting on every occurrence turns one span into an
 * unbounded number of them for a case nobody is reading.
 */
const Mark = React.memo(function Mark({ text, q }: { text: string; q: string }) {
  if (q === '' || text === '') return <>{text}</>
  const at = text.toLowerCase().indexOf(q)
  if (at < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, at)}
      {/*
        `bg-accent-soft` rather than the browser default, which is a solid yellow
        that would be the loudest thing on a dark table and would sit on top of
        the one colour this UI reserves for "this is being acted on".
      */}
      <mark className="rounded-[2px] bg-accent-soft px-[1px] text-accent">
        {text.slice(at, at + q.length)}
      </mark>
      {text.slice(at + q.length)}
    </>
  )
})

/**
 * How the server described the UUID form of a pair it just stored.
 *
 * The write always succeeds — an unresolvable shift is still a valid assignment
 * for the Current flow — so this is advice, never an error. `null` means the
 * request did not touch the job and shift.
 */
type TargetUuidOutcome = {
  state: 'cleared' | 'resolved' | 'job_only' | 'unknown_schedule' | 'pair_mismatch' | 'lookup_failed'
  warning: string | null
} | null

interface TargetSaveResult {
  ok: true
  uuid: TargetUuidOutcome
}

/** The row-level enum a save's outcome implies, for the optimistic patch. */
function uuidRowState(
  state: NonNullable<TargetUuidOutcome>['state'] | null
): 'resolved' | 'job_only' | 'unknown' | null {
  if (state === null || state === 'cleared') return null
  if (state === 'resolved') return 'resolved'
  if (state === 'job_only') return 'job_only'
  return 'unknown'
}

/**
 * The toast tone for a save that worked but may not be usable.
 *
 * `pair_mismatch` is the one that gets `danger`: the others are "this will work
 * later or on the other flow", and that one says the two ids the operator just
 * typed disagree with each other — which is a mistake to go and look at now.
 */
function uuidTone(
  state: NonNullable<TargetUuidOutcome>['state'] | null
): 'ok' | 'warn' | 'danger' {
  if (state === 'pair_mismatch') return 'danger'
  if (state === null || state === 'cleared' || state === 'resolved') return 'ok'
  return 'warn'
}

/**
 * What the shift's hover says about its UUID, appended to the id itself.
 *
 * Written as sentences an operator can act on rather than as state names. The
 * `resolved` case is the only one that says nothing further — there is nothing
 * to do about a shift that works.
 */
const UUID_TITLES: Record<'resolved' | 'job_only' | 'unknown', string> = {
  resolved: ' · shift UUID on file',
  job_only: ' · no shift UUID yet — the schedule poller has not filled it in',
  unknown: ' · no shift UUID on file — a Brazil run cannot address this shift',
}

/**
 * How the seat figure beside an AUTO chip reads, in one word.
 *
 *   open   the schedule workers last read the shift with room — the state it was picked in
 *   full   they last read zero: the queue has filled since the pick
 *   gone   no read has carried a count for the shift yet
 *
 * A legacy frame with no figure is not a tone; the caller renders nothing.
 */
type SeatTone = 'open' | 'full' | 'gone'

/*
 * A 6px disc in a translucent 2px ring of its own colour: the ring is what
 * makes a dot this small read as a lit indicator rather than a speck, on a
 * white ground or a black one. The `-soft` tokens are too faint for it at
 * this size, so the halo mixes the full colour down instead.
 */
const SEAT_DOT: Readonly<Record<SeatTone, string>> = Object.freeze({
  open: 'bg-live ring-2 ring-live/25',
  full: 'bg-warn ring-2 ring-warn/25',
  gone: 'bg-neutral ring-2 ring-neutral/25',
})

const SEAT_TEXT: Readonly<Record<SeatTone, string>> = Object.freeze({
  open: 'text-fg',
  full: 'text-warn',
  gone: 'text-faint',
})

function seatTone(seats: number | null): SeatTone {
  if (seats === null) return 'gone'
  return seats > 0 ? 'open' : 'full'
}

/** `Sep 06, 14:02` — the same shape the schedules page stamps its rows with. */
function seatStamp(atMs: number | null): string {
  if (atMs === null || !Number.isFinite(atMs) || atMs <= 0) return ''
  const when = new Date(atMs).toLocaleString(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  return `, as the schedule workers last read it at ${when}`
}

function seatTitle(seats: number | null, atMs: number | null, scheduleId: string): string {
  if (seats === null) {
    return `No seat count for ${scheduleId} yet — the schedule workers have not read one off the schedule.`
  }
  if (seats > 0) {
    return `${seats} seat${seats === 1 ? '' : 's'} open on ${scheduleId} — laborDemandAvailableCount as the schedule workers last read it${seatStamp(atMs)}.`
  }
  return `No seats open on ${scheduleId} — laborDemandAvailableCount was 0 as the schedule workers last read it${seatStamp(atMs)}. The shift has filled since this run picked it.`
}

/**
 * The seat count beside an AUTO chip: a dot in the colour of the answer and
 * the number itself.
 *
 * Green for room, amber for a queue that has filled, grey for a shift no read
 * has carried a count for yet. The dot carries the meaning and the number
 * carries the amount, so a row reads at a glance from across the room and
 * exactly on hover — the `title` names the field and the moment the schedule
 * workers last read it, which is what separates a fresh zero from a stale one.
 *
 * A native `title`, never a Tooltip, for the reason `JobCell` gives.
 */
function SeatFigure(p: { seats: number | null; atMs: number | null; scheduleId: string }) {
  const tone = seatTone(p.seats)
  return (
    <span
      title={seatTitle(p.seats, p.atMs, p.scheduleId)}
      className={cn(
        'tabular inline-flex flex-none cursor-default items-center gap-[4px] font-mono text-[10.5px] leading-[1.4]',
        SEAT_TEXT[tone]
      )}
    >
      <span aria-hidden="true" className={cn('size-[6px] flex-none rounded-full', SEAT_DOT[tone])} />
      {p.seats === null ? '—' : String(p.seats)}
    </span>
  )
}

/**
 * The AUTO chip, and the seat figure that rides beside it.
 *
 * One component for the four places the chip appears — the customer cell, the
 * phone card, the combined Job / Schedule column and the two split id columns
 * — so they cannot disagree about what the chip looks like or what sits next
 * to it. The figure is absent, not dashed, when the frame carries none: that
 * is a run recorded before the field existed, and a dash would claim the
 * schedule workers had read it.
 *
 * Sized to the mode chip, as before, so the fixed row height the virtualiser
 * depends on does not move. Primitive props only, for the memo.
 */
const AutoChip = React.memo(function AutoChip(p: {
  /** The hover text on the chip itself — what was picked and why. */
  title: string
  scheduleId: string
  seats: number | null | undefined
  seatsAtMs: number | null | undefined
}) {
  return (
    <span className="inline-flex max-w-full flex-none items-center gap-1">
      <span
        title={p.title}
        className="flex-none cursor-default rounded-xs bg-accent-soft px-1 font-mono text-[10px] leading-[1.4] text-accent"
      >
        AUTO
      </span>
      {p.seats === undefined ? null : (
        <SeatFigure seats={p.seats} atMs={p.seatsAtMs ?? null} scheduleId={p.scheduleId} />
      )}
    </span>
  )
})

/** The chip's own hover text, shared by every place it appears. */
function autoTitle(t: {
  jobId: string
  scheduleId: string
  siteId: string
  scheduleType: string
}): string {
  return `Auto-picked ${t.jobId} · ${t.scheduleId} (${t.siteId}, ${t.scheduleType}) — chosen by this run, not saved to the customer`
}

const JobCell = React.memo(function JobCell(p: {
  jobId: string | null
  jobIdFull: string | null
  scheduleId: string | null
  scheduleIdFull: string | null
  /**
   * The pair the RUNNING run chose for itself, when the customer has none.
   *
   * Passed as primitives rather than the `autoTarget` object: this is a
   * `React.memo` with the default shallow compare, and the object is rebuilt on
   * every published frame — so passing it would re-render the cell several times
   * a second for the whole life of a multi-hour wait.
   */
  autoJobId: string | null
  autoScheduleId: string | null
  /** The shift's seat count; `undefined` on a frame that predates the field. */
  autoSeats: number | null | undefined
  autoSeatsAtMs: number | null | undefined
  /**
   * Whether this shift can be addressed in UUID form — what the Brazil flow's
   * create needs.
   *
   * Extends the two `title` strings below and adds no DOM at all. Deliberately
   * not a chip and not a colour: on a fleet mid-migration most rows are the same
   * value, and a marker on nearly every row is noise rather than signal. An
   * operator asking about one specific customer hovers; one asking about the
   * fleet reads the counter on the run panel.
   */
  targetUuid: 'resolved' | 'job_only' | 'unknown' | null
  /** Lower-cased search needle, or '' — see `Mark`. */
  q: string
}) {
  /*
   * Auto-picked ids are shown in place of "Unassigned", and marked.
   *
   * The customer genuinely has neither id — nothing has been written to their
   * record and nothing will be — so the cell would otherwise read "Unassigned"
   * about a run that is actively applying to a specific shift. The AUTO chip is
   * what keeps that honest: these ids belong to the run, not to the person, and
   * looking the customer up will not find them.
   */
  if (p.jobId === null && p.scheduleId === null && p.autoJobId !== null) {
    const bare = (v: string | null): string => (v === null ? '—' : v.slice(v.lastIndexOf('-') + 1))
    const title = `Picked by this run: ${p.autoJobId} · ${p.autoScheduleId ?? '—'}. Not saved to the customer.`
    /*
     * The seat figure sits on the SCHEDULE line here, not beside the chip.
     *
     * This is the one place the chip shares its line with ten digits inside a
     * 132px track, and the figure's twenty-odd pixels would come straight out
     * of them. The count is a fact about the shift anyway, so the line that
     * names the shift is the honest place for it; the split columns, which
     * have the room, keep it beside the chip.
     */
    return (
      <span className="flex min-w-0 flex-col gap-0.5 leading-[1.25]">
        <span className="tabular flex items-baseline gap-1 font-mono text-[11.5px]">
          <AutoChip
            title={title}
            scheduleId={p.autoScheduleId ?? '—'}
            seats={undefined}
            seatsAtMs={undefined}
          />
          <span title={title} className="cursor-default truncate text-fg">
            {bare(p.autoJobId)}
          </span>
        </span>
        <span className="tabular flex items-baseline gap-1 font-mono text-[11.5px]">
          <span className="text-faint">S</span>
          <span title={title} className="cursor-default truncate text-muted">
            {bare(p.autoScheduleId)}
          </span>
          {p.autoSeats === undefined || p.autoScheduleId === null ? null : (
            <SeatFigure
              seats={p.autoSeats}
              atMs={p.autoSeatsAtMs ?? null}
              scheduleId={p.autoScheduleId}
            />
          )}
        </span>
      </span>
    )
  }
  if (p.jobId === null && p.scheduleId === null) {
    return <span className="text-[12px] text-faint">Unassigned</span>
  }
  return (
    <span className="flex min-w-0 flex-col gap-0.5 leading-[1.25]">
      {/* `title`, not a Tooltip: two Radix roots per row, 50 per screen, is
          the difference between a 60fps scroll and a 40fps one. */}
      <span
        title={p.jobIdFull ?? 'No job assigned'}
        className="tabular flex cursor-default items-baseline gap-1 font-mono text-[11.5px]"
      >
        <span className="text-faint">J</span>
        <span className="truncate text-fg">
          {p.jobId === null ? '—' : <Mark text={p.jobId} q={p.q} />}
        </span>
      </span>
      <span
        title={
          p.scheduleIdFull === null
            ? 'No schedule assigned'
            : `${p.scheduleIdFull}${UUID_TITLES[p.targetUuid ?? 'unknown']}`
        }
        className="tabular flex cursor-default items-baseline gap-1 font-mono text-[11.5px]"
      >
        <span className="text-faint">S</span>
        <span className="truncate text-muted">
          {p.scheduleId === null ? '—' : <Mark text={p.scheduleId} q={p.q} />}
        </span>
      </span>
    </span>
  )
})

/**
 * The cities a customer is watching.
 *
 * A row can carry many — the cell shows the first and a count, because
 * rendering them all would either blow the row height or truncate into a string
 * that says nothing.
 *
 * ── Why the hover is hand-rolled ────────────────────────────────────
 * This started as a native `title`, for the reason `JobCell` still uses one:
 * fifty Radix roots on screen is the difference between a 60fps scroll and a
 * 40fps one. But a `title` is the wrong control for a LIST — it waits about a
 * second, renders in the OS font at the pointer, and several browsers collapse
 * the newlines, so "all the cities" arrived as one run-on line if it arrived at
 * all.
 *
 * So there is exactly ONE panel for the whole table, owned by `CustomerTable`
 * and moved to whichever cell the pointer is over. Fifty cells cost fifty
 * `onMouseEnter` props — which they already had — and no portals, no positioning
 * work and no subscriptions until something is actually hovered.
 */
const CitiesCell = React.memo(
  function CitiesCell(p: {
    cities: readonly string[]
    warehouses: number
    actions: RowActions
  }) {
    const { cities, warehouses, actions } = p

    const onEnter = React.useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        if (cities.length === 0) return
        actions.showCities(e.currentTarget, cities, warehouses)
      },
      [actions, cities, warehouses]
    )

    // Touch has no hover, so a tap toggles the same shared panel. Defined
    // unconditionally (hooks) but ATTACHED only on coarse pointers: on desktop
    // the prop is `undefined`, so the rendered output — and the memo bailout —
    // are byte-identical to before. stopPropagation keeps the tap from also
    // reaching the body's delegated row-click handler.
    const onToggle = React.useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        if (cities.length === 0) return
        e.stopPropagation()
        actions.toggleCities(e.currentTarget, cities, warehouses)
      },
      [actions, cities, warehouses]
    )

    if (cities.length === 0) {
      return <span className="text-[12px] text-faint">None</span>
    }

    // Title-cased for display; `city_tags` stores them lower-case.
    const label = titleCase(cities[0] as string)
    const rest = cities.length - 1

    return (
      <span
        data-cities-cell=""
        onMouseEnter={onEnter}
        onMouseLeave={actions.hideCities}
        onClick={IS_COARSE ? onToggle : undefined}
        className="flex min-w-0 cursor-default flex-col justify-center leading-[1.25]"
      >
        {/* The whole list, for a screen reader, which cannot hover — and, via
            the tap toggle above, for touch, which cannot hover either. The cell
            is deliberately not focusable: making it so would add one tab stop
            per row — fifty on a screen — between the checkbox and the buttons. */}
        <span className="sr-only">
          {cities.length === 1 ? 'City: ' : `${cities.length} cities: `}
          {cities.map(titleCase).join(', ')}
          {warehouses > 0 ? `. ${warehouses} warehouses.` : ''}
        </span>

        <span aria-hidden="true" className="truncate text-[12.5px] text-muted">
          {label}
        </span>
        <span aria-hidden="true" className="text-[11px] text-faint tabular">
          {rest > 0 ? `+${rest} more` : null}
          {rest > 0 && warehouses > 0 ? ' · ' : null}
          {warehouses > 0 ? `${warehouses} site${warehouses === 1 ? '' : 's'}` : null}
        </span>
      </span>
    )
  },
  (a, b) =>
    a.actions === b.actions && a.warehouses === b.warehouses && sameList(a.cities, b.cities)
)

/** What the shared panel is currently showing, and where. */
interface CitiesHover {
  cities: readonly string[]
  warehouses: number
  /** Viewport coordinates of the cell it belongs to. */
  top: number
  left: number
  bottom: number
}

/**
 * The one cities panel.
 *
 * Fixed-position rather than portalled into the row: the row is inside a
 * `contain: layout paint style` box that also clips, so anything absolutely
 * positioned inside it is cut off at the cell edge. Fixed coordinates come from
 * the anchor's own rect, which is already in viewport space.
 *
 * Flips above the cell when there is not room below, and is clamped to the
 * viewport on both axes so a row at the bottom of a long list still shows its
 * whole list rather than half of it.
 */
const CITIES_PANEL_WIDTH = 232
const CITIES_PANEL_GAP = 6

function CitiesPanel(p: { hover: CitiesHover | null }) {
  const { hover } = p
  if (hover === null) return null

  const rows = hover.cities.length
  // 22px per line + the header line + 16px of padding, capped so a fifty-city
  // customer scrolls instead of covering the window.
  const estimated = Math.min(rows * 22 + 30, 320)
  const below = window.innerHeight - hover.bottom - CITIES_PANEL_GAP
  const flip = below < estimated && hover.top > below

  const left = Math.max(
    8,
    Math.min(hover.left, window.innerWidth - CITIES_PANEL_WIDTH - 8)
  )

  return (
    <div
      role="tooltip"
      aria-hidden="true"
      style={{
        position: 'fixed',
        left,
        width: CITIES_PANEL_WIDTH,
        maxHeight: 320,
        ...(flip
          ? { bottom: window.innerHeight - hover.top + CITIES_PANEL_GAP }
          : { top: hover.bottom + CITIES_PANEL_GAP }),
      }}
      className={cn(
        'z-[70] overflow-y-auto overscroll-contain rounded-md border border-line bg-s2 p-2 shadow-e3',
        // Never intercepts the pointer: the panel can overlap the cell that
        // opened it, and a pointer landing on the panel would fire the cell's
        // mouseleave and close it — a tooltip that flickers itself away.
        'pointer-events-none'
      )}
    >
      <div className="flex items-baseline justify-between gap-2 pb-1.5">
        <span className="text-[10.5px] font-semibold tracking-[0.05em] text-faint uppercase">
          {rows} {rows === 1 ? 'city' : 'cities'}
        </span>
        {hover.warehouses > 0 ? (
          <span className="text-[10.5px] text-faint tabular">
            {hover.warehouses} site{hover.warehouses === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
      <ul className="flex flex-col gap-0.5">
        {hover.cities.map((city) => (
          <li key={city} className="truncate text-[12px] text-muted">
            {titleCase(city)}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The account PIN, under the email.
 *
 * Rendered plainly rather than masked with a reveal: this is the Pending list,
 * where the operator is checking a handover against what was typed in, and a
 * value they have to click 165 times is a value they will stop checking. It is
 * labelled so a screen reader does not read six bare digits, and it carries no
 * `Mark` — the search box does not query it, so highlighting would imply it did.
 */
const PinLine = React.memo(function PinLine(p: { pin: string }) {
  return (
    <span className="flex min-w-0 items-center gap-1" title={`Account PIN ${p.pin}`}>
      <span aria-hidden="true" className="flex-none text-[9.5px] tracking-[0.06em] text-faint uppercase">
        pin
      </span>
      <span className="tabular truncate font-mono text-[11px] text-muted">
        <span className="sr-only">Account PIN </span>
        {p.pin}
      </span>
    </span>
  )
})

/** `"scarborough, on"` -> `"Scarborough, ON"` — the province stays upper-case. */
function titleCase(city: string): string {
  const comma = city.lastIndexOf(',')
  if (comma === -1) return city.replace(/\b[a-z]/g, (c) => c.toUpperCase())
  const place = city.slice(0, comma).replace(/\b[a-z]/g, (c) => c.toUpperCase())
  return `${place},${city.slice(comma + 1).toUpperCase()}`
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false
  return true
}

/**
 * Work hours, stacked — the same shape as Job / Schedule.
 *
 * There are only ever three values, so all three fit without a count or a
 * tooltip. Two lines is the row's natural capacity at either density; a third
 * is rendered at a tighter leading rather than hidden, because "Full-time,
 * Part-time and Flex Time" is meaningfully different from "Full-time and
 * Part-time" and a `+1` would hide exactly which one was dropped.
 */
const WorkHoursCell = React.memo(
  function WorkHoursCell(p: { hours: readonly string[] }) {
    if (p.hours.length === 0) {
      return <span className="text-[12px] text-faint">Any</span>
    }
    return (
      <span
        title={p.hours.join('\n')}
        className="flex min-w-0 cursor-default flex-col justify-center leading-[1.2]"
      >
        {p.hours.map((h) => (
          <span key={h} className="truncate text-[11.5px] text-muted">
            {h}
          </span>
        ))}
      </span>
    )
  },
  (a, b) => sameList(a.hours, b.hours)
)

/**
 * One id of the split Job / Schedule pair (Approved list only).
 *
 * The same rules as `JobCell`, halved: native `title` for the qualified form
 * (never a Radix tooltip in a row), the search mark, and the AUTO chip when a
 * running hunt picked this id for a customer who has none of their own — the
 * chip is what keeps the cell honest about an id that belongs to the run, not
 * the person. Primitive props only, so the default shallow memo holds while
 * the run map republishes frames around it.
 */
const IdOnlyCell = React.memo(function IdOnlyCell(p: {
  /** Dimmed single-letter prefix, flush against the digits: `J0000000592`. */
  prefix: 'J ' | 'S '
  value: string | null
  full: string | null
  /** The running pick, shown only when the customer has no id of their own. */
  auto: string | null
  /**
   * The picked SHIFT, whichever id this cell shows — the seat figure is a
   * fact about the shift, and the job column names it in its hover too.
   */
  autoScheduleId: string | null
  /** The shift's seat count; `undefined` on a frame that predates the field. */
  autoSeats: number | null | undefined
  autoSeatsAtMs: number | null | undefined
  q: string
}) {
  if (p.value === null && p.auto !== null) {
    const bare = p.auto.slice(p.auto.lastIndexOf('-') + 1)
    const title = `Picked by this run: ${p.auto}. Not saved to the customer.`
    /*
     * The id on its own line and the chip BENEATH it, not beside it. Side by
     * side, the chip's 34px came out of a 108px track sized for ten digits,
     * and the one value the column exists to show read `0000…`. Stacked, the
     * id keeps the whole width and the chip takes the second line the row
     * already has — the same shape the stacked Work hours cell uses — and
     * that second line is where the seat figure rides, beside the chip.
     */
    return (
      <span className="tabular flex min-w-0 flex-col items-start justify-center gap-0.5 font-mono text-[11.5px] leading-[1.2]">
        <span title={title} className="max-w-full cursor-default truncate text-fg">
          <span className="text-faint">{p.prefix}</span>
          {bare}
        </span>
        <AutoChip
          title={title}
          scheduleId={p.autoScheduleId ?? '—'}
          seats={p.autoScheduleId === null ? undefined : p.autoSeats}
          seatsAtMs={p.autoSeatsAtMs}
        />
      </span>
    )
  }
  if (p.value === null) {
    return <span className="text-[12px] text-faint">—</span>
  }
  return (
    <span
      title={p.full ?? undefined}
      className="tabular flex min-w-0 cursor-default items-baseline font-mono text-[11.5px]"
    >
      <span className="truncate text-fg">
        <span aria-hidden="true" className="text-faint">
          {p.prefix}
        </span>
        <Mark text={p.value} q={p.q} />
      </span>
    </span>
  )
})

/**
 * The column the whole redesign is about.
 *
 * The legacy panel spread this across three stacked widgets (a status pill, a
 * step string and a heartbeat clock) which meant the operator had to assemble
 * the state themselves. Here it is one block: what the session *is*, what it
 * is doing, and how far through it is — plus a loud, unmissable readout when
 * the heartbeat has stopped.
 */
/* ------------------------------------------------------------------ *
 * The Status column's editable tag
 * ------------------------------------------------------------------ */

/**
 * How long after the last keystroke the value is written.
 *
 * Longer than the filter row's 250ms on purpose: a filter that fires early
 * costs a read, and this costs a write to a shared production collection plus a
 * change-stream frame to every open console. 600ms is under the threshold where
 * a person starts wondering whether it saved, and it collapses a five-character
 * tag into one write instead of five.
 */
const NUMBER_SAVE_MS = 600

/** The stored column's own ceiling is six characters; this only stops a paste. */
const NUMBER_MAX_LENGTH = 64

/** Keeps a gesture inside a control instead of letting the row read it. */
function stopEvent(event: React.SyntheticEvent): void {
  event.stopPropagation()
}

/** Everything an inline cell spreads onto its `<input>`. */
interface InlineEditProps {
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur: () => void
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onDoubleClick: (event: React.SyntheticEvent) => void
}

/**
 * A cell you can type into, inside a virtualised table fed by a live stream.
 *
 * Extracted from the `computer_number` tag when the Workers column needed the
 * same behaviour. Every hard part here is about the environment rather than the
 * value, which is why one hook can serve both: the draft is a string, the save
 * is a callback, and what the string MEANS is the caller's business.
 *
 * ── Four things this has to get right ───────────────────────────────
 *
 * **It must not fight the live stream.** The same value arrives back over SSE a
 * moment after it is written — and can arrive from another operator at any time.
 * `seededRef` records the last server value adopted, so an echo of our own write
 * is recognised and ignored while somebody else's edit is taken up. Nothing
 * overwrites the box while it has focus, because deleting a character out from
 * under a person mid-word is worse than being briefly stale; a value that lands
 * during an edit is parked in `deferredRef` and applied on blur, but only if the
 * operator did not type something of their own, which wins.
 *
 * **It must not lose an edit to a scroll.** The list is virtualised, so the cell
 * unmounts the moment the row leaves the viewport — with the debounce still
 * pending and without a blur. The unmount flushes.
 *
 * **It must not hijack the table's keyboard.** The body delegates arrow keys,
 * Home, End and PageUp/Down to move the row focus, and calls `preventDefault` on
 * all of them. Inside a text field those are caret movements a person expects to
 * work, so every key is stopped here before it reaches that handler.
 *
 * **Blur must read the draft, not the DOM.** Enter and Escape both act and then
 * `blur()`, synchronously, before React has re-rendered — so `currentTarget.value`
 * inside the blur that follows is still the *pre-key* text. Reading it there made
 * Escape save the value it was cancelling and made Enter briefly reinstate the
 * old one. `draftRef` is the state, and it is updated before either key acts.
 */
function useInlineEdit(p: {
  /** The server's value for this cell, as text. `''` when there is none. */
  value: string
  /** Called with the trimmed draft when it settles. Never with an unchanged one. */
  save: (next: string) => void
  debounceMs: number
  /**
   * Runs on every keystroke, before the draft is set — for a column that can
   * express what it will accept as a rewrite rather than as a refusal. Identity
   * by default. Must be pure and idempotent: it also runs on the committed text.
   */
  sanitize?: (next: string) => string
}): InlineEditProps {
  const { value } = p

  const [draft, setDraft] = React.useState(value)
  /** What is in the box, readable synchronously. `draft` is its rendered copy. */
  const draftRef = React.useRef(value)
  /** The last server value adopted. Distinguishes an echo from a real change. */
  const seededRef = React.useRef(value)
  /** The last value handed to `save`, so a no-op edit writes nothing. */
  const savedRef = React.useRef(value)
  /** A server value that landed while this box had focus, held until blur. */
  const deferredRef = React.useRef<string | null>(null)
  const focusedRef = React.useRef(false)
  const timerRef = React.useRef<number | null>(null)
  /** Typed but not yet written, for the unmount flush. `null` when settled. */
  const pendingRef = React.useRef<string | null>(null)

  const applyDraft = React.useCallback((next: string) => {
    draftRef.current = next
    setDraft(next)
  }, [])

  /**
   * Latest committer and latest sanitiser, reassigned every render.
   *
   * Refs rather than deps so every callback below stays stable for the life of
   * the cell — and so the unmount effect can be mount-only and still flush
   * through the current closure rather than the one from mount.
   */
  const commitRef = React.useRef<(next: string) => void>(() => {})
  commitRef.current = (next: string) => {
    pendingRef.current = null
    const trimmed = next.trim()
    if (trimmed === savedRef.current) return
    savedRef.current = trimmed
    // Our own write will come back over the stream; recording it as seeded is
    // what stops that echo from re-seeding the box.
    seededRef.current = trimmed
    p.save(trimmed)
  }
  const sanitizeRef = React.useRef<(next: string) => string>((next) => next)
  sanitizeRef.current = p.sanitize ?? ((next) => next)

  const clearTimer = React.useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  React.useEffect(() => {
    if (value === seededRef.current) return
    seededRef.current = value
    // Someone else's edit, or our own rolled back after a failed save.
    if (focusedRef.current) {
      deferredRef.current = value
      return
    }
    deferredRef.current = null
    savedRef.current = value
    pendingRef.current = null
    clearTimer()
    applyDraft(value)
  }, [value, clearTimer, applyDraft])

  React.useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
      const pending = pendingRef.current
      if (pending !== null) commitRef.current(pending)
    }
    // Mount-only: `commitRef` carries the current closure, so nothing here
    // needs to be a dependency and the cleanup cannot fire early.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = sanitizeRef.current(event.target.value)
      applyDraft(next)
      pendingRef.current = next
      clearTimer()
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        commitRef.current(next)
      }, p.debounceMs)
    },
    [applyDraft, clearTimer, p.debounceMs]
  )

  const onFocus = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    focusedRef.current = true
    // A couple of characters, usually replaced wholesale. Selecting means the
    // next keystroke overwrites rather than appending to what is already there.
    event.currentTarget.select()
  }, [])

  const onBlur = React.useCallback(() => {
    focusedRef.current = false
    clearTimer()

    const typed = draftRef.current
    if (typed.trim() !== savedRef.current) {
      // The operator's own edit supersedes anything that landed during it.
      deferredRef.current = null
      commitRef.current(typed)
      return
    }

    // Nothing of ours to write. Take up whatever the effect parked.
    pendingRef.current = null
    const deferred = deferredRef.current
    deferredRef.current = null
    if (deferred !== null && deferred !== typed) {
      savedRef.current = deferred
      applyDraft(deferred)
    }
  }, [applyDraft, clearTimer])

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Before anything else: the row grid owns arrows, Home, End and Page keys
      // and preventDefaults them. In a text field they belong to the caret.
      event.stopPropagation()

      if (event.key === 'Enter') {
        event.preventDefault()
        clearTimer()
        commitRef.current(draftRef.current)
        event.currentTarget.blur()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        clearTimer()
        pendingRef.current = null
        applyDraft(savedRef.current)
        event.currentTarget.blur()
      }
    },
    [applyDraft, clearTimer]
  )

  return {
    value: draft,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    // A double click in a text box is somebody selecting the word they are
    // about to retype; the body would otherwise read it as "open this customer".
    onDoubleClick: stopEvent,
  }
}

/**
 * The `computer_number` cell, edited in place.
 *
 * 425 of 611 customers carry one; there are 64 distinct values and the longest
 * is six characters ("1N A S"). It is not derived from anything and nothing
 * reads it but a person — it is the operator's own shorthand for where a
 * customer has got to. So it is a text field, not a picker, and it saves without
 * a button. Everything about surviving the stream, the scroll and the table's
 * keyboard lives in `useInlineEdit`.
 */
const NumberTag = React.memo(function NumberTag(p: {
  row: CustomerRow
  actions: RowActions
  /**
   * The dedicated Number column's size. The compact default is shaped to
   * share a line with the status pill; a column of its own has the room for
   * a box that reads like an input rather than a tag.
   */
  wide?: boolean
}) {
  const value = p.row.computerNumber ?? ''
  const { row, actions } = p
  const save = React.useCallback(
    (next: string) => {
      actions.setNumber(row, next)
    },
    [actions, row]
  )
  const field = useInlineEdit({ value, save, debounceMs: NUMBER_SAVE_MS })
  const draft = field.value

  return (
    <input
      type="text"
      {...field}
      maxLength={NUMBER_MAX_LENGTH}
      spellCheck={false}
      autoComplete="off"
      aria-label={`Number for ${p.row.customerId}`}
      title={draft === '' ? 'Add a number' : draft}
      placeholder="–"
      className={cn(
        'flex-none rounded-xs border border-transparent bg-transparent px-1',
        // 16px below `md`, where the phone card is the layout: iOS Safari zooms
        // the whole page when a focused input's text is under 16px, and a tap on
        // a two-character tag is not worth pinching back out of. The table only
        // exists from `md` up, so the small size is scoped to exactly where it
        // is safe.
        p.wide
          ? 'h-[26px] w-[56px] text-[16px] md:h-[24px] md:w-[56px] md:text-[12px]'
          : 'h-[22px] w-[58px] text-[16px] md:h-[18px] md:w-[46px] md:text-[10.5px]',
        'text-center font-mono text-faint',
        'outline-none transition-colors duration-100',
        'placeholder:text-faint/50',
        'hover:border-line-strong hover:bg-s2 hover:text-fg',
        'focus:border-accent focus:bg-s1 focus:text-fg focus:ring-2 focus:ring-accent/20'
      )}
    />
  )
})

/**
 * Digits only, no leading zeros, and never more than two of them.
 *
 * The ceiling is 50, so a third digit cannot be part of any valid answer and
 * refusing the keystroke is kinder than accepting "150" and answering 400 for
 * it. The remaining two refusals — `0`, and 51 through 99 — are left to the
 * server on purpose: it owns the bound, its messages say why, and the save path
 * already rolls back and reports. Duplicating the range here would put the same
 * rule in a third place for the sake of one round trip.
 *
 * ── Why the leading zero is not merely cosmetic ─────────────────────
 * "07" parses to 7. A row already on 7 would therefore send 7, `saveWorkers`
 * would recognise it as unchanged and write nothing, and no echo would ever
 * come back to correct the box — leaving "07" on screen until the row happened
 * to unmount. Normalising here means the draft and the stored value can never
 * disagree about the same number.
 *
 * Idempotent, as the hook requires: it also runs over already-clean text.
 */
function digitsOnly(next: string): string {
  return next
    .replace(/\D/g, '')
    // A lone "0" survives, so typing it is a visible refusal rather than a
    // keystroke that silently does nothing.
    .replace(/^0+(?=\d)/, '')
    .slice(0, 2)
}

/**
 * How many pollers this customer's hunt arms with, for ONE of the two detectors.
 * Approved list only.
 *
 * ── Why one component serves both columns ──────────────────────────
 * `Workers` and `Shift list` are the same control over two fields: same
 * inherit-on-blank rule, same two-digit vocabulary, same next-Start semantics,
 * same optimistic save. Two copies would be two places for the debounce, the
 * sanitiser and the placeholder contract to drift apart, and the one thing that
 * must never differ between them is exactly what a reader would have to compare
 * line by line to confirm. What genuinely differs is the wording, so that is
 * what is passed in.
 *
 * ── Why the default is a placeholder and not a value ────────────────
 * An empty box means "use the hunt setting", and the setting's current number
 * is shown greyed out in it. Writing that number onto every row instead would
 * be a lie with consequences: 600 customers would each be pinned to whatever
 * the global happened to be on the day this shipped, and changing the setting
 * afterwards would move none of them. Blank is a real state, it is the state
 * every customer is in today, and clearing the box returns them to it.
 *
 * ── Zero is a value, and it is NOT blank ────────────────────────────
 * Blank inherits; `0` turns that detector off for this customer. The two look
 * similar in a narrow box and mean entirely different things, so each column
 * says what ITS zero does in its own tooltip — they are mirror images, and a
 * shared wording would be wrong for one of them.
 *
 * ── It applies on the next Start ────────────────────────────────────
 * A hunt fixes its worker counts when it arms — re-striping live pollers for a
 * customer who may be seconds from a strike is not worth a setting that will
 * apply to their next run anyway. So editing this on a running row is allowed
 * and saved, and takes effect when the operator stops and starts it. The column
 * header says so.
 */
const WorkerCountCell = React.memo(function WorkerCountCell(p: {
  /** Current override, or null to inherit. */
  value: number | null
  /** Persists the edit. `null` clears the override. */
  onSave: (next: number | null) => void
  /** The fleet-wide default, shown as the placeholder. */
  fallback: number
  /** Screen-reader label, already naming the customer. */
  label: string
  /** Tooltip while the box is empty. */
  inheritHint: string
  /** Tooltip while it carries an override; receives the current draft. */
  overrideHint: (draft: string) => string
}) {
  const value = p.value === null ? '' : String(p.value)
  const { onSave } = p
  const save = React.useCallback(
    (next: string) => {
      onSave(next === '' ? null : Number(next))
    },
    [onSave]
  )
  const field = useInlineEdit({
    value,
    save,
    debounceMs: NUMBER_SAVE_MS,
    sanitize: digitsOnly,
  })
  const draft = field.value
  const inherited = draft === ''

  return (
    <input
      type="text"
      {...field}
      // `inputMode` rather than `type="number"`: the spinner arrows are useless
      // at this size, and a number input reports `''` for anything it considers
      // invalid — which would make a stray keystroke indistinguishable from the
      // operator clearing the override.
      inputMode="numeric"
      maxLength={2}
      spellCheck={false}
      autoComplete="off"
      aria-label={p.label}
      title={inherited ? p.inheritHint : p.overrideHint(draft)}
      placeholder={String(p.fallback)}
      className={cn(
        'flex-none rounded-xs border border-transparent bg-transparent px-1',
        // Two digits is the whole vocabulary, and 36px holds two of 12px mono
        // with the caret beside them — the track is sized to exactly this box.
        // 16px on coarse pointers for the usual reason: iOS Safari zooms the
        // page on a focused input under 16px.
        'h-[26px] w-[36px] text-[16px] md:h-[24px] md:w-[36px] md:text-[12px]',
        'text-center font-mono',
        'outline-none transition-colors duration-100',
        // An override is the operator's decision and reads at full strength; an
        // inherited row is the quiet default and reads like the placeholder it
        // effectively is. That contrast IS the column — it is how a glance down
        // it finds the handful of customers running hot.
        inherited ? 'text-faint' : 'font-medium text-fg',
        'placeholder:font-normal placeholder:text-faint/50',
        'hover:border-line-strong hover:bg-s2 hover:text-fg',
        'focus:border-accent focus:bg-s1 focus:text-fg focus:ring-2 focus:ring-accent/20'
      )}
    />
  )
})

/** The schedule watch's count — `get-schedule-details`, the detector that always runs. */
const WorkersCell = React.memo(function WorkersCell(p: {
  row: CustomerRow
  actions: RowActions
  /** The fleet-wide `workersPerCustomer`, shown as the placeholder. */
  fallback: number
}) {
  const { row, actions, fallback } = p
  const onSave = React.useCallback(
    (next: number | null) => {
      actions.setWorkers(row, next)
    },
    [actions, row]
  )
  return (
    <WorkerCountCell
      value={row.huntWorkers}
      onSave={onSave}
      fallback={fallback}
      label={`Schedule workers for ${row.customerId}`}
      inheritHint={`Using the hunt setting (${fallback} workers). Type a number to give this customer its own. This is the only fleet that watches for the opening and claims, so 0 leaves nothing hunting.`}
      overrideHint={(draft) =>
        draft === '0'
          ? `No schedule watch — nothing watches for the opening, and the hunt will refuse to arm. Clear the box to use the hunt setting (${fallback}).`
          : `${draft} worker${draft === '1' ? '' : 's'} reading the schedule — takes effect on the next Start. Clear the box to use the hunt setting (${fallback}).`
      }
    />
  )
})

/**
 * The shift-list fleet's count — `searchScheduleCards`.
 *
 * On the Current flow these run only while the customer's shift has no UUID:
 * they ask the JOB which schedules it is advertising, match this customer's
 * shift against the answer and take the UUID beside it, then stand down. They
 * never claim. The server floors the count at 1, so the placeholder shows what
 * will actually run rather than what was typed.
 */
const GqlWorkersCell = React.memo(function GqlWorkersCell(p: {
  row: CustomerRow
  actions: RowActions
  /** The fleet-wide `graphqlWorkersPerCustomer`, shown as the placeholder. */
  fallback: number
}) {
  const { row, actions, fallback } = p
  const onSave = React.useCallback(
    (next: number | null) => {
      actions.setGqlWorkers(row, next)
    },
    [actions, row]
  )
  return (
    <WorkerCountCell
      value={row.huntGqlWorkers}
      onSave={onSave}
      fallback={fallback}
      label={`Shift-list workers for ${row.customerId}`}
      inheritHint={`Using the hunt setting (${fallback} worker${fallback === 1 ? '' : 's'}). Type a number to give this customer its own. These run only while the shift has no UUID, to read it off the job's listing, and never claim.`}
      overrideHint={(draft) =>
        draft === '0'
          ? `Raised to 1 when the hunt arms — the fleet that reads a missing UUID always exists. Clear the box to use the hunt setting (${fallback}).`
          : `${draft} worker${draft === '1' ? '' : 's'} reading the job's shift list while the shift has no UUID, standing down once it is found. Each holds its own ISP address. Takes effect on the next Start.`
      }
    />
  )
})

/**
 * The GA column — workers reading the job's schedule list for a shift's UUID.
 *
 * On the Current flow these arm beside the watch only while the assigned shift
 * has no UUID, to read it off the listing, and stand down once it lands. They
 * used to run at a different TIME from the two beside them — before an
 * application existed, waiting for the job to advertise anything at all so one
 * could be created. The create now goes out at Start and is retried if
 * refused, with no worker armed until it takes.
 *
 * ── The only worker column with no "off" ────────────────────────────
 * The two beside it accept `0` at the route, and each zero says something the
 * engine then answers — a watch of 0 refuses to arm, a shift list of 0 is
 * raised to 1. A zero here would be a run that never learns its shift's UUID,
 * with every counter on the panel reading healthy. So the hints below never
 * offer it and the route refuses it.
 */
const ScheduleWorkersCell = React.memo(function ScheduleWorkersCell(p: {
  row: CustomerRow
  actions: RowActions
  /** The fleet-wide `schedulesWorkersPerCustomer`, shown as the placeholder. */
  fallback: number
}) {
  const { row, actions, fallback } = p
  const onSave = React.useCallback(
    (next: number | null) => {
      actions.setScheduleWorkers(row, next)
    },
    [actions, row]
  )
  return (
    <WorkerCountCell
      value={row.scheduleWorkers}
      onSave={onSave}
      fallback={fallback}
      label={`GA workers for ${row.customerId}`}
      inheritHint={`Using the hunt setting (${fallback} worker${fallback === 1 ? '' : 's'}). Type a number to give this customer its own. This fleet cannot be turned off — it is what reads a shift's UUID off the listing.`}
      overrideHint={(draft) =>
        draft === '0'
          ? `0 is refused on this column: a shift with no UUID needs at least one reader. Clear the box to use the hunt setting (${fallback}).`
          : `${draft} worker${draft === '1' ? '' : 's'} reading the job's schedule list while this customer's shift has no UUID. Each holds an ISP address and a ring of borrowed accounts while it runs. Takes effect on the next Start.`
      }
    />
  )
})

/**
 * The Flow column — which claim sequence this customer runs.
 *
 * A `<select>` rather than the free-text cells beside it, because the vocabulary
 * is closed and the third option is the meaningful one: blank means "inherit the
 * hunt setting", which is what nearly every row is and what an operator needs to
 * be able to go back to.
 *
 * Saves on change rather than on blur, unlike `WorkerCountCell`. There is no
 * half-typed state to protect — every change is a complete, valid value — so
 * waiting for a blur would only delay the save and leave the row looking edited
 * when it was not.
 */
const FlowCell = React.memo(function FlowCell(p: {
  row: CustomerRow
  actions: RowActions
  /** The fleet-wide `huntFlow`, shown as the placeholder. */
  fallback: string
}) {
  const { row, actions, fallback } = p
  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const v = e.target.value
      actions.setFlow(row, v === '' ? null : (v as 'current' | 'brazil'))
    },
    [actions, row]
  )
  return (
    <select
      value={row.huntFlow ?? ''}
      onChange={onChange}
      aria-label={`Claim flow for ${row.customerId}`}
      title={
        row.huntFlow === null
          ? `Using the hunt setting (${fallback}). Takes effect on the next Start.`
          : row.huntFlow === 'brazil'
            ? 'Brazil — waits out the early-warning window, then bursts. Files no application at Start. Takes effect on the next Start.'
            : 'Current — one confirm the instant a shift is seen open. Takes effect on the next Start.'
      }
      className={cn(
        'w-full rounded-xs border border-transparent bg-transparent px-1 py-0.5 text-[11.5px]',
        'hover:border-line focus:border-accent focus:outline-none',
        row.huntFlow === null ? 'text-faint' : 'text-fg'
      )}
    >
      <option value="">{fallback}</option>
      <option value="current">Current</option>
      <option value="brazil">Brazil</option>
    </select>
  )
})

/**
 * "Won 4m ago" — the line the Confirmed list exists to show.
 *
 * A claim is the one thing that happens to a customer with nobody watching: the
 * run stands down, `status` flips to `confirmed`, and the row leaves the
 * Sessions list in the same write. Ordering Confirmed by `won_at` puts that
 * customer on top, but an order is not evidence — without a timestamp on the row
 * the first line looks exactly like the fortieth, and "did this just win, or is
 * the sort broken?" is unanswerable from the screen.
 *
 * Takes the second line of the status cell rather than a column of its own.
 * Every tier of this grid is budgeted to the pixel (see the `ColumnId` note on
 * why the rehire verdict has no column either), and the line it replaces reads
 * "not running here" — true of every confirmed customer, and the least
 * informative thing the cell could be saying about one that just claimed a shift.
 *
 * Rendered CONDITIONALLY by its callers, which is why the hook lives in here
 * rather than in the cell: `useRelativeTime` subscribes to the app's shared
 * clock, and a list where no row has won should not put 300 subscribers on it to
 * render nothing.
 */
const WonLine = React.memo(function WonLine(p: { iso: string }) {
  const ago = useRelativeTime(p.iso)
  const exact = React.useMemo(() => {
    const at = new Date(p.iso)
    // The browser's own locale and zone. An operator reads this to line the
    // claim up against a shift window they are looking at in local time, and a
    // bare ISO string in UTC is the one format that cannot be compared by eye.
    return Number.isNaN(at.getTime()) ? p.iso : at.toLocaleString()
  }, [p.iso])

  return (
    <span
      className="truncate text-[11.5px] text-ok"
      title={`Shift claimed ${exact}. This is what moved the customer to Confirmed.`}
    >
      Won {ago}
    </span>
  )
})

const SessionCell = React.memo(
  function SessionCell(p: {
    row: CustomerRow
    run: RunStateDto | null
    actions: RowActions
    /**
     * Whether the number tag still lives in this cell. False on the Approved
     * list, where `computer_number` has a column of its own — one editable
     * field per value, or two boxes race each other's debounced writes.
     */
    showNumber: boolean
  }) {
    const row = p.row
    // Subscribes to the app-wide ticker (one timer for the whole app — never a
    // timer per row) and re-renders only when the rendered string changes.
    // That tick is also what re-evaluates live -> stale below.
    const liveState = rowLiveState(p.run)
    const meta = LIVE_STATE_META[liveState]
    const isStale = liveState === 'stale'

    /*
     * THIS system's run wins the pill when there is one.
     *
     * `liveState` describes the legacy `customer_sessions` row, which a run
     * started here never creates — so a customer this process is actively
     * signing in showed as "Never started" while the log filled up behind it.
     * The run state is both more specific and more current (it arrives over the
     * stream in ~80ms), so it takes the pill and the line beneath it, and the
     * legacy state is left to describe the customers the other system owns.
     */
    const run = p.run
    const showRun = runOwnsRow(run)

    /*
     * Amazon's refusal outranks everything else on this cell.
     *
     * A blocked customer's run ends `failed` seconds after it starts, so without
     * this the row would read "Failed" — true, useless, and identical to the
     * dozen other ways a run can end. It would also say it for only ten minutes,
     * after which the row falls back to "Not started" and the single most
     * important fact about the customer is nowhere on the screen.
     *
     * The verdict is durable, it is the REASON for the failure, and it is the
     * one state on this row that no amount of pressing Start will change. So it
     * takes the pill outright rather than sharing the line: the run pill adds
     * nothing next to it, and 184px does not fit both.
     */
    const rehire = row.rehireEligibility
    const blocked = rehire !== null && rehire.blocked

    if (blocked) {
      return (
        <span className="flex w-full min-w-0 flex-col justify-center gap-[3px] py-1.5">
          <span className="flex min-w-0 items-center gap-1.5">
            <span
              title={`Amazon reports this customer as "${rehire.status}". Their session stops at sign-in until this changes. Checked ${rehire.checkedAt.slice(0, 16).replace('T', ' ')}.`}
              className="inline-flex min-w-0 shrink cursor-default"
            >
              {/*
                The raw status, truncated by `Badge`'s own `truncate` rather than
                shortened here. "Ineligible for Rehire - Within Waiting Period"
                does not fit, and every abbreviation of it that does is this
                system putting words in Amazon's mouth. The full string is on the
                title, and in the run drawer in full.
              */}
              <Badge tone="danger" dot>
                {rehire.status === '' ? 'Blocked' : rehire.status}
              </Badge>
            </span>
            {p.showNumber ? <NumberTag row={row} actions={p.actions} /> : null}
          </span>

          <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="truncate text-[11.5px] text-warn">
              {rehireSubtitle(rehire)}
            </span>
          </span>

          <span
            className="block h-[2px] w-full overflow-hidden rounded-full bg-inset"
            aria-hidden="true"
          >
            <span className="block h-full w-full rounded-full bg-danger" />
          </span>
        </span>
      )
    }

    return (
      <span className="flex w-full min-w-0 flex-col justify-center gap-[3px] py-1.5">
        {/*
          Pill, then the stale warning when there is one, then the tag — which
          keeps its 46px whatever else is on the line.

          The pill may now SHRINK (its label already truncates) rather than push
          the tag out of the cell, which it could not before. The tag stopped
          being text the moment it became editable: text that clips is merely
          hard to read, but a control that clips cannot be clicked, and the
          stale row is exactly where an operator most wants to leave a note.
        */}
        <span className="flex min-w-0 items-center gap-1.5">
          {showRun ? (
            <RunPhasePill phase={run.phase} className="inline-flex min-w-0 shrink" />
          ) : (
            <span title={meta.help} className="inline-flex min-w-0 shrink cursor-default">
              <Badge
                tone={meta.tone}
                dot={!isStale && liveState !== 'live'}
                pulse={liveState === 'live'}
              >
                {meta.label}
              </Badge>
            </span>
          )}

          {p.showNumber ? <NumberTag row={row} actions={p.actions} /> : null}
        </span>

        <span className="flex min-w-0 items-baseline gap-1.5">
          {/*
            A live run is still what this line is for — a customer can win, be
            unconfirmed, and be started again, and what they are doing NOW
            outranks a shift they took last week. Only an idle row gives the line
            up to `WonLine`, and only when there is a claim to report.
          */}
          {!showRun && row.wonAt !== null ? (
            <WonLine iso={row.wonAt} />
          ) : (
            <span
              className={cn('truncate text-[11.5px]', isStale && !showRun ? 'text-warn' : 'text-muted')}
            >
              {showRun
                ? runLine(run)
                : 'not running here'}
            </span>
          )}
          <span className="tabular flex-none font-mono text-[10.5px] text-faint">
            {showRun ? runCounterHint(run) : ''}
          </span>
        </span>

        {/* 2px pipeline progress. No transition — the design language keeps the
            pulse and the row flash as the only motion in the product. */}
        <span
          className="block h-[2px] w-full overflow-hidden rounded-full bg-inset"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round((showRun ? runProgress(run) : 0) * 100)}
          aria-label={`Pipeline progress for ${row.customerId}`}
        >
          <span
            className={cn(
              'block h-full rounded-full',
              showRun ? RUN_PHASE_BAR[run.phase] : TONE_BAR[meta.tone]
            )}
            style={{
              width: `${Math.max(2, Math.round((showRun ? runProgress(run) : 0) * 100))}%`,
            }}
          />
        </span>
      </span>
    )
  },
  // `actions` is one memoised object for the whole table, so identity is the
  // whole check — it changes only when a handler genuinely changes.
  (a, b) =>
    a.run === b.run &&
    a.actions === b.actions &&
    a.showNumber === b.showNumber &&
    sessionFieldsEqual(a.row, b.row)
)

/* ------------------------------------------------------------------ *
 * Live-run presentation
 * ------------------------------------------------------------------ */

/**
 * How far through a run each phase is.
 *
 * Hand-assigned rather than derived from a step index, because the phases are
 * not evenly spaced in time: the OTP wait alone is 10–60s and dominates every
 * run, so a linear bar would sit still through the longest part and then leap.
 * These weights track roughly how much of a typical run is behind you.
 */
const RUN_PROGRESS: Readonly<Record<RunPhase, number>> = Object.freeze({
  idle: 0,
  starting: 0.12,
  signing_in: 0.35,
  awaiting_otp: 0.6,
  applying: 0.85,
  holding: 1,
  failed: 1,
  stopped: 1,
})

const RUN_PHASE_BAR: Readonly<Record<RunPhase, string>> = Object.freeze({
  idle: 'bg-neutral',
  starting: 'bg-info',
  signing_in: 'bg-live',
  awaiting_otp: 'bg-warn',
  applying: 'bg-info',
  holding: 'bg-ok',
  failed: 'bg-danger',
  stopped: 'bg-neutral',
})

function runProgress(run: RunStateDto): number {
  return RUN_PROGRESS[run.phase]
}

/**
 * The line under the pill: what the run last said, else which step it is on.
 *
 * With neither, "working…" is only true of a run that is still going. A run that
 * FAILED or was STOPPED before it wrote a single line used to read "FAILED /
 * working…" — which looked like a run that was both over and busy, and hid that
 * nothing had been recorded about why.
 */
function runLine(run: RunStateDto): string {
  if (run.detail) return run.detail
  if (run.stepKey) return run.stepKey.replace(/_/g, ' ')
  if (run.phase === 'failed') return 'no reason recorded'
  if (run.phase === 'stopped') return 'stopped'
  return 'working…'
}

/**
 * The one number worth the ~4 characters the row can spare.
 *
 * Errors if there are any, because that is what changes what the operator does
 * next; otherwise the beat count while holding, which is the proof the seat is
 * still being re-claimed.
 */
function runCounterHint(run: RunStateDto): string {
  const m = run.metrics
  if (m.errors > 0) return `${m.errors}✕`
  if (run.phase === 'holding' && m.beats > 0) return `${m.beats}♥`
  if (m.calls > 0) return `${m.calls}⇄`
  return ''
}

/** Compact status for the phone card: one line, badge + what it is doing. */
const CardStatus = React.memo(
  function CardStatus(p: { row: CustomerRow; run: RunStateDto | null; actions: RowActions }) {
    const row = p.row
    const liveState = rowLiveState(p.run)
    const meta = LIVE_STATE_META[liveState]
    const isStale = liveState === 'stale'

    const run = p.run
    const showRun = runOwnsRow(run)

    /*
     * Same precedence as `SessionCell`: Amazon's refusal outranks the run.
     *
     * The card is one line on a phone, so there is room for one badge and one
     * short string. When a customer cannot be hired, those are the two things
     * worth spending them on.
     */
    const rehire = row.rehireEligibility
    if (rehire !== null && rehire.blocked) {
      return (
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span
            title={`Amazon reports this customer as "${rehire.status}". Their session stops at sign-in until this changes.`}
            className="inline-flex min-w-0 shrink cursor-default"
          >
            <Badge tone="danger" dot>
              {rehire.status === '' ? 'Blocked' : rehire.status}
            </Badge>
          </span>
          <span className="truncate text-[11.5px] text-warn">{rehireSubtitle(rehire)}</span>
          <NumberTag row={row} actions={p.actions} />
        </span>
      )
    }

    return (
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        {showRun ? (
          <RunPhasePill phase={run.phase} />
        ) : (
          <Badge
            tone={meta.tone}
            dot={!isStale && liveState !== 'live'}
            pulse={liveState === 'live'}
          >
            {meta.label}
          </Badge>
        )}
        {/* Same precedence as the table cell: a live run first, then the claim,
            then the "not running here" that is true of every idle row. */}
        {!showRun && row.wonAt !== null ? (
          <WonLine iso={row.wonAt} />
        ) : (
          <span
            className={cn('truncate text-[11.5px]', isStale && !showRun ? 'text-warn' : 'text-muted')}
          >
            {showRun
              ? runLine(run)
              : 'not running here'}
          </span>
        )}
        {/* The card never showed this value at all, which made the tag an
            editable field on one breakpoint and absent on another. It is 46px. */}
        <NumberTag row={row} actions={p.actions} />
      </span>
    )
  },
  (a, b) => a.run === b.run && a.actions === b.actions && sessionFieldsEqual(a.row, b.row)
)

/** Every field the two status cells paint, and nothing else. */
function sessionFieldsEqual(x: CustomerRow, y: CustomerRow): boolean {
  return (
    x === y ||
    (      x.liveState === y.liveState &&
      x.computerNumber === y.computerNumber &&
      x.customerId === y.customerId &&
      // The cell paints this, so the cell has to compare it. A claim arrives as
      // a change-stream frame on a row already on screen; without this line the
      // memo would hold the old render and "Won just now" would appear only
      // whenever something else about the row happened to change.
      x.wonAt === y.wonAt &&
      rehireEqual(x.rehireEligibility, y.rehireEligibility))
  )
}

/**
 * Compared FIELD BY FIELD, never by identity.
 *
 * `customer.patch` rebuilds the row — and every nested object on it — on every
 * change-stream frame, so an identity check here would be false on frames that
 * changed nothing about this cell and re-render every visible row. And the gate
 * writes this field on a document the stream is already watching, so those
 * frames are exactly the ones that arrive in a burst when a fleet starts.
 *
 * `checkedAt` is deliberately NOT compared: it moves on every re-check and is
 * only ever shown inside a `title`, so comparing it would repaint the cell once
 * per customer per Start for a string nobody is looking at. `status`, `blocked`
 * and `futureEligibleAt` are what the cell paints, and all three are here.
 */
function rehireEqual(
  a: CustomerRow['rehireEligibility'],
  b: CustomerRow['rehireEligibility']
): boolean {
  if (a === b) return true
  if (a === null || b === null) return false
  return (
    a.blocked === b.blocked &&
    a.status === b.status &&
    a.futureEligibleAt === b.futureEligibleAt
  )
}

/* ------------------------------------------------------------------ *
 * Overflow menu
 *
 * Mounted only for the row whose menu has actually been opened, which is why
 * it talks to Radix directly instead of going through `components/ui/dropdown`:
 * that primitive is uncontrolled, and a menu that appears *because* it was
 * clicked has to be born open — the click that mounted it is already over.
 * Styling is kept identical to the shared primitive on purpose.
 * ------------------------------------------------------------------ */

const MENU_CONTENT = cn(
  'z-[60] max-h-[min(70dvh,26rem)] min-w-[200px] overflow-y-auto overscroll-contain',
  'rounded-lg border border-line bg-s2 p-1 shadow-e2',
  'origin-(--radix-dropdown-menu-content-transform-origin)',
  'transition-[opacity,transform] duration-100 ease-out',
  'starting:scale-[0.97] starting:opacity-0'
)

const MENU_ITEM = cn(
  'relative flex h-8 cursor-default items-center gap-2 rounded-sm px-2 text-[13.5px]',
  'text-fg outline-none transition-colors duration-75 select-none',
  'data-highlighted:bg-s3 data-highlighted:text-fg',
  'data-disabled:pointer-events-none data-disabled:opacity-45',
  '[@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:text-[15px]'
)

const MENU_ITEM_DANGER = 'text-danger data-highlighted:bg-danger-soft data-highlighted:text-danger'
/**
 * Amber, for an item that is a step BACKWARDS rather than a destructive one.
 *
 * Moving a customer back to Pending undoes work without losing anything — the
 * job ids, sessions and logs all survive — so it does not belong in the same
 * red as "Force restart" or "Delete". Amber says "this reverses something"
 * where red says "this cannot be taken back".
 */
const MENU_ITEM_WARN = 'text-warn data-highlighted:bg-warn-soft data-highlighted:text-warn'

interface RowMenuItem {
  key: string
  label: string
  onSelect: () => void
  danger?: boolean
  /** Amber — reverses something without destroying it. See `MENU_ITEM_WARN`. */
  warn?: boolean
  disabled?: boolean
  separatorBefore?: boolean
}

function RowMenu(p: { row: CustomerRow; pending: boolean; actions: RowActions; open: boolean }) {
  const { row, actions, pending } = p

  /*
   * A pending customer has no session, so every session item here is a dead
   * entry for them — and "Force restart session" is worse than dead: this list
   * is explicitly not a place to start anything from. The menu is built from
   * what the row can actually do.
   */
  const isPending = row.status === 'pending'

  const items = React.useMemo<RowMenuItem[]>(() => {
    const out: RowMenuItem[] = [
      { key: 'detail', label: 'View profile', onSelect: () => actions.openDetail(row.id) },
    ]

    if (isPending) {
      out.push({ key: 'edit', label: 'Edit details', onSelect: () => actions.edit(row) })
    } else {
      out.push({
        key: 'logs',
        label: 'View run log',
        onSelect: () => actions.viewLogs(row),
      })
    }

    out.push(
      {
        key: 'copy-id',
        label: 'Copy customer ID',
        separatorBefore: true,
        onSelect: () => actions.copy(row.customerId),
      },
      { key: 'copy-email', label: 'Copy email', onSelect: () => actions.copy(row.email) }
    )

    if (!isPending) {
      /*
       * The job and schedule copies stay for the lists where they are the
       * point — following a customer into the schedule screen. A pending
       * customer has not been through any of that yet, and two entries that
       * are disabled on most rows make the menu longer without making it more
       * useful.
       */
      out.push(
        {
          key: 'copy-job',
          label: 'Copy job id',
          disabled: row.jobIdFull === null,
          onSelect: () => actions.copy(row.jobIdFull ?? ''),
        },
        {
          key: 'copy-schedule',
          label: 'Copy schedule id',
          disabled: row.scheduleIdFull === null,
          onSelect: () => actions.copy(row.scheduleIdFull ?? ''),
        },
        /*
         * Never disabled, unlike the copies above it: the case where a customer
         * has no job and no shift is exactly the case an operator opens this to
         * fix. The label says which of the two it will be.
         */
        {
          key: 'edit-target',
          label: row.jobId === null ? 'Assign job and shift…' : 'Change job and shift…',
          separatorBefore: true,
          disabled: pending,
          onSelect: () => actions.editTarget(row),
        }
      )
    }

    if (isPending) {
      out.push({
        key: 'delete',
        label: 'Delete customer',
        separatorBefore: true,
        danger: true,
        disabled: pending,
        onSelect: () => actions.askDelete(row),
      })
      return out
    }

    /*
     * Approved only, and above Force restart.
     *
     * `confirmed` is deliberately not offered it: approve/unapprove is one
     * reversible step over a customer nobody has acted on, whereas a confirmed
     * customer has had a shift claimed for them — walking that back two states
     * in one menu click would leave a job and schedule id on a row the console
     * then says has not been looked at.
     */
    if (row.status === 'approved') {
      out.push({
        key: 'unapprove',
        label: 'Move back to Pending',
        separatorBefore: true,
        warn: true,
        disabled: pending,
        onSelect: () => actions.unapprove(row),
      })
    }

    /*
     * The unconditional exit.
     *
     * Every other control on this row is gated on a state, and that is how an
     * operator ended up with none of them: a run whose entry read `idle` over a
     * live hunt offered Start (refused by the server) and hid Stop, with five
     * workers polling behind it and nothing on screen able to reach them.
     *
     * `live` now fixes that case properly, so this is not the remedy — it is the
     * backstop for the next disagreement nobody has found yet. Stop is
     * idempotent on the server and tears down the hunt, the heartbeat, the
     * wizard and the egress reservation whether or not the registry thinks any
     * of them exist, so offering it unconditionally can only ever be safe.
     */
    out.push({
      key: 'force-stop',
      label: 'Force stop everything',
      separatorBefore: row.status !== 'approved',
      danger: true,
      disabled: pending,
      onSelect: () => actions.stop(row),
    })

    out.push({
      key: 'restart',
      label: 'Force restart session',
      danger: true,
      disabled: pending,
      onSelect: () => actions.start(row),
    })

    return out
  }, [actions, row, pending, isPending])

  const onOpenChange = React.useCallback(
    (next: boolean) => {
      actions.setMenuOpen(row.id, next)
    },
    [actions, row.id]
  )

  return (
    // `modal={false}`: a modal menu locks body scroll and blanks pointer
    // events, which on a virtualised list means the rows behind it freeze.
    <DropdownMenu.Root open={p.open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          title="More actions"
          aria-label={`More actions for ${row.customerId}`}
        >
          <MoreHorizontal aria-hidden="true" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          collisionPadding={8}
          aria-label={`Actions for ${row.customerId}`}
          className={MENU_CONTENT}
        >
          {items.map((item) => (
            <React.Fragment key={item.key}>
              {item.separatorBefore ? (
                <DropdownMenu.Separator className="my-1 h-px bg-line" />
              ) : null}
              <DropdownMenu.Item
                disabled={item.disabled}
                onSelect={item.onSelect}
                className={cn(
                  MENU_ITEM,
                  item.danger && MENU_ITEM_DANGER,
                  item.warn && MENU_ITEM_WARN
                )}
              >
                <span className="flex-1 truncate">{item.label}</span>
              </DropdownMenu.Item>
            </React.Fragment>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

/* ------------------------------------------------------------------ *
 * Actions
 * ------------------------------------------------------------------ */

interface ActionsProps {
  row: CustomerRow
  pending: boolean
  actions: RowActions
  menu: RowMenuState
  /** Render "Start"/"Stop" beside the icon. Only where the column affords it. */
  labels: boolean
  /** This system's live run for this customer. See the note in ActionsCell. */
  run: RunStateDto | null
}

/**
 * What a PENDING customer's row offers.
 *
 * Nothing is ever started from this list. A pending customer has not been
 * approved, so a session for them is not a thing anyone should be able to
 * begin by mis-clicking a row — and the three buttons that replace Start are
 * the three decisions actually waiting on the operator: correct it, approve
 * it, or throw it away.
 *
 * Approve is the primary and the only filled button; Delete carries its colour
 * in the text over neutral chrome, like every other destructive control here,
 * and routes through a confirmation rather than firing on the click.
 */
const PendingActions = React.memo(
  function PendingActions(p: { row: CustomerRow; pending: boolean; actions: RowActions; labels: boolean }) {
    const { row, pending, actions } = p

    /*
     * Labels only where the column can pay for them.
     *
     * Three labelled buttons are 313px and three icons are 164px, and only the
     * `xl` track has room for the first — so below it they collapse to icons
     * with the label moving into `title` and `aria-label`. `iconOnly` rather
     * than a min-width: it strips the horizontal padding as well, which is the
     * difference between 32px and 35px per button and therefore the difference
     * between fitting at `lg` and not.
     */
    const icons = !p.labels

    return (
      <>
        <Button
          variant="secondary"
          size="sm"
          iconOnly={icons}
          disabled={pending}
          title="Edit this customer's details"
          aria-label={`Edit ${row.customerId}`}
          onClick={() => actions.edit(row)}
        >
          <Pencil aria-hidden="true" />
          {icons ? null : 'Edit'}
        </Button>

        <Button
          variant="primary"
          size="sm"
          iconOnly={icons}
          loading={pending}
          disabled={pending}
          title="Approve — moves this customer to the Approved list"
          aria-label={`Approve ${row.customerId}`}
          onClick={() => actions.approve(row)}
        >
          {pending ? null : <CheckCircle2 aria-hidden="true" />}
          {icons ? null : 'Approve'}
        </Button>

        {/* Delete lives in the overflow menu, not here. An irreversible action
            sitting between two routine ones, at the end of every row, is a
            mis-click waiting to happen — and the menu is where this table
            already keeps its destructive entry. */}
      </>
    )
  },
  (a, b) =>
    a.pending === b.pending &&
    a.actions === b.actions &&
    a.labels === b.labels &&
    a.row.id === b.row.id &&
    a.row.customerId === b.row.customerId
)

const ActionsCell = React.memo(
  function ActionsCell(p: ActionsProps) {
    const { row, pending, actions, run } = p
    const liveState = rowLiveState(run)
    const isPending = row.status === 'pending'

    /*
     * Two independent sources say "this customer is running", and BOTH have to
     * be honoured.
     *
     * `liveState` folds them together — a run this process owns overrides the
     * legacy `customer_sessions` view, which a run started here never creates.
     * `runningHere` is kept as a separate test because the two disagree in one
     * direction that matters: a run that FINISHED still owns the row for ten
     * minutes, and its `stopped`/`failed` state must not read as "still going".
     * Without it the button offered Start to someone already being held, and a
     * second click would send them a second OTP and burn a second attempt.
     */
    /*
     * `live` is OR'd in, and it is what makes Stop reachable at all.
     *
     * The phase is a record of what was last reported; `live` is whether the API
     * still has a hunt, a heartbeat or the wizard running. They disagree more
     * often than they look like they would — a sign-in that fails stamps
     * `failed` over a hunt that is still polling, and a terminal entry swept
     * after ten minutes is rebuilt as `idle` by the next log line.
     *
     * Neither `failed` nor `idle` is in `RUN_ACTIVE` or `CAN_STOP`, so on the
     * run this was written for the console showed Start (which the server
     * refused with "already running") and hid Stop — leaving an operator no way
     * to end five workers that had been going for two hours.
     */
    const runningHere = run !== null && (run.live || RUN_ACTIVE.has(run.phase))
    const canStop = runningHere || CAN_STOP.has(liveState)
    const canStart = !runningHere && CAN_START.has(liveState)
    // Always openable. It shows THIS system's run log, and a customer we have
    // never run has an empty one — which is the true answer, not a dead button.
    const hasSession = true

    /**
     * One primary action, not two.
     *
     * Start and Stop are mutually exclusive by definition — a session is either
     * runnable or running — so rendering both, with one always greyed out, spent
     * two slots and two colours per row to say one thing. Fifty rows of that is
     * a wall of red and green that stops meaning anything.
     *
     * So the primary slot *swaps*: Start when the session can be started, Stop
     * when it is running, and a disabled Start (with the reason in the hover
     * hint) only when neither applies.
     */
    const primary = canStop
      ? {
          label: 'Stop',
          icon: <Square aria-hidden="true" />,
          variant: 'danger' as const,
          disabled: pending,
          onClick: () => actions.stop(row),
          aria: `Stop automation for ${row.customerId}`,
          tip: 'Stop this session',
        }
      : {
          label: 'Start',
          icon: <Play aria-hidden="true" />,
          variant: (canStart ? 'primary' : 'secondary') as 'primary' | 'secondary',
          disabled: !canStart || pending,
          onClick: () => actions.start(row),
          aria: `Start automation for ${row.customerId}`,
          tip: canStart
            ? 'Start automation'
            : `Cannot start while ${LIVE_STATE_META[liveState].label.toLowerCase()}`,
        }

    return (
      // `flex-none`, not `w-full`: inside the phone card this sits next to a
      // `flex-1` status block, and a 100%-wide flex item would starve it.
      <span className="flex flex-none items-center justify-end gap-1.5">
        {isPending ? (
          <PendingActions row={row} pending={pending} actions={actions} labels={p.labels} />
        ) : (
          <>
            {/* `title` rather than a Tooltip — and it survives on the disabled
                button, where a Radix tooltip would have needed a wrapper span
                because a disabled button emits no pointer events. */}
            <Button
              variant={primary.variant}
              size="sm"
              loading={pending}
              disabled={primary.disabled}
              title={primary.tip}
              aria-label={primary.aria}
              onClick={primary.onClick}
              className={p.labels ? 'min-w-[74px]' : 'min-w-8'}
            >
              {pending ? null : primary.icon}
              {p.labels ? primary.label : null}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              iconOnly
              disabled={!hasSession}
              title={hasSession ? 'View session log' : 'No session yet'}
              aria-label={`View session log for ${row.customerId}`}
              onClick={() => actions.viewLogs(row)}
            >
              <ScrollText aria-hidden="true" />
            </Button>
          </>
        )}

        {p.menu === 'none' ? (
          // An unopened menu costs exactly one button element.
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            title="More actions"
            aria-haspopup="menu"
            aria-label={`More actions for ${row.customerId}`}
            onClick={() => actions.openMenu(row.id)}
          >
            <MoreHorizontal aria-hidden="true" />
          </Button>
        ) : (
          <RowMenu row={row} pending={pending} actions={actions} open={p.menu === 'open'} />
        )}
      </span>
    )
  },
  (a, b) =>
    a.pending === b.pending &&
    a.actions === b.actions &&
    a.menu === b.menu &&
    a.labels === b.labels &&
    a.run === b.run &&
    (a.row === b.row ||
      (a.row.id === b.row.id &&
        a.row.customerId === b.row.customerId &&
        a.row.email === b.row.email &&
        a.row.jobIdFull === b.row.jobIdFull &&
        a.row.scheduleIdFull === b.row.scheduleIdFull))
)

/* ================================================================== *
 * Row / card
 * ================================================================== */

interface BaseRowProps {
  row: CustomerRow
  /** Index within the loaded page — drives keyboard nav and `data-index`. */
  index: number
  /** Displayed ordinal across the whole result set (offset + index + 1). */
  ordinal: number
  /** Absolute offset from the virtualizer. Constant per index. */
  top: number
  selected: boolean
  focused: boolean
  tabbable: boolean
  pending: boolean
  menu: RowMenuState
  /** Stable for the lifetime of the table; the comparator checks identity only. */
  actions: RowActions
  /**
   * This system's live run for this customer, or null.
   *
   * Looked up by the parent and passed per row rather than subscribed to per
   * row. The map is rebuilt on every `run.state` frame, but a shallow rebuild
   * PRESERVES the identity of every entry it did not touch — so the comparator
   * below can settle "did this row's run change?" with one `!==`, and a counter
   * ticking for one customer re-renders exactly one row out of the fifty on
   * screen. Subscribing per row would instead wake all fifty on every frame.
   */
  run: RunStateDto | null
  /**
   * The active search term, lower-cased, or '' when the box is empty.
   *
   * Lower-cased ONCE by the parent rather than per cell — this is compared and
   * scanned for on four fields of every visible row, and folding case here would
   * be the only part of the highlight that could show in a profile. See `Mark`.
   */
  q: string
  /**
   * The fleet-wide `workersPerCustomer`, for the Workers column's placeholder.
   *
   * One number for the whole table, threaded down rather than read per cell: it
   * comes from a query, and subscribing fifty rows to it would mean fifty
   * components re-rendering the moment anybody saves the settings page. It only
   * moves when someone edits `/settings`, so it costs the comparator one
   * integer compare and never causes a repaint of its own.
   */
  defaultWorkers: number
  /** The fleet-wide `graphqlWorkersPerCustomer`, threaded down the same way. */
  defaultGqlWorkers: number
  defaultScheduleWorkers: number
  defaultFlow: string
}

interface RowProps extends BaseRowProps {
  /** Module-scope constant per breakpoint, so identity comparison is enough. */
  layout: TableLayout
}

/**
 * PERFORMANCE: the comparator lists exactly the fields painted below, and
 * nothing else. `createdAt`/`updatedAt` are deliberately absent — the server
 * bumps `updated_at` on every heartbeat, and comparing it would make the whole
 * page reconcile every second for a change nobody can see. Anything the row
 * *does* paint has to be here, or a realtime patch leaves a stale cell.
 */
function rowDataEqual(a: BaseRowProps, b: BaseRowProps): boolean {
  if (
    a.index !== b.index ||
    a.ordinal !== b.ordinal ||
    a.top !== b.top ||
    a.selected !== b.selected ||
    a.focused !== b.focused ||
    a.tabbable !== b.tabbable ||
    a.pending !== b.pending ||
    a.menu !== b.menu ||
    a.actions !== b.actions ||
    a.defaultWorkers !== b.defaultWorkers ||
    a.defaultGqlWorkers !== b.defaultGqlWorkers ||
    a.defaultScheduleWorkers !== b.defaultScheduleWorkers ||
    a.defaultFlow !== b.defaultFlow ||
    // Without this the highlight would be stale exactly when it matters. A new
    // search returns new row objects, but a customer present in both results has
    // identical FIELDS — so the field comparison below bails out and the row
    // keeps painting the previous term's marks.
    a.q !== b.q ||
    // Identity is the whole check: a state frame replaces exactly the entry it
    // is for, so an unchanged run is the same object it was last render.
    a.run !== b.run
  ) {
    return false
  }

  const x = a.row
  const y = b.row
  if (x === y) return true

  return (
    x.id === y.id &&
    x.customerId === y.customerId &&
    x.name === y.name &&
    x.email === y.email &&
    x.mode === y.mode &&
    x.agentUsername === y.agentUsername &&
    x.agentName === y.agentName &&
    x.jobId === y.jobId &&
    x.jobIdFull === y.jobIdFull &&
    x.scheduleId === y.scheduleId &&
    x.scheduleIdFull === y.scheduleIdFull &&
    x.computerNumber === y.computerNumber &&
    x.huntWorkers === y.huntWorkers &&
    // Shipped by the change stream for exactly this repaint — see
    // `CUSTOMER_FIELDS` in watchers.ts. Without it the Shift list cell goes on
    // painting the previous value after somebody else edits the row.
    x.huntGqlWorkers === y.huntGqlWorkers &&
    x.huntFlow === y.huntFlow &&
    // Painted by `SessionCell`, which takes the whole cell when it is set — so
    // a row that goes blocked mid-render has to reconcile. Sub-fields, not the
    // object: `customer.patch` builds a fresh one every frame.
    rehireEqual(x.rehireEligibility, y.rehireEligibility) &&
    /*
     * Painted by the Pending list only, but compared always.
     *
     * A comparator that lists fewer fields than the row renders is the exact
     * bug that made search highlights go stale: the row keeps painting the
     * previous value because nothing told it otherwise. `cities` and
     * `workHours` are arrays and are compared by content, since the API returns
     * a fresh array on every fetch and identity would report every row changed.
     */
    x.pin === y.pin &&
    x.warehouseCount === y.warehouseCount &&
    sameList(x.cities, y.cities) &&
    sameList(x.workHours, y.workHours) &&
    // Painted in the Customer cell on every list, and shipped both by the
    // change stream and by the drawer's optimistic save — so a pick made in
    // the drawer must repaint the row beside it in the same frame.
    x.shiftPriority === y.shiftPriority &&
    x.liveState === y.liveState
  )
}

function rowPropsEqual(a: RowProps, b: RowProps): boolean {
  return a.layout === b.layout && rowDataEqual(a, b)
}

/*
 * `rowDataEqual` already compares `q` for both shapes — the card uses it
 * directly and the table row through the wrapper above — so neither can paint a
 * stale highlight.
 */

/** Mount-only flash for a row that changed while it was scrolled out of view.
 *  The timestamp map is the parent's ref; this only reads it. */
function useEnterFlash(
  ref: React.RefObject<HTMLElement | null>,
  actions: RowActions,
  id: string
): void {
  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    const at = actions.flashAt(id)
    if (at !== undefined && performance.now() - at < FLASH_MS) playFlash(el)
    // Mount-only by design: while mounted, the parent drives the flash directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

/**
 * Glide the beat animation across the row when its run confirms a keep-alive.
 *
 * Free by construction: a beat replaces `runs[customerId]`, which already
 * fails `rowDataEqual`'s `a.run !== b.run` check and re-renders exactly this
 * row — the effect below is just a ref comparison riding that render. No new
 * subscription, nothing per frame, nothing at all for rows without a held run.
 *
 * `metrics.beats` rather than `updatedAt`, because it only moves on an
 * ACCEPTED beat — warn lines and hunt chatter bump `updatedAt` too, and a
 * "heartbeat" that fires on noise stops meaning the hold is alive. Primed on
 * mount (never flashing the first value it sees), so a row scrolling into
 * view does not glide just because its counter is non-zero.
 */
function useBeatFlash(ref: React.RefObject<HTMLElement | null>, run: RunStateDto | null): void {
  const prevBeats = React.useRef<number | null>(null)
  React.useEffect(() => {
    const beats = run !== null && run.phase === 'holding' ? run.metrics.beats : null
    const prev = prevBeats.current
    prevBeats.current = beats
    if (prev === null || beats === null || beats <= prev) return
    if (ref.current) playFlash(ref.current, 'flash-beat')
  }, [run, ref])
}

/**
 * Mount-time mark for a customer inside an ACTIVE window.
 *
 * The table is virtualised, so a hot customer's row may not EXIST when the
 * sighting arrives — it mounts later, when the scroll (theirs or the
 * operator's own) brings it into range, and this is what paints it then. As
 * `hold`, not `on`: the window was already open, so the row appearing is not
 * a new sighting and must not replay the entrance.
 *
 * Once per mount is enough, because the mark is a data attribute React does
 * not manage (see `markActiveHit`) — re-renders leave it exactly where it is.
 * Between mounts the parent's registered view drives it directly; on unmount
 * it leaves with the element. No subscription, nothing per frame.
 */
function useActiveHitMark(ref: React.RefObject<HTMLElement | null>, customerId: string): void {
  React.useEffect(() => {
    const el = ref.current
    if (el) markActiveHit(el, isActiveHot(customerId), { mounting: true })
  }, [ref, customerId])
}

const CustomerTableRow = React.memo(function CustomerTableRow(p: RowProps) {
  const { row, actions, layout } = p
  const ref = React.useRef<HTMLTableRowElement | null>(null)
  useEnterFlash(ref, actions, row.id)
  useBeatFlash(ref, p.run)
  useActiveHitMark(ref, row.customerId)

  const style = React.useMemo<React.CSSProperties>(
    () => ({ ...layout.rowStyle, transform: `translateY(${p.top}px)` }),
    [layout, p.top]
  )

  const onSelectChange = React.useCallback(() => {
    actions.toggleSelect(row.id)
  }, [actions, row.id])

  const onOpen = React.useCallback(() => {
    actions.openDetail(row.id)
  }, [actions, row.id])

  return (
    <tr
      ref={ref}
      role="row"
      data-index={p.index}
      data-row-id={row.id}
      data-customer-id={row.customerId}
      aria-rowindex={p.ordinal + HEADER_ROWS}
      aria-selected={p.selected}
      tabIndex={p.tabbable ? 0 : -1}
      onAnimationEnd={handleFlashEnd}
      style={style}
      className={cn(
        'group border-b border-line/70 outline-none',
        'transition-colors duration-75',
        p.selected ? 'bg-accent-soft' : 'hover:bg-s2',
        p.focused && 'ring-1 ring-accent/45 ring-inset',
        p.pending && 'opacity-80'
      )}
    >
      <td role="gridcell" className={cn(CELL, 'justify-center px-0')}>
        {/* The whole 36×56 cell is the hit area. */}
        <label className="flex size-full cursor-pointer items-center justify-center">
          <input
            type="checkbox"
            checked={p.selected}
            onChange={onSelectChange}
            aria-label={`Select ${row.name || row.customerId}`}
            className={cn(CHECKBOX, 'size-4')}
          />
        </label>
      </td>

      <td role="gridcell" className={CELL}>
        <span className="flex min-w-0 flex-col justify-center leading-[1.3]">
          <button
            type="button"
            onClick={onOpen}
            title={row.name || 'Unnamed'}
            className="truncate rounded-xs text-left text-[13px] font-medium text-fg hover:text-accent hover:underline"
          >
            {row.name ? <Mark text={row.name} q={p.q} /> : <span className="text-faint">Unnamed</span>}
          </button>
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="tabular truncate font-mono text-[11px] text-faint">
              <Mark text={row.customerId} q={p.q} />
            </span>
            <span
              title={`Mode ${row.mode}`}
              className="flex-none rounded-xs bg-inset px-1 font-mono text-[10px] text-faint"
            >
              {row.mode}
            </span>
            {/*
              The shift they want — a sun, a moon, or both — in this cell for
              the same reason the AUTO marker below is: it is the one cell on
              every list at every width, so the glyph is never a breakpoint
              away. Glyph-sized to the mode chip so the fixed row height the
              virtualiser depends on does not move; nothing at all when the
              row predates the column.
            */}
            <ShiftPriorityIcons value={row.shiftPriority} />
            {/*
              The AUTO marker, in the ONE cell that exists at every breakpoint.

              The job column only appears from 1280px up and the phone card has
              no job cell at all, so the chip in `JobCell` is invisible on most
              screens. This is the same box as the mode chip rather than a
              `<Badge>` deliberately — Badge's larger padding and type would grow
              the fixed row height the virtualiser depends on — and a native
              `title` rather than a Tooltip, for the reason stated on `JobCell`.
            */}
            {p.run?.autoTarget ? (
              <AutoChip
                title={autoTitle(p.run.autoTarget)}
                scheduleId={p.run.autoTarget.scheduleId}
                /*
                 * The seat figure rides here only while no job column is on
                 * screen to carry it — the same rule that puts the chip here
                 * at all. With a job column showing, the figure sits beside
                 * the shift it describes, and this cell — the tightest track
                 * on the wide tier, where the id is already being truncated
                 * to make room — keeps its chip plain rather than showing
                 * the same number a third time.
                 */
                seats={
                  layout.has.job || layout.has.jobId || layout.has.scheduleId
                    ? undefined
                    : p.run.autoTarget.seats
                }
                seatsAtMs={p.run.autoTarget.seatsAtMs}
              />
            ) : null}
          </span>
        </span>
      </td>

      {layout.has.agent ? (
        <td role="gridcell" className={CELL}>
          {row.agentUsername === null ? (
            <span className="text-[12px] text-faint">Unassigned</span>
          ) : (
            <span title={`@${row.agentUsername}`} className="truncate text-[12.5px] text-muted">
              <Mark text={row.agentName ?? row.agentUsername} q={p.q} />
            </span>
          )}
        </td>
      ) : null}

      {layout.has.email ? (
        <td role="gridcell" className={CELL}>
          <span className="flex min-w-0 flex-col justify-center leading-[1.3]">
            <span title={row.email} className="truncate text-[12.5px] text-muted">
              <Mark text={row.email} q={p.q} />
            </span>
            {/* Only the Pending list receives a PIN; every other query leaves
                it null, so this line simply does not exist there. */}
            {row.pin === null ? null : <PinLine pin={row.pin} />}
          </span>
        </td>
      ) : null}

      {layout.has.job ? (
        <td role="gridcell" className={CELL}>
          <JobCell
            jobId={row.jobId}
            jobIdFull={row.jobIdFull}
            scheduleId={row.scheduleId}
            scheduleIdFull={row.scheduleIdFull}
            autoJobId={p.run?.autoTarget?.jobId ?? null}
            autoScheduleId={p.run?.autoTarget?.scheduleId ?? null}
            autoSeats={p.run?.autoTarget?.seats}
            autoSeatsAtMs={p.run?.autoTarget?.seatsAtMs}
            targetUuid={row.targetUuid}
            q={p.q}
          />
        </td>
      ) : null}

      {layout.has.jobId ? (
        <td role="gridcell" className={CELL}>
          <IdOnlyCell
            prefix="J "
            value={row.jobId}
            full={row.jobIdFull}
            auto={p.run?.autoTarget?.jobId ?? null}
            autoScheduleId={p.run?.autoTarget?.scheduleId ?? null}
            autoSeats={p.run?.autoTarget?.seats}
            autoSeatsAtMs={p.run?.autoTarget?.seatsAtMs}
            q={p.q}
          />
        </td>
      ) : null}

      {layout.has.scheduleId ? (
        <td role="gridcell" className={CELL}>
          <IdOnlyCell
            prefix="S "
            value={row.scheduleId}
            full={row.scheduleIdFull}
            auto={p.run?.autoTarget?.scheduleId ?? null}
            autoScheduleId={p.run?.autoTarget?.scheduleId ?? null}
            autoSeats={p.run?.autoTarget?.seats}
            autoSeatsAtMs={p.run?.autoTarget?.seatsAtMs}
            q={p.q}
          />
        </td>
      ) : null}

      {layout.has.cities ? (
        <td role="gridcell" className={CELL}>
          <CitiesCell cities={row.cities} warehouses={row.warehouseCount} actions={actions} />
        </td>
      ) : null}

      {layout.has.workHours ? (
        <td role="gridcell" className={CELL}>
          <WorkHoursCell hours={row.workHours} />
        </td>
      ) : null}

      {layout.has.fleets ? (
        /* Three boxes in one cell, in `FLEET_COLUMNS` order, `FLEET_GAP` apart —
           the header's three letters sit over them by the same arithmetic. */
        <td role="gridcell" className={cn(CELL, COMPACT_CELL, 'gap-2')}>
          <WorkersCell row={row} actions={actions} fallback={p.defaultWorkers} />
          <GqlWorkersCell row={row} actions={actions} fallback={p.defaultGqlWorkers} />
          <ScheduleWorkersCell row={row} actions={actions} fallback={p.defaultScheduleWorkers} />
        </td>
      ) : null}

      {layout.has.flow ? (
        <td role="gridcell" className={cn(CELL, COMPACT_CELL)}>
          <FlowCell row={row} actions={actions} fallback={p.defaultFlow} />
        </td>
      ) : null}

      {layout.has.number ? (
        <td role="gridcell" className={cn(CELL, COMPACT_CELL)}>
          <NumberTag row={row} actions={actions} wide />
        </td>
      ) : null}

      {layout.has.status ? (
        <td role="gridcell" className={cn(CELL, 'items-stretch')}>
          <SessionCell row={row} run={p.run} actions={actions} showNumber={!layout.has.number} />
        </td>
      ) : null}

      <td role="gridcell" className={cn(CELL, 'justify-end pr-2', STICKY_ACTIONS)}>
        <ActionsCell
          row={row}
          pending={p.pending}
          actions={actions}
          menu={p.menu}
          run={p.run}
          labels={layout.actionLabels}
        />
      </td>
    </tr>
  )
}, rowPropsEqual)

/* ------------------------------------------------------------------ *
 * Phone card
 *
 * Same data, same selection, same virtualisation — a different shape. The
 * columns an operator needs on a phone are "who", "what state" and "the two
 * controls", and those fit vertically without a single horizontal pixel of
 * scroll.
 * ------------------------------------------------------------------ */

const CARD_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: CARD_HEIGHT,
  contain: 'layout paint style',
}

const CustomerCard = React.memo(function CustomerCard(p: BaseRowProps) {
  const { row, actions } = p
  const ref = React.useRef<HTMLDivElement | null>(null)
  useEnterFlash(ref, actions, row.id)
  useBeatFlash(ref, p.run)
  useActiveHitMark(ref, row.customerId)

  const style = React.useMemo<React.CSSProperties>(
    () => ({ ...CARD_BASE_STYLE, transform: `translateY(${p.top}px)` }),
    [p.top]
  )

  const onSelectChange = React.useCallback(() => {
    actions.toggleSelect(row.id)
  }, [actions, row.id])

  const onOpen = React.useCallback(() => {
    actions.openDetail(row.id)
  }, [actions, row.id])

  return (
    <div
      ref={ref}
      role="row"
      data-index={p.index}
      data-row-id={row.id}
      data-customer-id={row.customerId}
      // No header row in the card list, so the ordinal *is* the row index.
      aria-rowindex={p.ordinal}
      aria-selected={p.selected}
      tabIndex={p.tabbable ? 0 : -1}
      onAnimationEnd={handleFlashEnd}
      style={style}
      className={cn(
        'group overflow-hidden border-b border-line/70 outline-none',
        p.selected ? 'bg-accent-soft' : 'bg-s1',
        p.focused && 'ring-1 ring-accent/45 ring-inset',
        p.pending && 'opacity-80'
      )}
    >
      <div role="gridcell" className="flex h-full min-w-0 flex-col gap-2 px-2 py-2.5">
        <div className="flex min-w-0 items-center gap-1">
          {/* 44px touch target around a 18px box. */}
          <label className="flex size-11 flex-none cursor-pointer items-center justify-center">
            <input
              type="checkbox"
              checked={p.selected}
              onChange={onSelectChange}
              aria-label={`Select ${row.name || row.customerId}`}
              className={cn(CHECKBOX, 'size-[18px]')}
            />
          </label>

          <span className="flex min-w-0 flex-1 flex-col justify-center leading-[1.3]">
            <button
              type="button"
              onClick={onOpen}
              className="truncate rounded-xs text-left text-[14px] font-medium text-fg"
            >
              {row.name ? (
                <Mark text={row.name} q={p.q} />
              ) : (
                <span className="text-faint">Unnamed</span>
              )}
            </button>
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="tabular truncate font-mono text-[11.5px] text-faint">
                <Mark text={row.customerId} q={p.q} />
              </span>
              <span className="flex-none rounded-xs bg-inset px-1 font-mono text-[10px] text-faint">
                {row.mode}
              </span>
              {/* The same glyph the table row carries, a step larger for the
                  card's looser line. */}
              <ShiftPriorityIcons value={row.shiftPriority} size="card" />
              {/* The card has no job cell at all, so this is the only place the
                  auto-pick can be seen on a phone. */}
              {p.run?.autoTarget ? (
                <AutoChip
                  title={autoTitle(p.run.autoTarget)}
                  scheduleId={p.run.autoTarget.scheduleId}
                  seats={p.run.autoTarget.seats}
                  seatsAtMs={p.run.autoTarget.seatsAtMs}
                />
              ) : null}
            </span>
            <span className="truncate text-[12.5px] text-muted">
              <Mark text={row.email} q={p.q} />
            </span>
          </span>
        </div>

        <div className="flex min-w-0 items-center gap-2">
          <CardStatus row={row} run={p.run} actions={actions} />
          <ActionsCell
            row={row}
            pending={p.pending}
            actions={actions}
            menu={p.menu}
            run={p.run}
            labels={false}
          />
        </div>
      </div>
    </div>
  )
}, rowDataEqual)

/* ================================================================== *
 * Skeletons
 * ================================================================== */

const SKELETON_ROWS = Array.from({ length: 12 }, (_, i) => i)

const SKELETON_WIDTH: Readonly<Record<ColumnId, string>> = Object.freeze({
  select: 'size-4 rounded-xs',
  customer: 'h-3 w-28',
  agent: 'h-3 w-16',
  email: 'h-3 w-40',
  job: 'h-3 w-20',
  cities: 'h-3 w-20',
  workHours: 'h-3 w-16',
  fleets: 'h-3 w-40',
  flow: 'h-3 w-14',
  jobId: 'h-3 w-20',
  scheduleId: 'h-3 w-20',
  number: 'h-3 w-12',
  status: 'h-4 w-24 rounded-full',
  actions: 'h-5 w-24',
})

function SkeletonBody(p: { layout: TableLayout }) {
  return (
    <tbody role="rowgroup" style={{ display: 'grid' }} aria-busy="true">
      {SKELETON_ROWS.map((i) => (
        <tr
          key={i}
          role="row"
          style={{ height: HEADER_HEIGHT, ...p.layout.headerStyle, alignItems: 'center' }}
          className="border-b border-line/70"
        >
          {p.layout.columns.map((col) => (
            <td
              key={col}
              role="gridcell"
              className={cn(
                CELL,
                COLUMN_META[col].compact && COMPACT_CELL,
                col === 'select' && 'justify-center px-0',
                col === 'actions' && 'justify-end'
              )}
            >
              <Skeleton className={SKELETON_WIDTH[col]} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  )
}

function SkeletonCards() {
  return (
    <div role="rowgroup" aria-busy="true">
      {SKELETON_ROWS.map((i) => (
        <div
          key={i}
          style={{ height: CARD_HEIGHT }}
          className="flex flex-col gap-2 border-b border-line/70 px-3 py-2.5"
        >
          <Skeleton className="h-3.5 w-40" />
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-52" />
          <Skeleton className="h-4 w-28 rounded-full" />
        </div>
      ))}
    </div>
  )
}

/* ================================================================== *
 * Empty-state copy that reflects the live filter set
 * ================================================================== */

function describeFilters(search: CustomerSearch): string {
  const parts: string[] = []
  if (search.q !== '') parts.push(`matching “${search.q}”`)
  if (search.agent.length > 0) parts.push(`for ${describeAgents(search.agent)}`)
  if (search.liveState.length > 0)
    parts.push(`with state ${search.liveState.map((s) => LIVE_STATE_META[s].label).join(', ')}`)
  if (search.jobId !== '') parts.push(`on job ${search.jobId}`)
  if (search.scheduleId !== '') parts.push(`on schedule ${search.scheduleId}`)
  // The header filters, named the way their own column is. An empty table has
  // to say every reason it is empty, or the operator clears the one they can
  // see and is left staring at the same nothing.
  for (const key of COLUMN_FILTERS) {
    const value = search[key]
    if (value !== '') parts.push(`with ${COLUMN_FILTER_LABELS[key].toLowerCase()} “${value}”`)
  }
  return parts.join(' ')
}

function describeAgents(agents: readonly string[]): string {
  if (agents.length === 1) return agents[0] ?? 'one agent'
  return `${agents.length} agents`
}

/* ================================================================== *
 * The header filter row
 *
 * One input per column, each narrowing THAT column at the server. The band sits
 * under the labels, inside the sticky `<thead>`, so it survives scrolling and
 * lines up with the tracks it filters.
 *
 * Three things make it cheap, and all three are load-bearing:
 *
 *  1. **The committed value lives in the URL, the keystroke does not.** Each
 *     cell holds its own draft and pushes to the router 250ms after the last
 *     key. So typing repaints one 22px input, not a table — the body cannot
 *     re-render, because nothing it reads has changed yet.
 *  2. **Every cell is memoised on the value it shows.** A commit to `email`
 *     produces a new search object, but the other six cells receive the same
 *     strings and the same callback and bail out at the memo boundary.
 *  3. **The list never blanks.** `keepPreviousData` holds the previous rows
 *     under a slight dim while the narrowed set loads, so a filter reads as the
 *     table reacting rather than as the table reloading.
 * ================================================================== */

/** Matches the toolbar's search box. One request per burst of typing. */
const FILTER_DEBOUNCE_MS = 250

interface FilterCellProps {
  filter: ColumnFilter
  /** Column label, for the input's accessible name. */
  label: string
  placeholder: string
  /** The COMMITTED value, straight from the URL. Never the draft. */
  value: string
  /** Stable for the life of the table — see `onFilterCommit`. */
  commit: (filter: ColumnFilter, value: string) => void
}

const FilterCell = React.memo(function FilterCell(p: FilterCellProps) {
  const { filter, commit } = p
  const [draft, setDraft] = React.useState(p.value)
  /** The last value THIS input pushed, so an external change is distinguishable. */
  const pushedRef = React.useRef(p.value)
  const timerRef = React.useRef<number | null>(null)

  /*
   * Adopt the URL when it changes from anywhere other than this input — the
   * back button, "Clear all", a pasted link, a chip's ×.
   *
   * Guarded on `pushedRef` rather than on `draft`, because the two answer
   * different questions: `draft !== value` is true for the whole 250ms a person
   * is typing, and re-seeding then would delete characters out from under them.
   */
  React.useEffect(() => {
    if (p.value === pushedRef.current) return
    pushedRef.current = p.value
    setDraft(p.value)
  }, [p.value])

  React.useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    }
  }, [])

  const push = React.useCallback(
    (value: string) => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
      const next = value.trim()
      // Cheap guard against a redundant navigation: pressing Enter on an
      // already-committed term would otherwise reset the page and refetch.
      if (next === pushedRef.current) return
      pushedRef.current = next
      commit(filter, next)
    },
    [filter, commit]
  )

  const onChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setDraft(value)
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        const next = value.trim()
        if (next === pushedRef.current) return
        pushedRef.current = next
        commit(filter, next)
      }, FILTER_DEBOUNCE_MS)
    },
    [filter, commit]
  )

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        push(event.currentTarget.value)
      } else if (event.key === 'Escape') {
        event.preventDefault()
        setDraft('')
        push('')
        event.currentTarget.blur()
      }
    },
    [push]
  )

  const onClear = React.useCallback(() => {
    setDraft('')
    push('')
  }, [push])

  const active = draft !== ''

  return (
    <span className="relative flex w-full min-w-0 items-center">
      <input
        type="text"
        value={draft}
        onChange={onChange}
        onKeyDown={onKeyDown}
        spellCheck={false}
        autoComplete="off"
        placeholder={p.placeholder}
        aria-label={`Filter by ${p.label}`}
        title={`Filter by ${p.label}`}
        className={cn(
          FILTER_INPUT_CLASS,
          'w-full min-w-0 rounded-sm border bg-transparent py-0 pl-1.5',
          // The row above is uppercase with letter-spacing; a filter value is
          // literal text and has to be shown exactly as it will be matched.
          'text-[11.5px] font-normal tracking-normal text-fg normal-case',
          'outline-none transition-colors duration-100',
          'placeholder:text-faint/70',
          active ? 'border-accent/45 bg-accent-soft pr-5 text-accent' : 'border-transparent pr-1.5',
          'hover:border-line-strong',
          'focus:border-accent focus:bg-s1 focus:text-fg focus:ring-2 focus:ring-accent/20'
        )}
      />
      {active ? (
        <button
          type="button"
          onClick={onClear}
          aria-label={`Clear the ${p.label} filter`}
          className={cn(
            'absolute right-0.5 flex size-4 flex-none items-center justify-center rounded-xs',
            'text-faint transition-colors duration-100 hover:bg-s3 hover:text-fg',
            // The hit area grows to the 44px touch floor, the 16px box does not
            // — same pattern as TOUCH_TARGET in ui/button.tsx.
            "[@media(pointer:coarse)]:before:absolute [@media(pointer:coarse)]:before:top-1/2",
            "[@media(pointer:coarse)]:before:left-1/2 [@media(pointer:coarse)]:before:-translate-x-1/2",
            "[@media(pointer:coarse)]:before:-translate-y-1/2",
            "[@media(pointer:coarse)]:before:h-[max(100%,44px)] [@media(pointer:coarse)]:before:w-[max(100%,44px)]",
            "[@media(pointer:coarse)]:before:content-['']"
          )}
        >
          <X aria-hidden="true" className="size-3" />
        </button>
      ) : null}
    </span>
  )
})

/* ================================================================== *
 * Table
 * ================================================================== */

const EMPTY_ROWS: CustomerRow[] = []
const EMPTY_SET: ReadonlySet<string> = new Set<string>()
const CLOSED_MENU: MenuState = { id: null, open: false }

interface MenuState {
  id: string | null
  open: boolean
}

/**
 * Moving a selection along the chain Pending <-> Approved <-> Confirmed.
 *
 * Each is a named sub-resource on the server with one rule and one audit line,
 * which is why this is four verbs rather than a status to assign: `confirm`
 * only accepts `approved`, `unapprove` only accepts `approved`, and so on. No
 * move skips a step in either direction.
 */
type MoveAction = 'approve' | 'unapprove' | 'confirm' | 'unconfirm'

/** Everything the bulk bar can do to a selection. */
type BulkAction = 'start' | 'stop' | MoveAction

/** Where each move lands, and whether it is a step forward or back. */
const MOVE_META: Record<MoveAction, { destination: string; tone: 'ok' | 'warn' }> = {
  approve: { destination: 'Approved', tone: 'ok' },
  unapprove: { destination: 'Pending', tone: 'warn' },
  confirm: { destination: 'Confirmed', tone: 'ok' },
  unconfirm: { destination: 'Approved', tone: 'warn' },
}

/**
 * Which moves each list offers, in the order they appear on the bar.
 *
 * Backwards first, forwards second, so the button nearest the run controls is
 * always the conservative one. Every list has a way out in both directions it
 * has a neighbour in — `confirmed` was a one-way door until `/unconfirm`
 * existed, which is the reason it had no move at all before.
 */
const MOVES_BY_STATUS: Record<
  CustomerStatus,
  readonly {
    action: MoveAction
    label: string
    Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  }[]
> = {
  pending: [{ action: 'approve', label: 'Move to Approved', Icon: CheckCircle2 }],
  approved: [
    { action: 'unapprove', label: 'Move to Pending', Icon: Undo2 },
    { action: 'confirm', label: 'Move to Confirmed', Icon: BadgeCheck },
  ],
  confirmed: [{ action: 'unconfirm', label: 'Move to Approved', Icon: Undo2 }],
}

/**
 * A bulk action an operator has ASKED for, awaiting confirmation.
 *
 * The rows are captured here at the moment the button is pressed rather than
 * re-read from the table on confirm. The dialog names a count and lists who is
 * in it, and a realtime push landing while it is open must not make the confirm
 * button act on a different set than the sentence above it describes.
 */
interface BulkAsk {
  action: BulkAction
  rows: readonly CustomerRow[]
  /** How many of `rows` a worker held a seat for at ask time. */
  running: number
}

/**
 * react-table is used here for exactly one thing: row selection. Sorting and
 * filtering are the server's, and paging is the URL's — wiring those through a
 * client table model as well would give two sources of truth that disagree the
 * moment a filter narrows the result set.
 */
const TABLE_COLUMNS: ColumnDef<CustomerRow>[] = (
  Object.keys(COLUMN_META) as ColumnId[]
).map((id) => ({ id, header: COLUMN_META[id].label }))

export interface CustomerTableProps {
  status: CustomerStatus
  /**
   * The segmented agent queue this view is, if any.
   *
   * Orthogonal to `status`, and deliberately so: the Telegram list IS a pending
   * list, and passing `status="pending"` is what makes it inherit the pending
   * column tracks (which drop the Status column), `MOVES_BY_STATUS`, the PIN
   * projection, and `canControl === false` — the guard that keeps Start off a
   * customer nobody has approved. Only the row SET differs; see
   * `toCustomerQuery`.
   */
  segment?: CustomerSegment
  /** The table renders its own toolbar by default; opt out to compose one. */
  showToolbar?: boolean
  className?: string
}

export function CustomerTable(p: CustomerTableProps) {
  // Both take the status, so this view's default sort — `won_at desc` on
  // Confirmed — is what an absent `?sort=` means on the way in AND on the way
  // back out. See `defaultSortFor`.
  const search = useCustomerSearch(p.status)
  const setSearch = useSetCustomerSearch(p.status)
  const queryClient = useQueryClient()
  const { toast } = useToast()
  // The bulk bar's "Save for resume". Declared here with the other page-level
  // hooks so the callback below can stay stable across renders.
  const saveRoster = useSaveRoster()

  /* ---------------- viewport ---------------- */

  /*
   * The operator asking for every column, whatever the width says.
   *
   * It overrides the CARD threshold too, not just the column tiers, and that is
   * the part that actually matters. A phone in desktop mode reports ~620px —
   * well under `md` — so the list there is not a narrow table that could be
   * widened, it is cards, which have no columns at all. Forcing only the tier
   * would have changed nothing on the one device this exists for.
   *
   * What it turns on is the ordinary table at its richest tier, in a viewport
   * narrower than its `minWidth`. That is not a new rendering path: it is the
   * same situation as an `xl` layout in a hand-narrowed 1100px window, which
   * has always scrolled sideways. Actions stays pinned to the right edge
   * throughout, so Start and Stop never scroll out of reach.
   */
  const wideColumns = useUiStore((st) => st.wideColumns)
  const isTable = useMediaQuery(MD_UP) || wideColumns
  const isLg = useMediaQuery(LG_UP)
  const isXl = useMediaQuery(XL_UP)
  const isWide = useMediaQuery(WIDE_UP)
  const density = useUiStore((st) => st.density)
  const rowHeight = ROW_HEIGHTS[density]
  // Chosen from the LIST, not from each row: a grid whose track widths changed
  // per row would not be a grid. Every row in the Pending list carries three
  // buttons, so the whole list gets the wider actions column; the Approved
  // list swaps Agent for Notes.
  const byDensity =
    LAYOUTS[p.status === 'pending' ? 'pending' : p.status === 'approved' ? 'approved' : 'default'][
      density
    ]
  // See the note on `wideColumns` above; `readInitialWideColumns` covers why
  // this is a stated preference rather than something detected from the
  // viewport.
  const layout = isWide || wideColumns
    ? byDensity.wide
    : isXl
      ? byDensity.xl
      : isLg
        ? byDensity.lg
        : byDensity.md
  const itemHeight = isTable ? rowHeight : CARD_HEIGHT
  const headerOffset = isTable ? HEADER_HEIGHT : 0

  /* ---------------- data ---------------- */

  // limit/offset come from the URL (`page`, `size`). One page at a time: the
  // old behaviour fetched 500 rows and rendered all of them, which made every
  // filter change a half-megabyte round trip for twenty visible rows.
  const query = React.useMemo(
    () => toCustomerQuery(search, p.status, p.segment),
    [search, p.status, p.segment]
  )

  /*
   * What this list calls itself, and why it can be empty.
   *
   * A segmented queue names itself rather than its status — "No Telegram
   * customers" — and needs its own reason, because "when a customer moves to
   * Telegram they appear here" describes nothing that happens: they arrive by
   * being CREATED under that agent, not by moving.
   */
  const listNoun = listNounFor(p.status, p.segment)
  const emptyHint =
    p.segment === undefined
      ? `When a customer moves to ${p.status} they appear here. Nothing is waiting right now.`
      : `Customers created under the ${listNoun} agent wait here for approval. Nothing is waiting right now.`
  const result = useCustomers(query)

  const rows = result.data?.rows ?? EMPTY_ROWS
  const total = result.data?.total ?? null
  const offset = (search.page - 1) * search.size

  /*
   * The search term, folded to lower case ONCE for the whole table.
   *
   * Every visible row scans four fields for it, so this is the difference
   * between one `toLowerCase` per render and eighty. Derived from the COMMITTED
   * term in the URL rather than the input's own state, which is what keeps the
   * marks in step with the rows they are marking: the toolbar debounces by
   * 250ms, and highlighting on the keystroke instead would paint matches on a
   * result set fetched for a different query.
   */
  const needle = React.useMemo(() => search.q.trim().toLowerCase(), [search.q])

  /* ---------------- local UI state ---------------- */

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [pendingIds, setPendingIds] = React.useState<ReadonlySet<string>>(EMPTY_SET)
  const [focusedIndex, setFocusedIndex] = React.useState(-1)
  const [detailId, setDetailId] = React.useState<string | null>(null)
  const [signinId, setSigninId] = React.useState<string | null>(null)
  /**
   * The open run console: which customer's log the drawer is showing.
   *
   * Opening it never starts anything. Start posts straight to the server (see
   * `start` in the actions memo) so an operator can fire off a whole page of
   * customers without a full-screen drawer landing on each click; the logs
   * button beside Start is the deliberate way in when the console is wanted.
   */
  const [runCustomerId, setRunCustomerId] = React.useState<string | null>(null)
  /** Which row (if any) has been promoted to a real Radix overflow menu. */
  const [menu, setMenu] = React.useState<MenuState>(CLOSED_MENU)
  /**
   * The row a delete has been ASKED about.
   *
   * The whole row rather than an id, because the dialog names the person: a
   * confirmation that says "delete this customer?" is one an operator learns to
   * dismiss without reading.
   */
  const [deleteTarget, setDeleteTarget] = React.useState<CustomerRow | null>(null)
  /**
   * The row whose job / shift is being reassigned, and whether that save is in
   * flight. Held as the row rather than an id so the dialog can name the person
   * and seed both fields without a second lookup.
   */
  const [targetRow, setTargetRow] = React.useState<CustomerRow | null>(null)
  const [targetSaving, setTargetSaving] = React.useState(false)
  /**
   * The single cities panel's anchor and contents, or null when nothing is
   * hovered. One piece of state for the whole table rather than one tooltip per
   * row — see `CitiesCell`.
   */
  const [citiesHover, setCitiesHover] = React.useState<CitiesHover | null>(null)
  /**
   * The bulk action awaiting a yes, and whether that yes is in flight.
   *
   * Every button on the bulk bar goes through here. Start and Stop reach a whole
   * page of customers from one click at the bottom of the screen — the place a
   * pointer ends up by accident — and neither has an undo worth the name: a
   * mistaken Stop abandons live applications, and a mistaken Start sends real
   * verification codes to real people.
   */
  const [bulkAsk, setBulkAsk] = React.useState<BulkAsk | null>(null)
  const [bulkBusy, setBulkBusy] = React.useState(false)
  /**
   * The gender question, when a Start has run into it.
   *
   * Set by `runControl` itself rather than by the buttons, because every Start
   * on this screen — the row's, the bulk bar's — goes through `runControl`, and
   * the gate has to be in front of all of them or it is in front of none. Holds
   * the WHOLE batch the operator asked for and the subset that needs an answer;
   * confirming re-enters `runControl` with the answers, which then finds nothing
   * missing and sends.
   */
  const [genderAsk, setGenderAsk] = React.useState<GenderGateAsk<CustomerRow> | null>(null)

  const navigate = useNavigate()
  const approve = useApproveCustomer()
  const unapprove = useUnapproveCustomer()
  const remove = useDeleteCustomer()

  const scrollRef = React.useRef<HTMLDivElement | null>(null)

  /*
   * Close the cities panel the moment anything moves it out from under the
   * pointer.
   *
   * Its position is a viewport coordinate captured once, and the list is
   * virtualised — so a scroll leaves the panel pointing at a row that is no
   * longer there, and a resize can leave it off screen. Hiding is the right
   * answer rather than repositioning: the pointer is no longer over the cell
   * either, so there is nothing to keep open.
   *
   * `passive` because this never calls `preventDefault`, and the listener is
   * only attached while a panel is open — a table nobody is hovering pays
   * nothing.
   */
  React.useEffect(() => {
    if (citiesHover === null) return
    const close = () => setCitiesHover(null)
    const container = scrollRef.current
    container?.addEventListener('scroll', close, { passive: true })
    window.addEventListener('resize', close, { passive: true })
    // Catches a scroll of the page behind the table, and the drawer opening.
    window.addEventListener('wheel', close, { passive: true })
    // Touch opened the panel with a tap, so a tap elsewhere must close it —
    // there is no mouseleave coming. Taps on a cities cell are left to the
    // cell's own toggle (pointerdown fires before its click). Like everything
    // here, the listener exists only while a panel is open.
    const closeOnTapAway = IS_COARSE
      ? (e: PointerEvent) => {
          if (e.target instanceof Element && e.target.closest('[data-cities-cell]')) return
          close()
        }
      : null
    if (closeOnTapAway) document.addEventListener('pointerdown', closeOnTapAway, { passive: true })
    return () => {
      container?.removeEventListener('scroll', close)
      window.removeEventListener('resize', close)
      window.removeEventListener('wheel', close)
      if (closeOnTapAway) document.removeEventListener('pointerdown', closeOnTapAway)
    }
  }, [citiesHover])
  const bodyRef = React.useRef<HTMLElement | null>(null)
  /** id -> last-changed timestamp. Never state: a burst must not re-render. */
  const flashRef = React.useRef<Map<string, number>>(new Map())
  const prevRowsRef = React.useRef<Map<string, CustomerRow>>(new Map())
  const queryTokenRef = React.useRef<string>('')
  const wantFocusRef = React.useRef(false)

  /** Value-stable identity for "the view changed", used to reset view state. */
  const queryToken = React.useMemo(() => JSON.stringify(customersKey(query)), [query])

  /* ---------------- react-table (selection only) ---------------- */

  const table = useReactTable<CustomerRow>({
    data: rows,
    columns: TABLE_COLUMNS,
    state: { rowSelection },
    // The server sorts and filters; the client only reflects it. Doing either
    // here would silently disagree with the row count in the toolbar.
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    enableRowSelection: true,
    getRowId: (row) => row.id,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
  })

  const tableRows = table.getRowModel().rows

  /* ---------------- sorting (URL-driven) ---------------- */

  /*
   * One callback for all seven filter inputs, stable for the life of the table.
   *
   * Stability is what lets `FilterCell` be memoised at all: a fresh closure per
   * cell per render would change a prop on every one of them every time
   * anything on this screen moved, and the memo would never bail out.
   *
   * `setSearch` already resets the page — narrowing a filter while on page 4 of
   * 7 has to land on page 1 or the operator sees an empty table and a footer
   * that disagrees with it.
   */
  const onFilterCommit = React.useCallback(
    (key: ColumnFilter, value: string) => {
      const patch: Partial<Record<ColumnFilter, string>> = { [key]: value }
      setSearch(patch)
    },
    [setSearch]
  )

  const onSortHeader = React.useCallback(
    (key: CustomerSort) => {
      if (search.sort === key) {
        setSearch({ sort: key, dir: search.dir === 'asc' ? 'desc' : 'asc' })
        return
      }
      // Dates read newest-first on the first click; names read A-Z.
      setSearch({ sort: key, dir: key === 'name' || key === 'customer_id' ? 'asc' : 'desc' })
    },
    [search.sort, search.dir, setSearch]
  )

  /* ---------------- virtualisation ---------------- */

  const getItemKey = React.useCallback((index: number) => tableRows[index]?.id ?? index, [tableRows])

  const estimateSize = React.useCallback(() => itemHeight, [itemHeight])

  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    overscan: OVERSCAN,
    getItemKey,
  })

  // Crossing the phone/table boundary changes the item stride; the virtualizer
  // caches measurements and has to be told.
  React.useEffect(() => {
    virtualizer.measure()
  }, [virtualizer, itemHeight])

  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()

  /* ---------------- page clamping ----------------
   * A hand-edited (or bookmarked) `?page=99` must land on the last real page
   * rather than an empty table. Only ever done against a *fresh* count —
   * `keepPreviousData` means `total` can still belong to the previous query.
   * -------------------------------------------------------------------- */

  React.useEffect(() => {
    if (result.isPlaceholderData || total === null) return
    const pageCount = pageCountOf(total, search.size)
    if (search.page > pageCount) setSearch({ page: pageCount })
  }, [result.isPlaceholderData, total, search.page, search.size, setSearch])

  /* ---------------- flash on realtime change ---------------- */

  React.useEffect(() => {
    const seen = prevRowsRef.current

    // A filter or page change swaps the whole result set; ids that survive are
    // not "updates" and must not flash.
    if (queryTokenRef.current !== queryToken) {
      queryTokenRef.current = queryToken
      seen.clear()
      for (const row of rows) seen.set(row.id, row)
      return
    }

    const changed = new Set<string>()
    const next = new Map<string, CustomerRow>()
    for (const row of rows) {
      next.set(row.id, row)
      const before = seen.get(row.id)
      // `use-realtime` copies on write, so identity inequality *is* the diff.
      if (before !== undefined && before !== row) changed.add(row.id)
    }
    prevRowsRef.current = next
    if (changed.size === 0) return

    const now = performance.now()
    const flash = flashRef.current
    for (const id of changed) flash.set(id, now)
    if (flash.size > 1024) {
      for (const [id, at] of flash) {
        if (now - at > FLASH_MS) flash.delete(id)
      }
    }

    // Walk the ~20 mounted rows once rather than querying per changed id: a
    // burst can change hundreds of rows, but only the visible ones can flash.
    const body = bodyRef.current
    if (!body) return
    for (const child of Array.from(body.children)) {
      const el = child as HTMLElement
      const id = el.dataset['rowId']
      if (id !== undefined && changed.has(id)) playFlash(el)
    }
  }, [rows, queryToken])

  /* ---------------- ACTIVE-hit highlight and scroll ----------------
   * Fed by `active-hits.ts` off the realtime ingest. Both callbacks are DOM
   * work only — a class toggle over the ~20 mounted rows, or one scrollTo —
   * so a sighting never causes a React render of anything. They arrive
   * already coalesced to one call per animation frame however many customers
   * go hot in the same tick. The scroll is skipped while the topbar's
   * auto-scroll switch is off; the highlight never is.
   * ------------------------------------------------------------------ */

  /** Latest geometry and order, readable from the stable view callbacks. */
  const hitViewRef = React.useRef({ tableRows, headerOffset, itemHeight })
  hitViewRef.current = { tableRows, headerOffset, itemHeight }

  React.useEffect(() => {
    return registerActiveHitView({
      onChange() {
        const body = bodyRef.current
        if (!body) return
        for (const child of Array.from(body.children)) {
          const el = child as HTMLElement
          const cid = el.dataset['customerId']
          if (cid !== undefined) markActiveHit(el, isActiveHot(cid))
        }
      },
      onHit(customerId) {
        // The operator's auto-scroll switch, read at delivery rather than
        // captured, so a flip between the sighting and its frame is honoured.
        // Off skips the scroll only: `onChange` has already lit the row.
        if (!useUiStore.getState().autoScrollHits) return
        const { tableRows: rowsNow, headerOffset: header, itemHeight: stride } =
          hitViewRef.current
        // Not on this page or filtered out — nothing to scroll to. The row
        // still lights up wherever and whenever it does become visible.
        const index = rowsNow.findIndex((r) => r.original.customerId === customerId)
        if (index < 0) return
        const container = scrollRef.current
        if (!container) return
        /*
         * Same arithmetic as the keyboard-focus scroll above, and NOT
         * `virtualizer.scrollToIndex`, for the same reason: its coordinate
         * space ignores the sticky header. Centred rather than edge-aligned,
         * because the rows around a sighting — the rest of the fleet on that
         * opening — are exactly what the operator looks at next.
         */
        const top = Math.max(
          0,
          header + index * stride - Math.max(0, (container.clientHeight - stride) / 2)
        )
        container.scrollTo({ top, behavior: 'smooth' })
      },
    })
  }, [])

  /* ---------------- reset view state when the view changes ---------------- */

  React.useEffect(() => {
    setFocusedIndex(-1)
    wantFocusRef.current = false
    // A new filter — or a new page — is a new working set: carrying a selection
    // across it would let "Stop selected" hit rows the operator cannot see.
    setRowSelection({})
    setMenu(CLOSED_MENU)
    scrollRef.current?.scrollTo({ top: 0 })
  }, [queryToken])

  /* ---------------- keyboard focus management ---------------- */

  React.useEffect(() => {
    if (!wantFocusRef.current || focusedIndex < 0) return
    wantFocusRef.current = false

    // Not `virtualizer.scrollToIndex`: its coordinate space starts at the top
    // of the scroll container, which is *behind* the sticky header, so
    // scrolling up lands the target row underneath it. With a fixed row height
    // the correct offset is plain arithmetic.
    const container = scrollRef.current
    if (container) {
      const rowTop = headerOffset + focusedIndex * itemHeight
      const visibleTop = container.scrollTop + headerOffset
      const visibleBottom = container.scrollTop + container.clientHeight
      if (rowTop < visibleTop) {
        container.scrollTop = rowTop - headerOffset
      } else if (rowTop + itemHeight > visibleBottom) {
        container.scrollTop = rowTop + itemHeight - container.clientHeight
      }
    }

    // The row is only in the DOM once the virtualizer has reacted to that
    // scroll, which can take a frame or two after a Home/End jump.
    let attempts = 0
    let handle = 0
    const focusRow = () => {
      const el = bodyRef.current?.querySelector<HTMLElement>(`[data-index="${focusedIndex}"]`)
      if (el) {
        el.focus()
        return
      }
      attempts += 1
      if (attempts < 4) handle = requestAnimationFrame(focusRow)
    }
    handle = requestAnimationFrame(focusRow)
    return () => cancelAnimationFrame(handle)
  }, [focusedIndex, headerOffset, itemHeight])

  const moveFocus = React.useCallback(
    (next: number) => {
      const count = tableRows.length
      if (count === 0) return
      const clamped = Math.min(count - 1, Math.max(0, next))
      if (clamped === focusedIndex) {
        // Already the focused row (arrow held at the top or bottom of the
        // list). Re-assert DOM focus without arming the scroll effect, which
        // would otherwise stay armed and fire on an unrelated focus change.
        bodyRef.current?.querySelector<HTMLElement>(`[data-index="${clamped}"]`)?.focus()
        return
      }
      wantFocusRef.current = true
      setFocusedIndex(clamped)
    },
    [tableRows.length, focusedIndex]
  )

  /* ---------------- control plane (start / stop) ---------------- */

  /**
   * Customers this process is currently holding a seat for.
   *
   * Read once here and passed down, not read per row: it is a fresh `Set` on
   * every poll, so subscribing fifty rows to it would mean fifty re-renders
   * every five seconds instead of one prop the comparators already handle.
   */
  const { data: runs } = useRunStates()
  /*
   * The Workers column's placeholder. Fetched only for the list that has that
   * column, so Pending and Confirmed cost nothing for a number they never show.
   */
  const defaultWorkers = useDefaultWorkers(p.status === 'approved')
  const defaultGqlWorkers = useDefaultGqlWorkers(p.status === 'approved')
  const defaultScheduleWorkers = useDefaultScheduleWorkers(p.status === 'approved')
  const defaultFlow = useDefaultFlow(p.status === 'approved')
  /** Latest run map, readable from stable callbacks without becoming a dep. */
  const runsRef = React.useRef(runs)
  runsRef.current = runs

  const runControl = React.useCallback(
    async (
      targets: readonly CustomerRow[],
      action: 'start' | 'stop',
      /**
       * Answers to the gender question, keyed by row id — present only on the
       * re-entry from the dialog. A row in here is sent with its answer in the
       * body, so the server sets it and starts the run in one request.
       */
      genders?: ReadonlyMap<string, Gender>
    ): Promise<void> => {
      if (targets.length === 0) return

      /*
       * THE GENDER GATE, before anything is sent or shown.
       *
       * Decided from the rows in hand — `gender` rides on every row for exactly
       * this — so it costs no request, for one row or for fifty. A Start with
       * anyone unanswered opens the dialog and stops here: no optimistic flip,
       * no pending state, nothing on the wire. The dialog re-enters with the
       * answers and this same test then passes.
       *
       * All-or-nothing on purpose. Starting the forty-seven that were fine while
       * asking about three would be faster by one dialog's worth of seconds, and
       * would leave an operator who cancels it with a batch half-started and no
       * sentence anywhere saying which half.
       */
      if (action === 'start') {
        const missing = targets.filter((row) => row.gender === null && !genders?.has(row.id))
        if (missing.length > 0) {
          setGenderAsk({ targets, missing })
          return
        }
      }

      const optimistic = new Map<string, Partial<CustomerRow>>()
      const rollback = new Map<string, Partial<CustomerRow>>()
      const ids: string[] = []
      for (const row of targets) {
        ids.push(row.id)
        const answered = genders?.get(row.id)
        /*
         * The answer lands on the row NOW, with the live-state flip, and is
         * deliberately NOT in the rollback. The server persists it before it
         * decides anything about the run — so a Start refused as "already
         * running" has still set the gender, and rolling the row back to `null`
         * would make the next Start ask a question the server has already had
         * answered. If the write genuinely never happened (a dropped socket),
         * the next Start is refused with `gender_required` and the row is
         * corrected from the server's answer below.
         */
        optimistic.set(
          row.id,
          action === 'start'
            ? answered === undefined
              ? startPatch()
              : { ...startPatch(), gender: answered }
            : stopPatch()
        )
        rollback.set(row.id, controlSnapshot(row))
      }

      setPendingIds((prev) => {
        const next = new Set(prev)
        for (const id of ids) next.add(id)
        return next
      })
      // Optimistic: the row shows the requested state immediately, because a
      // control that looks like it did nothing gets clicked twice.
      applyCustomerPatches(queryClient, optimistic)

      const settled = await Promise.allSettled(
        targets.map((row) => {
          const answered = genders?.get(row.id)
          return apiPost<unknown>(
            `/api/customers/${encodeURIComponent(row.id)}/${action}`,
            // No body at all for the ordinary case — the request it has always been.
            answered === undefined ? undefined : { gender: answered }
          )
        })
      )

      const failedPatches = new Map<string, Partial<CustomerRow>>()
      const failedLabels: string[] = []
      /*
       * Rows the SERVER says have no gender, whatever the row said.
       *
       * The gate above trusted the cached row; this is the correction when it
       * was wrong — the field cleared in the detail panel since the page loaded,
       * or an optimistic answer whose write never landed. These are not
       * failures to toast about; they are the question, asked again, with the
       * row put back to the truth so the gate agrees with the server next time.
       */
      const unanswered: CustomerRow[] = []
      // The first server-side reason, verbatim. With no console opening on
      // Start any more, this toast is the only place a refusal ("already
      // running", "watching for a shift — stop it first") can reach the
      // operator at all.
      let failedReason: string | null = null
      settled.forEach((outcome, i) => {
        const row = targets[i]
        if (!row || outcome.status !== 'rejected') return
        const previous = rollback.get(row.id)
        if (apiErrorCode(outcome.reason) === 'gender_required') {
          unanswered.push(row)
          failedPatches.set(row.id, { ...previous, gender: null })
          return
        }
        failedLabels.push(row.customerId)
        if (failedReason === null && outcome.reason instanceof ApiError) {
          failedReason = outcome.reason.message
        }
        if (previous) failedPatches.set(row.id, previous)
      })

      // Roll back only the fields the optimistic write touched, so a realtime
      // patch that landed in the meantime is not clobbered.
      if (failedPatches.size > 0) applyCustomerPatches(queryClient, failedPatches)

      // Asked again, for exactly these, flagged as the list having been wrong.
      if (unanswered.length > 0) {
        setGenderAsk({ targets: unanswered, missing: unanswered, stale: true })
      }

      /*
       * Nothing to invalidate here any more.
       *
       * This used to refresh a `['automation','running']` poll so a Stop would
       * not appear to do nothing for up to five seconds. That key no longer
       * exists: the server pushes `run.state` from its own stop path, so the
       * pill flips within ~80ms of the request landing. Re-adding an
       * invalidation would refetch the whole run map on every bulk control press
       * to learn something already on its way over the wire.
       */
      setPendingIds((prev) => {
        const next = new Set(prev)
        for (const id of ids) next.delete(id)
        return next
      })

      const verb = action === 'start' ? 'Start' : 'Stop'
      if (failedLabels.length > 0) {
        const first = failedLabels[0]
        toast({
          title: `${verb} refused`,
          description:
            failedLabels.length === 1
              ? `${first ?? 'The customer'}: ${failedReason ?? `${verb} was not delivered. Nothing changed on the server.`}`
              : `${verb} was refused for ${failedLabels.length} customers${failedReason ? ` — first reason: ${failedReason}` : ''}. Nothing changed on the server for them.`,
          tone: 'warn',
        })
      }
      /*
       * Counted, not assumed. The rows being asked about again are neither sent
       * nor refused — the dialog on screen is their report — so "Start sent for
       * 50" over a batch where three are still waiting on an answer would be a
       * number the operator cannot reconcile with what they are looking at.
       */
      const notSent = new Set<string>(failedPatches.keys())
      const sent = targets.filter((row) => !notSent.has(row.id))
      if (sent.length > 0) {
        toast({
          title: `${verb} requested`,
          description:
            sent.length === 1
              ? `${verb} sent for ${sent[0]?.customerId ?? ''}.`
              : `${verb} sent for ${sent.length} customers.`,
          tone: 'ok',
        })
      }
    },
    [queryClient, toast]
  )

  /* ---------------- moving a selection between the two lists ---------------- */

  /**
   * Move every selected customer one step along the status chain.
   *
   * One request per row, like `runControl`, because there is no bulk transition
   * on the server: each of the four is a named sub-resource with a rule of its
   * own about which status it will accept, and an audit line of its own, which
   * is exactly what makes a partial outcome survivable — the rows that were
   * refused stay where they are and stay ticked.
   *
   * Deliberately NOT optimistic, unlike start/stop. A moved row leaves this list
   * entirely, so an optimistic write that had to be rolled back would put a
   * customer back into a list the operator has already scrolled past. The same
   * reasoning is why `useApproveCustomer` waits for the server too; here it costs
   * one round trip for the whole batch rather than one per row.
   */
  const runMove = React.useCallback(
    async (targets: readonly CustomerRow[], action: MoveAction): Promise<void> => {
      if (targets.length === 0) return

      const ids = targets.map((row) => row.id)
      setPendingIds((prev) => {
        const next = new Set(prev)
        for (const id of ids) next.add(id)
        return next
      })

      try {
        const settled = await Promise.allSettled(
          // `customerId`, not `id` — matching the single-row hooks these share a
          // route with. The server resolves either, but one file should not ask
          // the same endpoint two different ways.
          targets.map((row) =>
            apiPost<unknown>(`/api/customers/${encodeURIComponent(row.customerId)}/${action}`)
          )
        )

        const movedIds: string[] = []
        const failedLabels: string[] = []
        // The first server-side reason, verbatim — "not pending any more",
        // "not approved". These mean the list is stale, and the operator needs
        // the sentence rather than a count.
        let failedReason: string | null = null
        settled.forEach((outcome, i) => {
          const row = targets[i]
          if (!row) return
          if (outcome.status === 'fulfilled') {
            movedIds.push(row.id)
            return
          }
          failedLabels.push(row.customerId)
          if (failedReason === null && outcome.reason instanceof ApiError) {
            failedReason = outcome.reason.message
          }
        })

        /*
         * Untick what actually moved, and only that.
         *
         * The refetch below drops the moved rows out of `rows`, which alone
         * would be enough to shrink the bar's count — but it would leave their
         * ids in `rowSelection` for a view change to clean up. Removing them
         * here keeps the rows that were REFUSED ticked and nothing else, so the
         * bar goes on naming exactly who is left to deal with.
         */
        if (movedIds.length > 0) {
          setRowSelection((prev) => {
            const next = { ...prev }
            let changed = false
            for (const id of movedIds) {
              if (id in next) {
                delete next[id]
                changed = true
              }
            }
            return changed ? next : prev
          })
        }

        /*
         * Refetched once for the batch, not once per row.
         *
         * Both keys, and both for the same reason `invalidateLists` does it: the
         * rows leave this list, and the rail's Pending/Approved counts have to
         * change together or neither is believable. A refusal invalidates too —
         * "not pending any more" is the server saying this page is stale.
         */
        void queryClient.invalidateQueries({ queryKey: ['customers'] })
        void queryClient.invalidateQueries({ queryKey: ['stats'] })

        const { destination, tone } = MOVE_META[action]
        if (failedLabels.length === 0) {
          toast({
            title: `Moved to ${destination}`,
            description:
              targets.length === 1
                ? `${targets[0]?.name || targets[0]?.customerId} is now in ${destination}. Nothing else was changed.`
                : `${targets.length} customers are now in ${destination}. Nothing else was changed.`,
            tone,
          })
        } else if (movedIds.length === 0) {
          toast({
            title: 'Move refused',
            description:
              failedLabels.length === 1
                ? `${failedLabels[0]}: ${failedReason ?? 'the move was not delivered. Nothing changed on the server.'}`
                : `None of the ${failedLabels.length} selected customers moved${failedReason ? ` — first reason: ${failedReason}` : ''}. Nothing changed on the server.`,
            tone: 'danger',
          })
        } else {
          toast({
            title: `Moved ${movedIds.length} of ${targets.length}`,
            description: `${failedLabels.length} ${failedLabels.length === 1 ? 'customer was' : 'customers were'} refused and ${failedLabels.length === 1 ? 'is' : 'are'} still ticked${failedReason ? ` — first reason: ${failedReason}` : ''}.`,
            tone: 'warn',
          })
        }
      } finally {
        // In the `finally` so a throw between the two can never strand a row
        // with its per-row buttons disabled for the life of the page.
        setPendingIds((prev) => {
          const next = new Set(prev)
          for (const id of ids) next.delete(id)
          return next
        })
      }
    },
    [queryClient, toast]
  )

  /* ---------------- the Status column's editable tag ---------------- */

  /**
   * Save one customer's `computer_number`, optimistically.
   *
   * ── Why optimistic, and why nothing is invalidated ──────────────────
   * The operator has already typed the value and is looking at it. Waiting for
   * a round trip to Atlas before painting it would make a two-character edit
   * feel like a form submission, and the row would visibly flicker back to the
   * old value in between. So the cache is patched first and the request follows.
   *
   * Nothing is invalidated on success, and that is the important half. The
   * obvious `invalidateQueries(['customers'])` would refetch every cached page
   * of the table on every debounced keystroke — the single most expensive thing
   * this screen can do — to learn a value it already has. The server's change
   * stream publishes `computer_number` (it is in `CUSTOMER_FIELDS`), so every
   * *other* open console gets the new value pushed to it within ~80ms, and this
   * one already has it. The two paths agree without either one refetching.
   *
   * On failure the field is put back exactly as it was and the operator is told.
   * Rolling back only `computerNumber` matters: a realtime patch may well have
   * landed on this row while the request was in flight, and a wholesale restore
   * would undo that too.
   */
  const saveNumber = React.useCallback(
    async (row: CustomerRow, value: string): Promise<void> => {
      const next = value.trim()
      const previous = row.computerNumber
      if (next === (previous ?? '')) return

      const stored = next === '' ? null : next
      applyCustomerPatches(queryClient, new Map([[row.id, { computerNumber: stored }]]))

      try {
        await apiPatch<{ ok: true }>(`/api/customers/${encodeURIComponent(row.id)}`, {
          computerNumber: stored,
        })
      } catch (err) {
        applyCustomerPatches(queryClient, new Map([[row.id, { computerNumber: previous }]]))
        toast({
          title: 'Number not saved',
          description: `${row.customerId}: ${friendlyError(err)}`,
          tone: 'danger',
        })
      }
    },
    [queryClient, toast]
  )

  /* ---------------- the Workers column ---------------- */

  /**
   * Save one customer's worker override, on the same terms as the number tag:
   * optimistic, no invalidation, rolled back and reported if the server refuses.
   *
   * The server is the authority on the range. The cell already makes a third
   * digit impossible to type, but `0` and 51-99 reach the API and come back as
   * a 400 whose message explains itself ("more than 50 workers on one IP is a
   * rate-limit ban…"). Re-implementing that bound here would put it in a third
   * place and give the operator a worse sentence for the trouble.
   */
  const saveWorkers = React.useCallback(
    async (row: CustomerRow, value: number | null): Promise<void> => {
      const previous = row.huntWorkers
      if (value === previous) return

      applyCustomerPatches(queryClient, new Map([[row.id, { huntWorkers: value }]]))

      try {
        await apiPatch<{ ok: true }>(`/api/customers/${encodeURIComponent(row.id)}`, {
          huntWorkers: value,
        })
      } catch (err) {
        applyCustomerPatches(queryClient, new Map([[row.id, { huntWorkers: previous }]]))
        toast({
          title: 'Workers not saved',
          description: `${row.customerId}: ${friendlyError(err)}`,
          tone: 'danger',
        })
      }
    },
    [queryClient, toast]
  )

  /**
   * The same, for the shift-list detector's count.
   *
   * Deliberately a second callback rather than one parameterised by field name.
   * The optimistic patch, the rollback and the toast all name the field three
   * times, and a generic version would either take those as three more
   * arguments — which is longer than this — or build them from a key, which is
   * how a rollback ends up restoring the wrong column. Two short, obvious
   * functions beat one clever one on the path where being wrong silently
   * discards an operator's edit.
   */
  const saveGqlWorkers = React.useCallback(
    async (row: CustomerRow, value: number | null): Promise<void> => {
      const previous = row.huntGqlWorkers
      if (value === previous) return

      applyCustomerPatches(queryClient, new Map([[row.id, { huntGqlWorkers: value }]]))

      try {
        await apiPatch<{ ok: true }>(`/api/customers/${encodeURIComponent(row.id)}`, {
          huntGqlWorkers: value,
        })
      } catch (err) {
        applyCustomerPatches(queryClient, new Map([[row.id, { huntGqlWorkers: previous }]]))
        toast({
          title: 'Shift-list workers not saved',
          description: `${row.customerId}: ${friendlyError(err)}`,
          tone: 'danger',
        })
      }
    },
    [queryClient, toast]
  )

  /**
   * The same again for the GA fleet — and deliberately its own function.
   *
   * Three near-identical callbacks rather than one parameterised by column name,
   * for the reason stated above: the rollback restores a specific field, and a
   * clever shared version is how it ends up restoring the wrong one. Being wrong
   * here silently discards an operator's edit.
   */
  const saveScheduleWorkers = React.useCallback(
    async (row: CustomerRow, value: number | null): Promise<void> => {
      const previous = row.scheduleWorkers
      if (value === previous) return

      applyCustomerPatches(queryClient, new Map([[row.id, { scheduleWorkers: value }]]))

      try {
        await apiPatch<{ ok: true }>(`/api/customers/${encodeURIComponent(row.id)}`, {
          scheduleWorkers: value,
        })
      } catch (err) {
        applyCustomerPatches(queryClient, new Map([[row.id, { scheduleWorkers: previous }]]))
        toast({
          title: 'GA workers not saved',
          description: `${row.customerId}: ${friendlyError(err)}`,
          tone: 'danger',
        })
      }
    },
    [queryClient, toast]
  )

  /**
   * The claim flow, saved the moment it changes.
   *
   * Its own callback rather than a parameterised share with the three worker
   * savers above, for the reason stated there: the rollback restores a SPECIFIC
   * field, and a clever shared version is how it ends up restoring the wrong
   * one — silently discarding an operator's edit.
   */
  const saveFlow = React.useCallback(
    async (row: CustomerRow, value: 'current' | 'brazil' | null): Promise<void> => {
      const previous = row.huntFlow
      if (value === previous) return

      applyCustomerPatches(queryClient, new Map([[row.id, { huntFlow: value }]]))

      try {
        await apiPatch<{ ok: true }>(`/api/customers/${encodeURIComponent(row.id)}`, {
          // The route reads an empty string as "clear the override", which is
          // what a null means here — see its own note on the field.
          huntFlow: value ?? '',
        })
      } catch (err) {
        applyCustomerPatches(queryClient, new Map([[row.id, { huntFlow: previous }]]))
        toast({
          title: 'Flow not saved',
          description: `${row.customerId}: ${friendlyError(err)}`,
          tone: 'danger',
        })
      }
    },
    [queryClient, toast]
  )

  /* ---------------- reassigning the job and shift ---------------- */

  /**
   * Write both ids, or clear both.
   *
   * ── Deliberately NOT optimistic, unlike the number edit ─────────────
   * The tag in the Status column is a note to the operator; this reassigns which
   * shift a real person is applying for, and the server can refuse it — the pair
   * rule, a bad id, a customer that has since been deleted. Painting the new job
   * first and closing would surface that failure after the operator had moved
   * on, with the row quietly back on the old job and nothing on screen to say
   * so. The dialog owns a Save button with a pending state, which is exactly the
   * affordance for waiting; so it waits, and it closes only on success.
   *
   * The cache is patched rather than invalidated once the server agrees, which
   * needs one thing the ids alone do not carry: the Job column paints
   * `jobIdFull` / `scheduleIdFull`, derived server-side. Writing only the bare
   * ids would leave the cell showing the OLD qualified id until the change
   * stream caught up. They are rebuilt here from the country code already on the
   * row — the same value the server qualifies with.
   */
  const saveTarget = React.useCallback(
    async (row: CustomerRow, jobId: string, scheduleId: string): Promise<void> => {
      const job = jobId === '' ? null : jobId
      const schedule = scheduleId === '' ? null : scheduleId

      const country = idCountry(row)
      const qualify = (prefix: string, value: string | null): string | null =>
        value === null ? null : country === null ? value : `${prefix}-${country}-${value}`

      setTargetSaving(true)
      try {
        const result = await apiPatch<TargetSaveResult>(
          `/api/customers/${encodeURIComponent(row.id)}`,
          { jobId: job, scheduleId: schedule }
        )

        applyCustomerPatches(
          queryClient,
          new Map([
            [
              row.id,
              {
                jobId: job,
                jobIdFull: qualify('JOB', job),
                scheduleId: schedule,
                scheduleIdFull: qualify('SCH', schedule),
                // Patched with the pair so the cell's hover is right at once
                // rather than a change-stream frame later.
                targetUuid: uuidRowState(result.uuid?.state ?? null),
              },
            ],
          ])
        )
        setTargetRow(null)
        toast({
          title: job === null ? 'Job and shift cleared' : 'Job and shift updated',
          /*
           * The server's sentence when it has one — it knows things this side
           * does not, like whether the shift is in the schedule list at all.
           * The write succeeded either way, so the warning goes in the
           * description rather than replacing the title.
           */
          description:
            result.uuid?.warning ??
            (job === null
              ? `${row.customerId} is no longer assigned to a shift.`
              : `${row.customerId} is now on job ${job}, shift ${schedule}.`),
          tone: job === null ? 'warn' : uuidTone(result.uuid?.state ?? null),
        })
      } catch (err) {
        toast({ title: 'Could not save', description: friendlyError(err), tone: 'danger' })
      } finally {
        setTargetSaving(false)
      }
    },
    [queryClient, toast]
  )

  /* ---------------- the one stable actions object ---------------- */

  const actions = React.useMemo<RowActions>(
    () => ({
      openDetail: (id) => setDetailId(id),
      toggleSelect: (id) =>
        setRowSelection((prev) => {
          const next = { ...prev }
          if (next[id]) delete next[id]
          else next[id] = true
          return next
        }),
      /*
       * Open the run drawer; the drawer starts the run.
       *
       * Deliberately NOT `startRun.mutate(...)` here. Firing the mutation from
       * this callback meant a rejected start — no PIN on file (404), already
       * running (409) — resolved into a mutation object nobody rendered, so the
       * drawer opened, sat on "No run recorded", and told the operator nothing.
       * The drawer owns the call so its pending and error states appear exactly
       * where the operator is already looking.
       *
       * The run itself is fire-and-forget: the server claims the per-customer
       * lock before responding and writes progress to the run log, so closing
       * the drawer or reloading does not touch the run.
       */
      // Fire-and-forget, same door as the toolbar's "Start selected": the row
      // flips optimistically, the toast confirms, and no drawer opens — the
      // logs button (`viewLogs`) is the way into the console when it is wanted.
      start: (row) => void runControl([row], 'start'),
      stop: (row) => void runControl([row], 'stop'),
      /*
       * This console's run log for the row, and only that.
       *
       * There used to be a fallback here: a row with no run of ours opened the
       * SIBLING fleet's session log instead. Two different systems' records
       * behind one button, with nothing on screen saying which you got.
       *
       * Opening the drawer unconditionally is the honest version — a customer
       * this console has never run shows an empty run log, which is exactly
       * what is true.
       *
       * Read through a ref rather than closed over: `actions` is memoised and
       * compared by identity in the row comparator, so listing the run map as a
       * dependency would rebuild this object on every state frame and re-render
       * all fifty visible rows because one counter ticked.
       */
      viewLogs: (row) => setRunCustomerId(row.customerId),
      copy: (value) => {
        if (value === '') return
        const clipboard = navigator.clipboard
        if (!clipboard?.writeText) {
          toast({ title: 'Clipboard unavailable', description: value, tone: 'warn' })
          return
        }
        void clipboard.writeText(value).then(
          () => toast({ title: 'Copied', description: value, tone: 'neutral' }),
          () => toast({ title: 'Could not copy', description: value, tone: 'warn' })
        )
      },
      setNumber: (row, value) => void saveNumber(row, value),
      setWorkers: (row, value) => void saveWorkers(row, value),
      setGqlWorkers: (row, value) => void saveGqlWorkers(row, value),
      setScheduleWorkers: (row, value) => void saveScheduleWorkers(row, value),
      setFlow: (row, value) => void saveFlow(row, value),
      editTarget: (row) => setTargetRow(row),
      openMenu: (id) => setMenu({ id, open: true }),
      setMenuOpen: (id, open) =>
        // Closing keeps the row promoted rather than unmounting the menu under
        // Radix's feet: it restores focus to its trigger on close, and a
        // trigger that vanished in the same commit drops focus to <body>.
        setMenu((prev) => (open ? { id, open: true } : prev.id === id ? { id, open: false } : prev)),
      flashAt: (id) => flashRef.current.get(id),

      edit: (row) => {
        void navigate({ to: '/customers/$id/edit', params: { id: row.customerId } })
      },

      approve: (row) => {
        setPendingIds((prev) => new Set(prev).add(row.id))
        approve.mutate(row.customerId, {
          onSuccess: () => {
            toast({
              title: `${row.name || row.customerId} approved`,
              description: 'Moved to the Approved list.',
              tone: 'ok',
            })
          },
          onError: (err) => {
            toast({
              title: 'Could not approve',
              description: friendlyError(err),
              tone: 'danger',
            })
          },
          onSettled: () => {
            setPendingIds((prev) => {
              const next = new Set(prev)
              next.delete(row.id)
              return next
            })
          },
        })
      },

      unapprove: (row) => {
        setPendingIds((prev) => new Set(prev).add(row.id))
        unapprove.mutate(row.customerId, {
          onSuccess: () => {
            toast({
              title: `${row.name || row.customerId} moved back`,
              description: 'Now in Pending. Nothing else was changed.',
              tone: 'warn',
            })
          },
          onError: (err) => {
            toast({ title: 'Could not move back', description: friendlyError(err), tone: 'danger' })
          },
          onSettled: () => {
            setPendingIds((prev) => {
              const next = new Set(prev)
              next.delete(row.id)
              return next
            })
          },
        })
      },

      // Asks; it does not delete. The dialog below owns the call, so the
      // irreversible step is never one click away from a mis-aimed pointer.
      askDelete: (row) => setDeleteTarget(row),

      showCities: (anchor, cities, warehouses) => {
        const r = anchor.getBoundingClientRect()
        setCitiesHover({ cities, warehouses, top: r.top, left: r.left, bottom: r.bottom })
      },
      hideCities: () => setCitiesHover(null),
      // Functional update on purpose: deciding "same cell tapped again?" from
      // `prev` keeps `citiesHover` out of this memo's deps — depending on it
      // would rebuild `actions` on every hover, and `actions` identity is part
      // of every row's memo bailout.
      toggleCities: (anchor, cities, warehouses) => {
        const r = anchor.getBoundingClientRect()
        setCitiesHover((prev) =>
          prev !== null && prev.top === r.top && prev.left === r.left
            ? null
            : { cities, warehouses, top: r.top, left: r.left, bottom: r.bottom }
        )
      },
    }),
    [runControl, saveNumber, saveWorkers, saveGqlWorkers, toast, navigate, approve, unapprove]
  )

  /* ---------------- delegated row events ----------------
   * One listener on the body instead of a handler per row: keydown, click and
   * focus all bubble, so a whole page of rows costs exactly three closures.
   * ------------------------------------------------------ */

  const indexFromEvent = React.useCallback((target: EventTarget | null): number => {
    if (!(target instanceof HTMLElement)) return -1
    const el = target.closest<HTMLElement>('[data-index]')
    if (!el) return -1
    const raw = el.dataset['index']
    if (raw === undefined) return -1
    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : -1
  }, [])

  const onBodyKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const index = indexFromEvent(event.target)
      if (index < 0) return

      const target = event.target as HTMLElement
      const insideControl =
        target.closest('button, a, input, select, textarea, [role="menuitem"]') !== null

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          moveFocus(index + 1)
          break
        case 'ArrowUp':
          event.preventDefault()
          moveFocus(index - 1)
          break
        case 'PageDown':
          event.preventDefault()
          moveFocus(index + 10)
          break
        case 'PageUp':
          event.preventDefault()
          moveFocus(index - 10)
          break
        case 'Home':
          event.preventDefault()
          moveFocus(0)
          break
        case 'End':
          event.preventDefault()
          moveFocus(tableRows.length - 1)
          break
        case 'Enter': {
          if (insideControl) return
          event.preventDefault()
          const row = tableRows[index]?.original
          if (row) setDetailId(row.id)
          break
        }
        case ' ': {
          if (insideControl) return
          // Space would otherwise page the scroll container out from under the
          // operator's selection.
          event.preventDefault()
          const row = tableRows[index]?.original
          if (row) actions.toggleSelect(row.id)
          break
        }
        default:
          break
      }
    },
    [indexFromEvent, moveFocus, tableRows, actions]
  )

  /**
   * Double click opens the row — unless the double click was somebody selecting
   * a word.
   *
   * Double click is the browser's select-a-word gesture, so binding "open the
   * profile" to it means an operator who double clicks an email to copy it gets
   * a drawer over the thing they were trying to read. That is not a rare edge:
   * copying an address out of this table is one of the commonest things anyone
   * does with it.
   *
   * The two intents are distinguishable after the fact. By the time `dblclick`
   * dispatches, the browser has already set the selection — on the second
   * mousedown, before mouseup — so a non-empty selection means the gesture
   * landed on text and was a selection. Empty means it landed on the row's own
   * padding, where opening is the only thing it could have meant.
   *
   * `toString().trim()` rather than `isCollapsed` alone: a double click between
   * two cells can leave a technically non-collapsed range spanning nothing but
   * whitespace, which is not a selection anybody made on purpose.
   */
  const onBodyDoubleClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if ((event.target as HTMLElement).closest('button, a, input, [role="menuitem"]')) return

      const selection = window.getSelection()
      if (selection !== null && !selection.isCollapsed && selection.toString().trim() !== '') {
        return
      }

      const index = indexFromEvent(event.target)
      const row = index >= 0 ? tableRows[index]?.original : undefined
      if (row) setDetailId(row.id)
    },
    [indexFromEvent, tableRows]
  )

  const onBodyFocus = React.useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      const index = indexFromEvent(event.target)
      if (index >= 0) setFocusedIndex((prev) => (prev === index ? prev : index))
    },
    [indexFromEvent]
  )

  const setBodyRef = React.useCallback((el: HTMLElement | null) => {
    bodyRef.current = el
  }, [])

  /* ---------------- selection ---------------- */

  const selectedRows = table.getSelectedRowModel().rows
  const selectedCount = selectedRows.length

  const headerChecked: boolean | 'indeterminate' = table.getIsAllRowsSelected()
    ? true
    : table.getIsSomeRowsSelected()
      ? 'indeterminate'
      : false

  const onToggleAll = React.useCallback(
    (value: boolean) => {
      table.toggleAllRowsSelected(value)
    },
    [table]
  )

  const onClearSelection = React.useCallback(() => {
    setRowSelection({})
  }, [])

  /* ---------------- bulk job/shift (toolbar slot) ----------------
   * Two boxes and a button that appear beside Sort while anything is selected:
   * seed from the FIRST customer ticked, let the operator edit, write the pair
   * to every selected row in one request.
   * ---------------------------------------------------------------- */

  const [bulkJob, setBulkJob] = React.useState('')
  const [bulkSchedule, setBulkSchedule] = React.useState('')
  const [bulkSaving, setBulkSaving] = React.useState(false)
  /**
   * Which row the boxes were seeded from — the first customer ticked. The seed
   * happens ONCE per selection: ticking a second, third, tenth row must not
   * overwrite the values, which by then may carry the operator's own edits.
   * Only an emptied selection resets it (and a filter/page change empties the
   * selection, so a new view always starts clean).
   */
  const bulkSeedRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (selectedCount === 0) {
      if (bulkSeedRef.current !== null) {
        bulkSeedRef.current = null
        setBulkJob('')
        setBulkSchedule('')
      }
      return
    }
    if (bulkSeedRef.current !== null) return
    const first = selectedRows[0]?.original
    if (first === undefined) return
    bulkSeedRef.current = first.id
    setBulkJob(first.jobId ?? '')
    setBulkSchedule(first.scheduleId ?? '')
  }, [selectedCount, selectedRows])

  /**
   * Write the pair to every selected customer.
   *
   * One `PATCH /customers/target` rather than a request per row — fifty
   * selected rows is one round trip and one `updateMany`, not fifty writes
   * racing each other. Like `saveTarget` it is deliberately NOT optimistic
   * (the server can refuse the pair), but the wait is a single request and the
   * cache is patched the moment it lands, so every row repaints at once.
   *
   * The cache patch rebuilds `jobIdFull`/`scheduleIdFull` per row from that
   * row's own country code — selections can mix markets, so the qualified form
   * is not one string shared by the batch.
   */
  const saveBulkTargets = React.useCallback(async (): Promise<void> => {
    const targets = table.getSelectedRowModel().rows.map((r) => r.original)
    if (targets.length === 0) return

    const jobField = readIdField(bulkJob, 'job')
    const scheduleField = readIdField(bulkSchedule, 'shift')
    const bothEmpty = jobField.value === '' && scheduleField.value === ''
    const bothSet = jobField.value !== '' && scheduleField.value !== ''
    // The bar disables Update on any of these; this is the second line of
    // defence for an Enter keypress racing a keystroke.
    if (jobField.error !== null || scheduleField.error !== null || (!bothEmpty && !bothSet)) return

    const job = jobField.value === '' ? null : jobField.value
    const schedule = scheduleField.value === '' ? null : scheduleField.value

    setBulkSaving(true)
    try {
      const outcome = await apiPatch<{
        matched: number
        modified: number
        uuid: NonNullable<TargetUuidOutcome>
      }>('/api/customers/target', {
        ids: targets.map((row) => row.customerId),
        jobId: job,
        scheduleId: schedule,
      })

      // One pair for the whole selection, so one UUID state for all of them.
      const uuidState = uuidRowState(outcome.uuid.state)
      const patches = new Map<string, Partial<CustomerRow>>()
      for (const row of targets) {
        const country = idCountry(row)
        const qualify = (prefix: string, value: string | null): string | null =>
          value === null ? null : country === null ? value : `${prefix}-${country}-${value}`
        patches.set(row.id, {
          jobId: job,
          jobIdFull: qualify('JOB', job),
          scheduleId: schedule,
          scheduleIdFull: qualify('SCH', schedule),
          targetUuid: uuidState,
        })
      }
      applyCustomerPatches(queryClient, patches)

      const missed = targets.length - outcome.matched
      if (missed > 0) {
        toast({
          title: `Updated ${outcome.matched} of ${targets.length}`,
          description: `${missed} selected ${missed === 1 ? 'customer was' : 'customers were'} not found — the list may be stale. Refresh to see who is left.`,
          tone: 'warn',
        })
      } else {
        toast({
          title: job === null ? 'Job and shift cleared' : 'Job and shift updated',
          // The server's sentence when it has one — it applies to the whole
          // selection, since they all just got the same pair.
          description:
            outcome.uuid.warning ??
            (job === null
              ? `${outcome.matched} ${outcome.matched === 1 ? 'customer is' : 'customers are'} no longer assigned to a shift.`
              : `${outcome.matched} ${outcome.matched === 1 ? 'customer' : 'customers'} now on job ${job}, shift ${schedule}.`),
          tone: job === null ? 'warn' : uuidTone(outcome.uuid.state),
        })
      }
    } catch (err) {
      toast({
        title: 'Selection not updated',
        description: friendlyError(err),
        tone: 'danger',
      })
    } finally {
      setBulkSaving(false)
    }
  }, [table, bulkJob, bulkSchedule, queryClient, toast])

  const onBulkUpdate = React.useCallback(() => {
    void saveBulkTargets()
  }, [saveBulkTargets])

  /* ---------------- bulk bar: ask, then act ---------------- */

  /** Where a selection on this list can be moved. Never empty in practice. */
  const moves = MOVES_BY_STATUS[p.status]

  /**
   * Whether this list may run anything at all.
   *
   * Pending may not, and the per-row UI has always agreed: `PendingActions`
   * offers Edit, Approve and Delete, and the overflow menu returns before it
   * reaches Force stop. The bulk bar was the one way round that — `/start`
   * gates on a PIN and IMAP password being on file, not on status, so Start
   * from here would really sign a customer in and send them a verification
   * code before anyone had approved them.
   */
  const canControl = p.status !== 'pending'

  /**
   * Open the confirmation for `action` over whatever is ticked right now.
   *
   * Reads the run map from the ref rather than the render's `runs`, which keeps
   * this callback stable across the five-second poll — the bulk bar's buttons
   * would otherwise get a new handler identity on every frame.
   */
  const askBulk = React.useCallback(
    (action: BulkAction) => {
      const rows = table.getSelectedRowModel().rows.map((r) => r.original)
      if (rows.length === 0) return
      const map = runsRef.current
      let running = 0
      for (const row of rows) {
        if (runOwnsRow(map?.[row.customerId] ?? null)) running += 1
      }
      setBulkAsk({ action, rows, running })
    },
    [table]
  )

  const onStartSelected = React.useCallback(() => {
    askBulk('start')
  }, [askBulk])

  const onStopSelected = React.useCallback(() => {
    askBulk('stop')
  }, [askBulk])

  /**
   * Save the ticked rows as the resume roster.
   *
   * Keyed by `customerId`, never `row.id`: the roster has to survive a redeploy
   * and be startable weeks later, and the customer id is the identifier every
   * other surface — the API, the logs, the toasts — agrees on.
   *
   * This is the CURATED path. Selection is per page, so for "everyone that is
   * running" the resume bar's own button asks the server to resolve the set
   * instead; see the note there.
   */
  const onSaveForResume = React.useCallback(() => {
    const ids = table.getSelectedRowModel().rows.map((r) => r.original.customerId)
    if (ids.length === 0) return
    saveRoster.mutate(
      { ids },
      {
        onSuccess: (res) => {
          toast({
            title: `Saved ${res.saved} for resume`,
            description:
              res.unknown.length > 0
                ? `${res.unknown.length} are no longer approved and were skipped. Press "Start saved" after the deploy.`
                : 'Press "Start saved" after the deploy to bring them back one at a time.',
            tone: res.unknown.length > 0 ? 'warn' : 'ok',
          })
        },
        onError: (err) => {
          toast({
            title: 'Roster not saved',
            description: err instanceof ApiError ? err.message : 'The request did not reach the server.',
            tone: 'danger',
          })
        },
      }
    )
  }, [table, saveRoster, toast])

  const onCancelBulk = React.useCallback(() => {
    setBulkAsk(null)
  }, [])

  const onCancelGender = React.useCallback(() => {
    setGenderAsk(null)
  }, [])

  /**
   * The answers, back into the same door the question came out of.
   *
   * The captured `targets` are re-sent whole — the answered rows with their
   * gender in the body, the rest as an ordinary Start — so a batch of fifty
   * with three asked about is still one Start of fifty, in the order the
   * operator ticked them. Closed first, because the starts report through the
   * same toasts every Start does and a dialog left open over them would be a
   * second, staler account of the same event.
   */
  const onConfirmGender = React.useCallback(
    (answers: ReadonlyMap<string, Gender>) => {
      const ask = genderAsk
      if (ask === null) return
      setGenderAsk(null)
      void runControl(ask.targets, 'start', answers)
    },
    [genderAsk, runControl]
  )

  /**
   * The yes.
   *
   * `bulkBusy` guards re-entry: the dialog stays open and its button spins until
   * the batch settles, so an Enter held down on the confirm cannot fire a second
   * page of requests behind the first.
   */
  const onConfirmBulk = React.useCallback(async (): Promise<void> => {
    const ask = bulkAsk
    if (ask === null || bulkBusy) return
    setBulkBusy(true)
    try {
      if (ask.action === 'start' || ask.action === 'stop') {
        /*
         * WHO is the captured set — that is what the dialog counted and named.
         * WHAT their fields say is re-read from the current page, because
         * `runControl` snapshots them to roll its optimistic write back, and the
         * question may have been on screen through several realtime pushes. A
         * row that has since left the page keeps the copy it was asked about.
         */
        const current = new Map(rows.map((row) => [row.id, row] as const))
        await runControl(
          ask.rows.map((row) => current.get(row.id) ?? row),
          ask.action
        )
      } else {
        await runMove(ask.rows, ask.action)
      }
    } finally {
      setBulkBusy(false)
      setBulkAsk(null)
    }
  }, [bulkAsk, bulkBusy, rows, runControl, runMove])

  const onClearFilters = React.useCallback(() => {
    setSearch(CLEARED_FILTERS)
  }, [setSearch])

  const onRetry = React.useCallback(() => {
    void result.refetch()
  }, [result])

  const onCloseDetail = React.useCallback(() => {
    setDetailId(null)
  }, [])

  /* ---------------- render ---------------- */

  const filtersActive = hasActiveFilters(search)
  const isInitialLoad = result.isPending
  const isEmpty = !isInitialLoad && !result.isError && tableRows.length === 0
  const tabbableIndex = focusedIndex < 0 ? 0 : focusedIndex

  const menuStateFor = (id: string): RowMenuState =>
    menu.id === id ? (menu.open ? 'open' : 'idle') : 'none'

  const bodyClass = cn(
    'relative',
    /*
     * Keeping the previous page on screen while the next loads, dimmed so nobody
     * reads stale rows as fresh ones — but only just.
     *
     * This was 55%, which is most of the way to invisible, and it fires on every
     * committed keystroke while searching. The result was a table that flashed
     * dark-light-dark-light as you typed, which reads as "struggling" no matter
     * how fast the response actually is; the request behind it is 63ms. 78% is
     * unmistakably a held frame while still being legible enough to keep reading
     * the row you were looking at, and the short fade means a fast response
     * never fully lands the dim at all.
     */
    'transition-opacity duration-150 ease-out',
    result.isPlaceholderData && 'opacity-[0.78]'
  )

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-1 flex-col bg-s1', p.className)}>
      {p.showToolbar === false ? null : (
        <CustomerToolbar
          status={p.status}
          segment={p.segment}
          total={total}
          tookMs={result.data?.tookMs}
          loaded={rows.length}
          loading={result.isFetching}
          bulkSlot={
            selectedCount === 0 ? null : (
              <BulkTargetBar
                count={selectedCount}
                job={bulkJob}
                schedule={bulkSchedule}
                pending={bulkSaving}
                onJobChange={setBulkJob}
                onScheduleChange={setBulkSchedule}
                onUpdate={onBulkUpdate}
              />
            )
          }
        />
      )}

      {/* The resume roster, on the one list it means anything for: Approved is
          where the Sessions view lives, and a run is something only an approved
          customer has. It renders nothing at all until there is a roster saved
          or a resume in flight. */}
      {p.status === 'approved' ? <ResumeBar /> : null}

      {/* Phone: the header row is gone with the table, so select-all needs a
          home of its own. Outside the scroll container, so it does not have to
          be sticky and the keyboard scroll maths stays at zero offset. */}
      {isTable ? null : (
        <div className="flex flex-none items-center gap-2 border-b border-line bg-s1 px-2 py-1.5">
          <Checkbox
            checked={headerChecked}
            onCheckedChange={onToggleAll}
            aria-label={headerChecked === true ? 'Deselect all rows' : 'Select all rows on this page'}
            className="px-2"
          />
          <span className="text-[12.5px] text-muted">Select all</span>
          <span className="tabular ml-auto font-mono text-[11.5px] text-faint">
            {rows.length} shown
          </span>
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-auto overscroll-contain"
          // The virtualizer measures this element; it is the scroll parent and
          // the sticky-header containing block in one.
        >
          {isTable ? (
            /* Real table elements laid out with CSS grid: that is what lets a
               row be absolutely positioned by the virtualizer while `scope`,
               `aria-sort` and `aria-rowcount` keep working. `display: grid`
               strips the implicit table roles, so every one is restated
               explicitly — and `grid` rather than `table`, because these rows
               are selectable and arrow-key navigable. */
            <table
              role="grid"
              aria-multiselectable="true"
              aria-label={`${p.status} customers`}
              aria-rowcount={(total ?? tableRows.length) + HEADER_ROWS}
              aria-colcount={layout.columns.length}
              className="w-full border-collapse text-left"
              style={{ display: 'grid', minWidth: layout.minWidth }}
            >
              <thead role="rowgroup" className="sticky top-0 z-20 bg-s1" style={{ display: 'grid' }}>
                {/* The border lives on the row, not the group, so the header is
                    exactly HEADER_HEIGHT tall — the constant the keyboard
                    scroll maths depends on. */}
                <tr
                  role="row"
                  aria-rowindex={1}
                  style={layout.headerStyle}
                  className="h-11 border-b border-line"
                >
                  {layout.columns.map((col) => {
                    const meta = COLUMN_META[col]
                    const sortKey = meta.sortKey
                    const sorted = sortKey !== undefined && search.sort === sortKey
                    const direction = sorted ? search.dir : null

                    return (
                      <th
                        key={col}
                        role="columnheader"
                        scope="col"
                        aria-sort={
                          sortKey === undefined
                            ? undefined
                            : direction === 'asc'
                              ? 'ascending'
                              : direction === 'desc'
                                ? 'descending'
                                : 'none'
                        }
                        className={cn(
                          CELL,
                          meta.compact && COMPACT_CELL,
                          'group/head text-[11px] font-semibold tracking-[0.06em] text-faint uppercase',
                          col === 'select' && 'justify-center px-0',
                          meta.align === 'right' && 'justify-end',
                          meta.align === 'center' && 'justify-center',
                          // Header cell pins with the body cell, otherwise the
                          // label drifts away from the buttons it names.
                          col === 'actions' && cn(STICKY_ACTIONS, 'z-[2] pr-2')
                        )}
                      >
                        {col === 'select' ? (
                          <>
                            <Checkbox
                              checked={headerChecked}
                              onCheckedChange={onToggleAll}
                              aria-label={
                                headerChecked === true
                                  ? 'Deselect all rows on this page'
                                  : 'Select all rows on this page'
                              }
                            />
                            <span className="sr-only">Select</span>
                          </>
                        ) : col === 'fleets' ? (
                          /*
                           * Three letters over three boxes. The same box width
                           * and gap as the cell below, so each letter is
                           * centred over the box it names — the header IS the
                           * legend for the row.
                           */
                          <span className="flex items-center gap-2">
                            <span className="sr-only">{meta.label}</span>
                            {FLEET_COLUMNS.map((fleet) => (
                              <Tooltip key={fleet.key} content={fleet.hint}>
                                <span className="w-[36px] flex-none text-center">{fleet.label}</span>
                              </Tooltip>
                            ))}
                          </span>
                        ) : sortKey !== undefined ? (
                          // The header is one of the few places a Radix tooltip
                          // is affordable: seven of them, mounted once, never
                          // touched by the scroll path.
                          <Tooltip content={meta.hint ?? `Sort by ${meta.label}`}>
                            <button
                              type="button"
                              onClick={() => onSortHeader(sortKey)}
                              aria-label={`Sort by ${meta.label}, currently ${
                                direction === 'asc'
                                  ? 'ascending'
                                  : direction === 'desc'
                                    ? 'descending'
                                    : 'unsorted'
                              }`}
                              className={cn(
                                'inline-flex min-w-0 items-center gap-1 rounded-xs',
                                'transition-colors duration-100 hover:text-fg',
                                sorted && 'text-accent'
                              )}
                            >
                              <span className={cn(meta.srOnlyLabel && 'sr-only', 'truncate')}>
                                {meta.label}
                              </span>
                              {direction === 'asc' ? (
                                <ArrowUp aria-hidden="true" className="size-3 flex-none" />
                              ) : direction === 'desc' ? (
                                <ArrowDown aria-hidden="true" className="size-3 flex-none" />
                              ) : (
                                <ChevronsUpDown
                                  aria-hidden="true"
                                  className={cn(
                                    'size-3 flex-none opacity-0 transition-opacity duration-100',
                                    'group-hover/head:opacity-60 group-focus-within/head:opacity-60'
                                  )}
                                />
                              )}
                            </button>
                          </Tooltip>
                        ) : (
                          /*
                           * The hint reaches an UNSORTABLE header too.
                           *
                           * It did not, and three columns were quietly carrying
                           * explanations nobody could ever read: this branch
                           * rendered a bare label, so `cities`' "hover a row to
                           * see every city" and `workers`' "takes effect on the
                           * next Start" existed only in the source. The latter
                           * is the whole contract of an editable column — a
                           * number you can change on a running customer that
                           * will not apply until you restart it.
                           *
                           * `Tooltip` renders its child untouched when there is
                           * no content, so a column without a hint costs
                           * nothing and mounts no Radix root.
                           */
                          <Tooltip content={meta.hint}>
                            <span className={cn(meta.srOnlyLabel && 'sr-only', 'truncate')}>
                              {meta.label}
                            </span>
                          </Tooltip>
                        )}
                      </th>
                    )
                  })}
                </tr>

                {/* The filter band. Same tracks as the labels above and the
                    rows below, so every input sits over the column it narrows. */}
                <tr
                  role="row"
                  aria-rowindex={2}
                  style={layout.filterStyle}
                  className="border-b border-line bg-s2"
                >
                  {layout.columns.map((col) => {
                    const meta = COLUMN_META[col]
                    const filter = meta.filter
                    return (
                      <td
                        key={col}
                        role="gridcell"
                        className={cn(
                          'flex h-full min-w-0 items-center px-1.5',
                          col === 'actions' && STICKY_FILTER_ACTIONS
                        )}
                      >
                        {filter === undefined ? null : (
                          <FilterCell
                            filter={filter}
                            label={COLUMN_FILTER_LABELS[filter]}
                            placeholder={meta.filterHint ?? 'Filter…'}
                            value={search[filter]}
                            commit={onFilterCommit}
                          />
                        )}
                      </td>
                    )
                  })}
                </tr>
              </thead>

              {isInitialLoad ? (
                <SkeletonBody layout={layout} />
              ) : (
                <tbody
                  ref={setBodyRef}
                  role="rowgroup"
                  onKeyDown={onBodyKeyDown}
                  onDoubleClick={onBodyDoubleClick}
                  onFocus={onBodyFocus}
                  className={bodyClass}
                  style={{ display: 'grid', height: totalSize, position: 'relative' }}
                >
                  {virtualItems.map((item) => {
                    const tableRow = tableRows[item.index]
                    if (!tableRow) return null
                    const row = tableRow.original
                    return (
                      <CustomerTableRow
                        key={item.key}
                        row={row}
                        run={runs?.[row.customerId] ?? null}
                        index={item.index}
                        ordinal={offset + item.index + 1}
                        top={item.start}
                        selected={tableRow.getIsSelected()}
                        focused={focusedIndex === item.index}
                        tabbable={tabbableIndex === item.index}
                        pending={pendingIds.has(row.id)}
                        menu={menuStateFor(row.id)}
                        actions={actions}
                        layout={layout}
                        q={needle}
                        defaultWorkers={defaultWorkers}
                        defaultGqlWorkers={defaultGqlWorkers}
                        defaultScheduleWorkers={defaultScheduleWorkers}
                        defaultFlow={defaultFlow}
                      />
                    )
                  })}
                </tbody>
              )}
            </table>
          ) : isInitialLoad ? (
            <SkeletonCards />
          ) : (
            <div
              role="grid"
              aria-multiselectable="true"
              aria-label={`${p.status} customers`}
              aria-rowcount={total ?? tableRows.length}
              aria-colcount={1}
            >
              <div
                ref={setBodyRef}
                role="rowgroup"
                onKeyDown={onBodyKeyDown}
                onDoubleClick={onBodyDoubleClick}
                onFocus={onBodyFocus}
                className={bodyClass}
                style={{ height: totalSize, position: 'relative' }}
              >
                {virtualItems.map((item) => {
                  const tableRow = tableRows[item.index]
                  if (!tableRow) return null
                  const row = tableRow.original
                  return (
                    <CustomerCard
                      key={item.key}
                      row={row}
                      run={runs?.[row.customerId] ?? null}
                      index={item.index}
                      ordinal={offset + item.index + 1}
                      top={item.start}
                      selected={tableRow.getIsSelected()}
                      focused={focusedIndex === item.index}
                      tabbable={tabbableIndex === item.index}
                      pending={pendingIds.has(row.id)}
                      menu={menuStateFor(row.id)}
                      actions={actions}
                      q={needle}
                      defaultWorkers={defaultWorkers}
                      defaultGqlWorkers={defaultGqlWorkers}
                      defaultScheduleWorkers={defaultScheduleWorkers}
                      defaultFlow={defaultFlow}
                    />
                  )
                })}
              </div>
            </div>
          )}

          {result.isError ? (
            <div className="flex items-start gap-3 border-t border-line bg-danger-soft px-4 py-3">
              <AlertTriangle aria-hidden="true" className="mt-px size-4 flex-none text-danger" />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-fg">Could not load customers</p>
                <p className="mt-0.5 text-[12px] break-words text-muted">
                  {result.error instanceof Error ? result.error.message : 'Unknown error'}
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={onRetry} loading={result.isFetching}>
                <RefreshCw aria-hidden="true" />
                Retry
              </Button>
            </div>
          ) : null}

          {isEmpty ? (
            <EmptyState
              icon={filtersActive ? <Search /> : <Inbox />}
              title={filtersActive ? 'No customers match these filters' : `No ${listNoun} customers`}
              description={
                filtersActive
                  ? `Nothing in the ${listNoun} list ${describeFilters(search)}. Loosen a filter, or clear them all to see the full list.`
                  : emptyHint
              }
              action={
                filtersActive ? (
                  <Button variant="secondary" size="sm" onClick={onClearFilters}>
                    Clear all filters
                  </Button>
                ) : undefined
              }
            />
          ) : null}
        </div>

        {/* Bulk bar. Floats over the table rather than pushing it, so the row
            under the pointer does not move the moment a checkbox is ticked. */}
        {selectedCount > 0 ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 z-30 flex justify-center px-4">
            <div
              role="group"
              aria-label={`${selectedCount} rows selected`}
              className="pointer-events-auto flex max-w-full flex-wrap items-center justify-center gap-2 rounded-lg border border-line-strong bg-s2 py-2 pr-2 pl-3 shadow-e3"
            >
              <span className="tabular font-mono text-[12px] text-fg">
                {selectedCount} selected
              </span>
              <span aria-hidden="true" className="hidden h-4 w-px bg-line sm:block" />
              {canControl ? (
                <>
                  <Button variant="success" size="sm" onClick={onStartSelected}>
                    <Play aria-hidden="true" />
                    Start selected
                  </Button>
                  <Button variant="danger" size="sm" onClick={onStopSelected}>
                    <Square aria-hidden="true" />
                    Stop selected
                  </Button>
                  {/* Approved only, like the resume bar itself. Replaces the
                      saved roster rather than adding to it, which the tooltip
                      says because a save that silently merged would be
                      impossible to correct. */}
                  {p.status === 'approved' ? (
                    <Tooltip content="Saves these customers as the resume roster, replacing whatever is saved now. To capture everyone running across every page, use “Save all running” above.">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={onSaveForResume}
                        loading={saveRoster.isPending}
                      >
                        <BookmarkPlus aria-hidden="true" />
                        Save for resume
                      </Button>
                    </Tooltip>
                  ) : null}
                </>
              ) : null}
              {/* Separated from the run controls: these change which list the
                  rows are in, not what they are doing. The divider only earns
                  its place when there is something on both sides of it. */}
              {moves.length > 0 ? (
                <>
                  {canControl ? (
                    <span aria-hidden="true" className="hidden h-4 w-px bg-line sm:block" />
                  ) : null}
                  {moves.map((move) => (
                    <Button
                      key={move.action}
                      variant="secondary"
                      size="sm"
                      onClick={() => askBulk(move.action)}
                    >
                      <move.Icon aria-hidden="true" />
                      {move.label}
                    </Button>
                  ))}
                </>
              ) : null}
              <Button variant="ghost" size="sm" onClick={onClearSelection}>
                Clear
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Pinned below the body: paging must not require scrolling to the
          bottom of the page you have finished with. */}
      <CustomerPagination
        status={p.status}
        page={search.page}
        size={search.size}
        total={total}
        rowCount={rows.length}
        loading={result.isFetching}
      />

      <CustomerDetailPanel customerId={detailId} onClose={onCloseDetail} />

      {/* Kept mounted for the debug path — this is only reachable deliberately. */}
      <SignInDialog
        customerId={signinId}
        open={signinId !== null}
        onOpenChange={(o) => { if (!o) setSigninId(null) }}
      />

      {/* Read-only entry: Start posts directly and never opens this. */}
      <RunDrawer
        customerId={runCustomerId}
        onClose={() => setRunCustomerId(null)}
      />

      <CitiesPanel hover={citiesHover} />

      <TargetDialog
        row={targetRow}
        running={targetRow === null ? false : runOwnsRow(runs?.[targetRow.customerId] ?? null)}
        pending={targetSaving}
        onCancel={() => setTargetRow(null)}
        onSave={(row, jobId, scheduleId) => void saveTarget(row, jobId, scheduleId)}
      />

      {/* Outside the `selectedCount > 0` block on purpose: a realtime push that
          empties the selection mid-question would otherwise unmount the dialog
          with a batch already in flight. */}
      <BulkConfirmDialog
        ask={bulkAsk}
        pending={bulkBusy}
        onCancel={onCancelBulk}
        onConfirm={() => void onConfirmBulk()}
      />

      {/* Opens AFTER the bulk confirm closes, for a Start that ran into a row
          with no gender on file — the row's Start reaches it the same way. */}
      <GenderGateDialog ask={genderAsk} onCancel={onCancelGender} onConfirm={onConfirmGender} />

      <DeleteCustomerDialog
        row={deleteTarget}
        pending={remove.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={(row) => {
          remove.mutate(row.customerId, {
            onSuccess: () => {
              setDeleteTarget(null)
              toast({
                title: `${row.name || row.customerId} deleted`,
                description: 'The customer record has been removed.',
                tone: 'ok',
              })
            },
            onError: (err) => {
              toast({ title: 'Could not delete', description: friendlyError(err), tone: 'danger' })
            },
          })
        }}
      />
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Reassigning the job and shift
 * ------------------------------------------------------------------ */

/** Everything on file is ten digits; twenty is the one outlier. */
const ID_DIGITS = 10
const ID_MAX_DIGITS = 20

/**
 * What the operator typed, reduced to what the column stores.
 *
 * Accepts the qualified `JOB-CA-0000000554` the table paints and the schedules
 * screen links, as well as the bare suffix. Mirrors the server's `IdSuffix`
 * transform exactly, so the preview under the field is what actually lands.
 */
function toIdSuffix(raw: string): string {
  const trimmed = raw.trim()
  const qualified = /^(?:JOB|SCH|job|sch)-[A-Za-z]{2}-(.+)$/.exec(trimmed)
  return (qualified?.[1] ?? trimmed).trim()
}

/**
 * The country code this row's ids are qualified with, read off the row itself.
 *
 * Not a constant: the market is a hunt setting now, so `JOB-CA-…` and
 * `JOB-US-…` are both possible and the server decides which by way of
 * `jobIdFull`. Reading it back off the value the server already sent is the only
 * way to preview the qualified form without this component guessing the market.
 *
 * `null` when the customer has neither id yet — in which case there is nothing
 * to read it from, and the preview shows the bare suffix instead of inventing a
 * country.
 */
function idCountry(row: CustomerRow): string | null {
  const full = row.jobIdFull ?? row.scheduleIdFull
  if (full === null) return null
  return /^(?:JOB|SCH)-([A-Za-z]{2})-/.exec(full)?.[1] ?? null
}

interface IdFieldState {
  /** Reduced to the suffix — what will be stored. */
  value: string
  /** Non-null blocks Save. */
  error: string | null
  /** Non-null is shown but does not block. */
  warning: string | null
}

/**
 * The toolbar's bulk job/shift editor. Appears beside Sort while anything is
 * selected, pre-filled from the first customer ticked (see `bulkSeedRef`).
 *
 * Same rules as `TargetDialog` — digits either bare or qualified, and the pair
 * moves together or not at all — enforced by the same `readIdField`, so the
 * two ways of writing this pair can never disagree about what is valid. The
 * feedback is compressed to fit a toolbar: a red border and `title` for a hard
 * error, an amber border on whichever box the pair rule is waiting on, and a
 * disabled Update that says why.
 *
 * All props are primitives or stable setters, so the memo holds while the
 * table re-renders around it.
 */
const BulkTargetBar = React.memo(function BulkTargetBar(p: {
  count: number
  job: string
  schedule: string
  pending: boolean
  onJobChange: (next: string) => void
  onScheduleChange: (next: string) => void
  onUpdate: () => void
}) {
  const jobField = readIdField(p.job, 'job')
  const scheduleField = readIdField(p.schedule, 'shift')

  const bothEmpty = jobField.value === '' && scheduleField.value === ''
  const bothSet = jobField.value !== '' && scheduleField.value !== ''
  const pairError =
    bothEmpty || bothSet ? null : 'Fill both ids, or clear both to unassign the selection.'

  const blocked = jobField.error !== null || scheduleField.error !== null || pairError !== null
  const noun = p.count === 1 ? 'customer' : 'customers'

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !blocked && !p.pending) {
      event.preventDefault()
      p.onUpdate()
    }
  }

  const inputClass = (field: IdFieldState, missing: boolean) =>
    cn(
      // 16px below `sm` for the same reason as every input on this bar: a
      // smaller focused font makes iOS zoom the whole page.
      'h-8 w-[128px] rounded-md border bg-s1 px-2 font-mono text-[16px] sm:h-7 sm:w-[118px] sm:text-[12px]',
      'text-fg outline-none transition-colors duration-100',
      'placeholder:font-sans placeholder:text-[12px] placeholder:text-faint/70',
      field.error !== null
        ? 'border-danger focus:ring-2 focus:ring-danger/25'
        : missing
          ? 'border-warn focus:border-accent focus:ring-2 focus:ring-accent/20'
          : 'border-line hover:border-line-strong focus:border-accent focus:ring-2 focus:ring-accent/20'
    )

  return (
    <div
      role="group"
      aria-label={`Set the job and shift for ${p.count} selected ${noun}`}
      className={cn(
        'flex h-11 items-center gap-1.5 rounded-md border border-accent/40 bg-accent-soft',
        'px-1.5 sm:h-9'
      )}
    >
      <input
        type="text"
        value={p.job}
        onChange={(event) => p.onJobChange(event.target.value)}
        onKeyDown={onKeyDown}
        spellCheck={false}
        autoComplete="off"
        inputMode="numeric"
        placeholder="Job id"
        aria-label={`Job id for ${p.count} selected ${noun}`}
        aria-invalid={jobField.error !== null}
        title={
          jobField.error ??
          (bothSet && jobField.value !== '' ? undefined : pairError ?? undefined) ??
          jobField.warning ??
          undefined
        }
        className={inputClass(jobField, pairError !== null && jobField.value === '')}
      />
      <input
        type="text"
        value={p.schedule}
        onChange={(event) => p.onScheduleChange(event.target.value)}
        onKeyDown={onKeyDown}
        spellCheck={false}
        autoComplete="off"
        inputMode="numeric"
        placeholder="Shift id"
        aria-label={`Shift (schedule) id for ${p.count} selected ${noun}`}
        aria-invalid={scheduleField.error !== null}
        title={
          scheduleField.error ??
          (bothSet && scheduleField.value !== '' ? undefined : pairError ?? undefined) ??
          scheduleField.warning ??
          undefined
        }
        className={inputClass(scheduleField, pairError !== null && scheduleField.value === '')}
      />
      <Button
        variant="primary"
        size="sm"
        className="h-8 px-2.5 sm:h-7"
        loading={p.pending}
        disabled={blocked || p.pending}
        onClick={p.onUpdate}
        title={pairError ?? jobField.error ?? scheduleField.error ?? undefined}
        aria-label={
          bothEmpty
            ? `Unassign the job and shift from ${p.count} selected ${noun}`
            : `Apply this job and shift to ${p.count} selected ${noun}`
        }
      >
        {bothEmpty ? 'Unassign' : 'Update'} {p.count}
      </Button>
    </div>
  )
})

function readIdField(raw: string, kind: 'job' | 'shift'): IdFieldState {
  const value = toIdSuffix(raw)
  if (value === '') return { value, error: null, warning: null }
  if (!/^\d+$/.test(value)) {
    return { value, error: `A ${kind} id is digits only — "${value}" is not.`, warning: null }
  }
  if (value.length > ID_MAX_DIGITS) {
    return { value, error: `Too long: ${value.length} digits, at most ${ID_MAX_DIGITS}.`, warning: null }
  }
  return {
    value,
    error: null,
    // A warning rather than a rule: ten digits is what today's data does, not
    // something Amazon published, so this says "check that" and gets out of the way.
    warning:
      value.length === ID_DIGITS
        ? null
        : `Every ${kind} id on file is ${ID_DIGITS} digits — this one is ${value.length}.`,
  }
}

/**
 * Reassign a customer's job and shift.
 *
 * ── Why this exists at all ──────────────────────────────────────────
 * `job_id` and `schedule_id` are not static customer data: the sibling system
 * assigns and clears them continuously as it fetches schedules, and 181 of 611
 * customers are unassigned at any moment. Everything else in this console reads
 * them. This is the one place that writes them, for putting a customer on a
 * specific shift by hand.
 *
 * ── The pair ────────────────────────────────────────────────────────
 * Both fields save together and the dialog will not let one be filled alone.
 * 430 rows carry both, 181 carry neither, none carries one — and
 * `create-application` needs both, so half an assignment is a customer whose
 * every run stops with "No job and shift are assigned to this customer yet".
 * Clearing both is a real edit and is allowed; it unassigns.
 *
 * The change takes effect on the next run. `runApplicationStep` re-reads the
 * pair from the collection immediately after the candidate id rather than
 * trusting what the run started with, so an edit made while a customer is
 * signing in is still picked up — which is also why the dialog says so out loud
 * for a customer that is currently live.
 */
function TargetDialog(p: {
  row: CustomerRow | null
  running: boolean
  pending: boolean
  onCancel: () => void
  onSave: (row: CustomerRow, jobId: string, scheduleId: string) => void
}) {
  const { row } = p

  const [job, setJob] = React.useState('')
  const [schedule, setSchedule] = React.useState('')

  /*
   * Seed from the row each time the dialog opens on a different customer.
   *
   * Keyed on `row.id` rather than on the values: re-seeding whenever the row
   * object changed would wipe half-typed input every time a realtime patch
   * touched that customer, and this table receives one on every heartbeat.
   */
  const seededFor = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (row === null) {
      seededFor.current = null
      return
    }
    if (seededFor.current === row.id) return
    seededFor.current = row.id
    setJob(row.jobId ?? '')
    setSchedule(row.scheduleId ?? '')
  }, [row])

  const jobField = readIdField(job, 'job')
  const scheduleField = readIdField(schedule, 'shift')
  const country = row === null ? null : idCountry(row)

  const bothEmpty = jobField.value === '' && scheduleField.value === ''
  const bothSet = jobField.value !== '' && scheduleField.value !== ''
  const pairError = bothEmpty || bothSet ? null : 'A job and a shift are assigned together — fill both, or clear both to unassign.'

  const unchanged =
    row !== null && jobField.value === (row.jobId ?? '') && scheduleField.value === (row.scheduleId ?? '')

  const blocked =
    jobField.error !== null || scheduleField.error !== null || pairError !== null || unchanged

  const { pending, onSave } = p
  const onSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      if (row === null || blocked || pending) return
      onSave(row, jobField.value, scheduleField.value)
    },
    [row, blocked, pending, onSave, jobField.value, scheduleField.value]
  )

  return (
    <Dialog
      open={row !== null}
      onOpenChange={(open) => {
        if (!open && !p.pending) p.onCancel()
      }}
      title={row?.jobId === null ? 'Assign a job and shift' : 'Change the job and shift'}
      description="Paste either form — JOB-CA-0000000554 or just the digits. Both are saved together."
      footer={
        row === null ? null : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="md" disabled={p.pending} onClick={p.onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              form={TARGET_FORM_ID}
              type="submit"
              loading={p.pending}
              disabled={blocked || p.pending}
            >
              {bothEmpty ? 'Unassign' : 'Save'}
            </Button>
          </div>
        )
      }
    >
      {row === null ? null : (
        <form id={TARGET_FORM_ID} onSubmit={onSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-0.5 rounded-md border border-line bg-s1 px-3 py-2">
            <span className="text-[13.5px] font-medium text-fg">{row.name || '(no name)'}</span>
            <span className="font-mono text-[11.5px] text-muted">{row.customerId}</span>
          </div>

          <IdField
            label="Job id"
            prefix="JOB"
            country={country}
            value={job}
            field={jobField}
            current={row.jobId}
            autoFocus
            onChange={setJob}
          />
          <IdField
            label="Shift (schedule) id"
            prefix="SCH"
            country={country}
            value={schedule}
            field={scheduleField}
            current={row.scheduleId}
            onChange={setSchedule}
          />

          {pairError === null ? null : (
            <p className="rounded-md bg-warn-soft px-2.5 py-1.5 text-[12px] text-warn">{pairError}</p>
          )}

          {p.running ? (
            <p className="rounded-md bg-info-soft px-2.5 py-1.5 text-[12px] text-info">
              This customer is running. The job and shift are re-read just before the
              application is created, so a change now is picked up by the run in flight.
            </p>
          ) : null}

          <p className="text-[11.5px] text-faint">
            The other system reassigns these on its own as it fetches schedules, so a manual
            value can be replaced later.
          </p>
        </form>
      )}
    </Dialog>
  )
}

const TARGET_FORM_ID = 'customer-target-form'

/** One id input, with the qualified form it will be saved as shown beneath it. */
function IdField(p: {
  label: string
  prefix: 'JOB' | 'SCH'
  /** From the row's own qualified id; null when it has none to read. */
  country: string | null
  value: string
  field: IdFieldState
  current: string | null
  autoFocus?: boolean
  onChange: (next: string) => void
}) {
  const id = `target-${p.prefix.toLowerCase()}`
  const { field } = p
  const qualified = p.country === null ? null : `${p.prefix}-${p.country}-${field.value}`

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[12px] font-medium text-muted">
        {p.label}
      </label>
      <input
        id={id}
        type="text"
        value={p.value}
        autoFocus={p.autoFocus}
        onChange={(event) => p.onChange(event.target.value)}
        spellCheck={false}
        autoComplete="off"
        inputMode="numeric"
        placeholder={
          p.country === null ? '0000000000' : `${p.prefix}-${p.country}-0000000000 or 0000000000`
        }
        aria-invalid={field.error !== null}
        aria-describedby={`${id}-hint`}
        className={cn(
          'h-9 w-full rounded-md border bg-s1 px-2.5 font-mono text-[13px] text-fg',
          'outline-none transition-colors duration-100',
          'placeholder:font-sans placeholder:text-faint/70',
          field.error === null
            ? 'border-line hover:border-line-strong focus:border-accent focus:ring-2 focus:ring-accent/20'
            : 'border-danger focus:ring-2 focus:ring-danger/25'
        )}
      />
      <p id={`${id}-hint`} className="min-h-[15px] text-[11.5px]">
        {field.error !== null ? (
          <span className="text-danger">{field.error}</span>
        ) : field.value === '' ? (
          <span className="text-faint">
            {p.current === null ? 'Not assigned.' : 'Empty — this will be cleared.'}
          </span>
        ) : (
          <>
            <span className="font-mono text-muted">{qualified ?? `stored as ${field.value}`}</span>
            {field.warning === null ? null : (
              <span className="text-warn"> · {field.warning}</span>
            )}
          </>
        )}
      </p>
    </div>
  )
}

/**
 * The confirmation for the one irreversible action in this console.
 *
 * It names the person and their id rather than asking about "this customer",
 * because a dialog that reads the same on every row is one an operator learns
 * to dismiss without looking — and there is no undo behind this one. The
 * default focus stays on Cancel, and the destructive button is not the one
 * Enter presses.
 */
function DeleteCustomerDialog(p: {
  row: CustomerRow | null
  pending: boolean
  onCancel: () => void
  onConfirm: (row: CustomerRow) => void
}) {
  const { row } = p

  return (
    <Dialog
      open={row !== null}
      onOpenChange={(open) => {
        if (!open && !p.pending) p.onCancel()
      }}
      title="Delete this customer?"
      description="This cannot be undone. Only a customer who has never been started can be deleted."
      footer={
        row === null ? null : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="md" disabled={p.pending} onClick={p.onCancel}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="md"
              loading={p.pending}
              disabled={p.pending}
              onClick={() => p.onConfirm(row)}
            >
              {p.pending ? null : <Trash2 aria-hidden="true" />}
              Delete permanently
            </Button>
          </div>
        )
      }
    >
      {row === null ? null : (
        <div className="flex flex-col gap-1 rounded-md border border-line bg-s1 px-3 py-2.5">
          <span className="text-[14px] font-medium text-fg">{row.name || '(no name)'}</span>
          <span className="font-mono text-[12px] text-muted">{row.customerId}</span>
          <span className="truncate text-[12.5px] text-muted">{row.email}</span>
        </div>
      )}
    </Dialog>
  )
}

/* ------------------------------------------------------------------ *
 * Confirming a bulk action
 * ------------------------------------------------------------------ */

/** How many customer ids the dialog names before it starts counting. */
const BULK_NAMED_LIMIT = 6

/**
 * What each button on the bulk bar says before it does anything.
 *
 * Every description states the consequence rather than restating the label. A
 * dialog that reads "Are you sure?" is one an operator clears without reading,
 * which would leave the bar exactly as dangerous as it was with an extra click
 * in front of it.
 */
const BULK_COPY: Record<
  BulkAction,
  {
    title: (count: number) => string
    description: string
    confirm: string
    variant: 'success' | 'danger' | 'primary'
    Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  }
> = {
  start: {
    title: (n) => (n === 1 ? 'Start this customer?' : `Start ${n} customers?`),
    description:
      'Each one signs in and begins hunting for the job and shift on its own row. Signing in sends a real verification code to a real inbox, so this is not free to undo.',
    confirm: 'Start selected',
    variant: 'success',
    Icon: Play,
  },
  stop: {
    title: (n) => (n === 1 ? 'Stop this customer?' : `Stop ${n} customers?`),
    description:
      'The hunt, sign-in and heartbeat are torn down for each, and an application part-way through being filled in is abandoned. Anyone not running is left as they are.',
    confirm: 'Stop selected',
    variant: 'danger',
    Icon: Square,
  },
  approve: {
    title: (n) =>
      n === 1 ? 'Move this customer to Approved?' : `Move ${n} customers to Approved?`,
    description:
      'They leave the Pending list and can be started from Approved. Nothing else on the row changes, and Move to Pending brings them straight back.',
    confirm: 'Move to Approved',
    variant: 'primary',
    Icon: CheckCircle2,
  },
  unapprove: {
    title: (n) =>
      n === 1 ? 'Move this customer back to Pending?' : `Move ${n} customers back to Pending?`,
    description:
      'They leave the Approved list. Job and shift ids, sessions and logs all survive, and Move to Approved brings them straight back.',
    confirm: 'Move to Pending',
    variant: 'danger',
    Icon: Undo2,
  },
  confirm: {
    title: (n) =>
      n === 1 ? 'Move this customer to Confirmed?' : `Move ${n} customers to Confirmed?`,
    // The one description here that corrects an assumption rather than just
    // stating a consequence. Everywhere else in this console, Confirmed is
    // written by the claim path and means a shift was really taken.
    description:
      'Confirmed is otherwise written only when a shift is really claimed. Moving someone here by hand does not claim one — they appear in Confirmed but not in Won, and nothing starts, stops or renews as a result.',
    confirm: 'Move to Confirmed',
    variant: 'primary',
    Icon: BadgeCheck,
  },
  unconfirm: {
    title: (n) =>
      n === 1 ? 'Move this customer back to Approved?' : `Move ${n} customers back to Approved?`,
    description:
      'They leave the Confirmed list. Any shift they really won stays on the row and in the Won list — this changes which list they sit in and nothing else, in either direction.',
    confirm: 'Move to Approved',
    variant: 'danger',
    Icon: Undo2,
  },
}

/**
 * The one thing worth interrupting for, or null.
 *
 * Both cases turn on how many of the selected rows a worker is holding a seat
 * for, because that is the fact this screen cannot otherwise show at the moment
 * of the click: the Pending list carries no Stop button, and a Start aimed at a
 * run already in flight comes back a 409.
 */
function bulkWarning(ask: BulkAsk | null): string | null {
  if (ask === null || ask.running === 0) return null
  const { running } = ask
  const subject =
    ask.rows.length === 1
      ? 'This customer is'
      : running === 1
        ? 'One of these is'
        : `${running} of these are`
  const them = running === 1 ? 'it' : 'them'

  if (ask.action === 'unapprove') {
    return `${subject} running right now. Moving ${them} back to Pending does not stop ${them}, and the Pending list has no Stop button — stop ${them} from here first if that is what you meant.`
  }
  if (ask.action === 'start') {
    const rest = ask.rows.length - running
    return rest === 0
      ? `${subject} already running. Start is refused for ${running === 1 ? 'it' : 'every one of them'}, so nothing will change.`
      : `${subject} already running. Start is refused for ${them}; the other ${rest} still start.`
  }
  // Approving a running customer is harmless — the Approved list can stop them.
  return null
}

/**
 * The question in front of every bulk action.
 *
 * It names the count in the title and the customers in the body for the same
 * reason `DeleteCustomerDialog` names one: the bar floats over the bottom of the
 * table, which is where a pointer ends up by accident, and "12 selected" is not
 * something an operator can check their intent against.
 *
 * The dialog owns the whole round trip rather than closing on the click — the
 * confirm button spins until the batch settles, which is what makes a held Enter
 * or an impatient second click a no-op instead of a second page of requests.
 */
function BulkConfirmDialog(p: {
  ask: BulkAsk | null
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const { ask } = p
  const copy = ask === null ? null : BULK_COPY[ask.action]
  const warning = bulkWarning(ask)
  const rows = ask?.rows ?? EMPTY_ROWS
  const single = rows.length === 1 ? rows[0] : undefined

  return (
    <Dialog
      open={ask !== null}
      onOpenChange={(open) => {
        // Escape, the overlay and the X all route through here. None of them may
        // close a question whose answer is already in flight.
        if (!open && !p.pending) p.onCancel()
      }}
      title={copy === null ? '' : copy.title(rows.length)}
      description={copy?.description}
      footer={
        copy === null ? null : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="md" disabled={p.pending} onClick={p.onCancel}>
              Cancel
            </Button>
            <Button
              variant={copy.variant}
              size="md"
              loading={p.pending}
              disabled={p.pending}
              onClick={p.onConfirm}
            >
              {p.pending ? null : <copy.Icon aria-hidden="true" />}
              {copy.confirm}
            </Button>
          </div>
        )
      }
    >
      {ask === null ? null : (
        <div className="flex flex-col gap-3">
          {single !== undefined ? (
            <div className="flex flex-col gap-1 rounded-md border border-line bg-s1 px-3 py-2.5">
              <span className="text-[14px] font-medium text-fg">{single.name || '(no name)'}</span>
              <span className="font-mono text-[12px] text-muted">{single.customerId}</span>
              <span className="truncate text-[12.5px] text-muted">{single.email}</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              {rows.slice(0, BULK_NAMED_LIMIT).map((row) => (
                <span
                  key={row.id}
                  className="rounded border border-line bg-s1 px-1.5 py-0.5 font-mono text-[11.5px] text-muted"
                >
                  {row.customerId}
                </span>
              ))}
              {rows.length > BULK_NAMED_LIMIT ? (
                <span className="text-[11.5px] text-muted">
                  and {rows.length - BULK_NAMED_LIMIT} more
                </span>
              ) : null}
            </div>
          )}

          {warning === null ? null : (
            <div className="flex items-start gap-2 rounded-md border border-warn/30 bg-warn-soft/40 px-3 py-2 text-[12.5px] text-warn">
              <AlertTriangle aria-hidden="true" className="mt-px size-4 shrink-0" />
              <span>{warning}</span>
            </div>
          )}
        </div>
      )}
    </Dialog>
  )
}
